/*#
# `<tosi-virta>` — the board

The 2D board: one filter, tasks grouped into lanes by the value of one tag key
(`status` by default — the empty lane is the **inbox**), a card view with the
progressively-disclosed transcript, and keyboard navigation. Every view is a
composition of the one primitive: tag filter plus sort.

Point it at a [store](/store/) and it binds. The store below is seeded with a
few hundred tasks so the board is exercised at a realistic size; the demo
identity writes to an in-memory log, so play freely.

```html
<tosi-virta lane="status" color="sky" style="width: 100%; height: 100%"></tosi-virta>
```
```js
import { MemoryLog, VirtaStore, seedEvents } from 'tosijs-virta'

const store = new VirtaStore({ log: new MemoryLog(), identity: 'you' })
await store.load()
await store.import(seedEvents({ tasks: 300, seed: 3 }))
preview.querySelector('tosi-virta').store = store
```
```test
const board = preview.querySelector('tosi-virta')
await new Promise((r) => setTimeout(r, 100))
// a lane builds only the cards on screen, so count what the lanes hold
const held = () =>
  [...board.querySelectorAll('.lane h3 .lane-count')].reduce(
    (n, el) => n + parseInt(el.textContent, 10),
    0
  )
const all = held()
test('the board renders lanes and cards', () => {
  expect(board.querySelectorAll('.lane').length).toBeGreaterThan(2)
  expect(board.querySelectorAll('.card').length).toBeGreaterThan(0)
  expect(all).toBeGreaterThan(50)
})
test('typing a filter narrows the board', async () => {
  const input = board.querySelector('input[part="filter"]')
  input.value = 'project:tosijs-ui'
  input.dispatchEvent(new Event('change', { bubbles: true }))
  await new Promise((r) => setTimeout(r, 100))
  expect(board.querySelectorAll('.card').length).toBeGreaterThan(0)
  expect(held()).toBeGreaterThan(0)
  expect(held()).toBeLessThan(all)
})
```

## The card window

The title, the body rendered as markdown, and an editable tag list: add a
tag by typing it or picking from every tag on the board, remove one with its
×. Each edit is one `tag`/`untag` event, so the transcript shows who changed
what. Below that: move, close/reopen, work on this, and the transcript.

## Working on it

Anyone — a person or an agent — can say what they are working on: the
`work` verb puts a `working:<identity>` tag on the task and takes it off
everything else that identity was working on, since you work on one thing
at a time. Say `also: true` to keep the others. Working and in-progress
imply each other: working on a task that isn't started moves it to
in-progress, moving a card into in-progress (a drag, or the card's
**status** select) puts you on it, and moving it out takes everyone off.
So the card window needs no work button, and no close button either:
status **done** closes a task, and moving it out of done reopens it
(board #173). The card shows a small jiggling avatar per worker; `working:owner`
is an ordinary tag, so `working:owner` is a filter and `working` is a board
of everything in progress.

## Keys

| key | action |
| --- | --- |
| `j` / `k` or `↓` / `↑` | next / previous card |
| `Enter` | open the selected card |
| `Escape` | close the card view |
| `/` | focus the filter |

## Attributes

- `lane` — the tag key that becomes the lanes (default `status`). A task
  with no status is in the inbox — the implicit `status:inbox`; moving it
  out adds a status, moving it back removes it.
- `filter` — the initial filter text; the input writes it back.
- `color` — a [palette](/palette/) name for the board background (default
  `sky`). Boards are saved filters, so each one gets its own color.
- `hide` — lane values to leave out, space-separated (default `freezer
  obsolete rejected`); the toggle row under the filter writes it
  back.
- `show` — a project board's view toggles, space-separated: `no-own`
  leaves out the project's own tasks, `no-others` leaves out related tasks
  (owned by another project, upstream, downstream or blocking this one),
  and `stakeholders` keeps only own tasks that another project cares about.
  The "show" control (own | related | archived, #765) and the filter menu
  write it. Own and related can't both be off. Archived tasks are the filter's business:
  hidden unless the filter says `archived` (only those) or `archived:any`
  (those too), which is the filter menu's "archived tasks" submenu.
- `db` — with no `store` set, open (or create) an IndexedDB log with this
  name as the base (on a host board the name joins the host and is keyed by
  the credential); `snapshot` — a URL of a JSON list of events to fill an
  empty log with (the demo board's real projects); `seed` — else seed that
  many generated tasks if the log is empty;
  `identity` — who the board writes as (default `you`). Together they make
  a board that needs no code: see the [full-screen board](/board/).

## Lanes

`status:` lanes come in a fixed order — inbox, freezer, backlog, ready,
in-progress, review, then the endings: done, obsolete, rejected. Every one is shown even when empty, so there is always somewhere
to drop. The inbox is the implicit status: no tag
is written for it, `status:inbox` in a filter finds it, and dropping a card
there removes its status. Other values a board meets follow
alphabetically. The row of toggles under the filter switches lanes on and
off. Freezer starts off, and so do obsolete and rejected, whose tasks are
archived anyway.

## Overview

The toolbar's view switch (the tiles icon) swaps the lanes for one tile per project:
its icon, color or background, version, a one-line description, open /
in-progress / blocked counts, the latest activity, and links to its site,
npm package, and repo. Click a tile to open that project's board; the
settings button opens its settings — only what cannot be derived: the
repo, the site, and the look (icon, color, background — each a text field,
with a file or color picker beside it that only fills the field in).
Description, package, and version come from the repo and the tasks.
Settings are stored as `project` events, so a project has a transcript like
a task does. Unset projects get a color and initials from their name. A
**retired** project (the settings panel's retire) leaves the slice menu and
trails the overview, muted; its history is untouched, and unretire brings it
back. `view` is part of the URL state. A tile also counts
what the project is **waiting on elsewhere**: open tasks moved to other
projects that were marked as blocking or delaying it. Tasks with no project
get a derived **no project** tile, first, only while any exist — and "no
project" joins the slice menu for the same span. Nothing is written for
it: `project:none` is the implicit value, as `status:inbox` is for status.

**Import from GitHub** (the account menu's Import from GitHub…, or a
project's "refresh from GitHub"): point it at a repo and its description,
homepage, package, version, and issues arrive — see [GitHub import](/github/).
Repeating it only adds what is new.

## Connecting to a host

```
<tosi-virta host="https://us-central1-<project>.cloudfunctions.net" poll="10" hash></tosi-virta>
```

A board with a `host` syncs with a [service-compris](/remote/) host. The
avatar at the toolbar's right end is the account: signed out it offers
**Sign in with Google**, **Sign in with email…**, and, for agents, **Use an
agent token…**; signed in it shows your initials, and its menu has who you
are and **sign out**. Every write is then that person — the host resolves
their roles from the ID token on each request. The Firebase SDK and the
project's public web config load on demand from the host's own project
(see [sign-in](/firebase-auth/)); nothing is bundled, and the session
persists in this browser until sign-out. An agent token is kept in this
browser's storage, never in markup or the URL. The `auth` attribute lists
what the menu offers (`google password token` by default); `docs` adds a
Docs item that goes there, for a page without the site header (the hosted
board's). The board loads
from its local replica — an IndexedDB named for the host, or `db` as the
name's base, always keyed by the credential (`replicaDbName`) — and pulls
the delta; writes go to the host and come back through the replica; other
writers' events arrive by polling the delta every `poll` seconds (10 by
default), when the tab comes back into view, and after each write. The
toolbar's right edge says `live`, or `offline: <reason>`; who you are is the
avatar's tooltip. A replica only asks for what comes after its cursor, so a
host whose log was reset needs the account menu's **Reload from server**: it
drops the local copy and loads the log again, keeping the credential.
`seed` is ignored on a host board — a shared host is not a demo. (The
sign-in UI belongs upstream — tosijs-ui#177 asks for it; this is the interim.)

## Size

Teams that estimate write `size:<points>` on the Fibonacci scale
(1 2 3 5 8 13 21; stop wherever the team stops — the top value means "or
more"). It is a tag like any other: filter on it, color it, pick it from
the menu. When any card in a lane carries a size the lane header shows the
lane's points beside its count, and the sort menu offers biggest / smallest
first, unsized last. `size:8` is orange and `size:13` and up red — big
tasks call themselves out as candidates for breaking down. Nothing asks
for a size: an untagged task is a normal task, not a lapse.

## Continue

The card window's **continue** button is "yes, and": a new task that
carries on from this one, linked both ways (`continues:#n` on the new,
`continued-by:#m` on the old, plus a comment) and inheriting only the
obvious tags — project, kind, area, priority — never status, size, or who is
working. It opens with its title selected so the first keystrokes rename it.
The `continue` verb does the same for agents.

## The toolbar: view, and the slice menu

The top line reads left to right: a **view** switch (tiles = overview,
columns = board), then one **slice** menu — the mode and the slice of it:
**project** › all projects or one project, **status** › one status,
**owner** › one status. Each mode has its own icon on its leaves (a project
shows its own), so the button reads compactly: a project's icon and name,
or ⚑ `ready`. In project mode the lanes are statuses: the default board. In
status mode they are projects — "what is in progress, everywhere". In owner
mode they are owners — who is carrying what, the view for balancing a team.
Only project mode has "all". One menu rather than two keeps the toolbar
narrow: the slice and the tag list give way on a small screen, so the view
switch and the account button always stay on it. Dragging between lanes
moves along whichever axis is showing; between project lanes it asks the
question the card's project select does (below). `axis` and `scope` are
attributes and URL state; `lane` still accepts any other tag key directly.

## Moving a task to another project

A task has one owner: one `project:`. The card window's **project** select
(or a drag between project lanes) hands it upstream or downstream — "this
should not be done here". No question is asked: the project it leaves stays
**interested** (it still sees the task on its board). Delete the
`interested:` tag to end that, or change it to `blocks:` / `delays:` when
the old project is waiting on it (the `move` verb takes the relation
directly). The move is one commit: `project:` changes, `from:<old>` keeps
the provenance, `interested:<old>` records the old project's interest, and
a comment says so. The old project's board shows the task,
marked as the new owner's; its tile counts what it is waiting on;
`blocks:<name>` as a filter lists it. A support ticket handed to engineering
is exactly this: two tags change, nothing is cloned, and the transcript
keeps the whole history.

## Sort, state, and the URL

Cards sort by **priority** by default: `priority:critical` … `low`, then
unprioritized; blocked tasks first within a priority; then oldest in the
lane first. The other sorts: oldest / newest in lane (age is time since the
last `status:` change), recently updated, recently created, number, title.

`filter`, `project`, `sort`, and `hide` are attributes, so a board's view can
be written in markup. With the boolean `hash` attribute the same four are
mirrored into the URL hash (namespace `virta`, defaults written as absent),
plus `task=<num>` for the open card — so a URL can point at a card. It runs
both ways: every control writes the hash, and editing the hash (or the back
button) moves every control.

Tag colors come from one [registry](/tag-colors/): chips on cards, chips in
the card window, and the dots in the filter menu all agree.

## The filter

Two controls that make one filter. The search box takes free text — each
word must appear in the title or body — and also accepts terms typed
directly (`project:tosijs-ui`, `-status`, `#38`). The tag list beside it holds
the terms; its menu is hierarchical: every tag key as a submenu of its
values (plus "any" and "no"), the bare tags, and a closed-tasks switch. The
`filter` attribute always holds the composed result, so a board can be
saved as one string.

Drag a card to another column to move it (one `setTag`, the same verb the
"move to" select uses). Drag it out of the browser and it lands as a
markdown file — see [markdown interchange](/markdown/) for the shape. Drop a
markdown, text, or HTML file — or a dragged selection of text — on a column
and it becomes a task there (a `.md` that came from a board moves instead of
duplicating). Try dragging some of this paragraph onto the board above.

Columns scroll sideways; each column scrolls its own cards. Opening a card
floats it in a window over the board; click the backdrop, the ×, or press
`Escape` to put it back.

The component never talks to the log itself. It calls the store's verbs — the
same ones the [agent API](/verbs/) exposes — so a human and an agent editing
the same task produce indistinguishable events.
*/

/*{ "pin": "top", "order": 20 }*/

import {
  Component,
  bind,
  elements,
  tosi,
  touch,
  varDefault,
  vars,
  debounce,
  StyleSheet,
  type TosiStyleSheet,
} from 'tosijs'
import { emptySettings } from './fold'
import { isRetired } from './model'
import type {
  AttachedFile,
  EventDraft,
  ProjectProfile,
  Task,
  VirtaEvent,
} from './model'
import * as dragAndDrop from 'tosijs-ui/drag-and-drop'
import { TosiDialog, tosiDialog } from 'tosijs-ui/dialog'
import { defineIcons, icons } from 'tosijs-ui/icons'
import { VIRTA_LOGO } from './logo'
import { popMenu, type MenuItem } from 'tosijs-ui/menu'
import { marked } from 'marked'
import { isSafeNavigationUrl, sanitizeInPlace } from 'tosijs-kilpi'
import { postNotification } from 'tosijs-ui/notifications'
import { hashState, type HashState } from 'tosijs-ui/hash-state'
import { tosiSegmented, type TosiSegmented } from 'tosijs-ui/segmented'
import { tosiDiff, type TosiDiff } from 'tosijs-ui/diff'
import { tosiCarousel, type TosiCarousel } from 'tosijs-ui/carousel'
import {
  tosiSelect,
  type SelectOptions,
  type TosiSelect,
} from 'tosijs-ui/select'
import { tosiTagList, type TosiTagList } from 'tosijs-ui/tag-list'
import {
  htmlToText,
  markdownToTask,
  taskFileName,
  taskToMarkdown,
} from './markdown'
import { CachedLog, HostResetError, isAuthRefusal } from './cached-log'
import { ClosedLogError, IndexedDBLog } from './indexeddb-log'
import { MemoryLog } from './log'
import {
  fetchWebConfig,
  firebaseAuth,
  projectIdFromHost,
  type AuthProvider,
  type AuthSession,
} from './firebase-auth'
import { RemoteLog, ServiceClient, ServiceError } from './remote'
import {
  fetchPublic,
  isPublic,
  PUBLIC_FILES_AREA,
  publicSummaries,
  syncPublic,
  type PublicProject,
} from './public'
import { importProject } from './github'
import {
  PALETTE,
  PALETTE_NAMES,
  boardColor,
  colorFor,
  inkOn,
  nextColor,
} from './palette'
import { seedEvents } from './seed'
import { tagColor } from './tag-colors'
import { ReplayError, VirtaStore } from './store'
import {
  HIDDEN_BY_DEFAULT,
  INBOX_STATUS,
  SORT_LABELS,
  STATUS_ORDER,
  PRIORITY_ORDER,
  chipStyle,
  RELATION_KEYS,
  formatFilter,
  groupByTag,
  orderLanes,
  initialsOf,
  isBlocked,
  parseFilter,
  parseTag,
  sortTasks,
  tagValues,
  workersOf,
  type SortKey,
  waitingOn,
  concerning,
  isFinished,
  ARCHIVED_TAG,
  FINISHED_STATUSES,
  sizePoints,
  NONE_VALUE,
  ENTITY_TYPES,
  RUNGS,
  MODES,
  TYPE_KEY,
  TASK_TYPE,
  PROJECT_KEY,
} from './tags'
import {
  SHOWCASE_COUNT,
  SHOWCASE_TEXT_MAX,
  pickShowcases,
  showcasesAcross,
} from './showcase'
import {
  attachmentNameFor,
  contentTypeFor,
  iconDataUrl,
  uploadFile,
} from './files'
import { childrenOf, parentNumOf, progressOf, statusValue } from './parents'
import {
  PIN_KEY,
  RAN_KEY,
  SKILL_KEY,
  SKILL_TYPE,
  skillVersions,
  type IgnoredEdit,
  type SkillVersion,
} from './skills'
import { isEntry, lineage, recordLine } from './practices'
import {
  describeEvent,
  retractions,
  whoWrote,
  type TranscriptEntry,
} from './transcript'
import { replicaDbName, tokenFingerprint } from './core'

export { replicaDbName, tokenFingerprint }

interface Row {
  id: string
  type: 'event' | 'group'
  kind: string
  by: string
  at: string
  text: string
  count: number
  /** a comment its author took back: '' | 'retracted' (collapsed) | 'revealed' (its text, struck) — #759 */
  state: string
  /** what the reader may do to this row: 'retract' / 'undo' on their own comment, else '' */
  action: string
  /** an attachment's `url\nname`, rendered as a preview or a link (board #2436); '' otherwise */
  media: string
}

/** One overview tile: everything precomputed so the template only binds. */
interface Tile {
  /** what the tile stands for: a project name, or `none` for the unassigned tile */
  key: string
  /** a retired project: muted, shown after the active ones */
  retired: boolean
  name: string
  icon: string
  bg: string
  ink: string
  description: string
  version: string
  summary: string
  activity: string
  site: string
  packageUrl: string
  repoUrl: string
}

interface ProjectForm {
  name: string
  icon: string
  color: string
  background: string
  description: string
  site: string
  package: string
  repo: string
  retired: string
  public: string
  version: string
  dispatch: string
  dispatchEvery: string
  dispatchCap: string
}

const PROJECT_FORM_KEYS = [
  'icon',
  'color',
  'background',
  'description',
  'site',
  'package',
  'repo',
  'version',
  'retired',
  'public',
  'dispatch',
  'dispatchEvery',
  'dispatchCap',
] as const

/**
 * The project settings a person edits, with their captions: one table for
 * the settings panel and its save. The rest (description, package,
 * version) is derived from the repo and the tasks.
 */
const PROJECT_FIELDS = [
  {
    key: 'repo',
    section: 'Links',
    caption: 'GitHub repo',
    hint: 'owner/repo — description, package and version come from it',
  },
  { key: 'site', section: 'Links', caption: 'site', hint: 'https://…' },
  {
    key: 'icon',
    section: 'Look',
    caption: 'icon',
    hint: 'an emoji or an image URL',
  },
  {
    key: 'color',
    section: 'Look',
    caption: 'color',
    hint: 'a palette name or any css color',
  },
  {
    key: 'background',
    section: 'Look',
    caption: 'background',
    hint: 'a css color, gradient, or url(…)',
  },
  // board #161: off unless a person names a session here; each poke is a
  // paid turn, so how often and how many are the cost controls
  {
    key: 'dispatch',
    section: 'Dispatch',
    caption: 'poke session',
    hint: "a Claude session's name — empty is off",
  },
  {
    key: 'dispatchEvery',
    section: 'Dispatch',
    caption: 'every (minutes)',
    hint: '60',
  },
  {
    key: 'dispatchCap',
    section: 'Dispatch',
    caption: 'pokes a day',
    hint: '4',
  },
] as const

// the virta mark; loading, a loader spins on its corner
defineIcons({ virta: VIRTA_LOGO })

/**
 * A footer button as an icon and a label: the label is the tooltip and the
 * accessible name too, and goes on a narrow board (owner's iPhone,
 * 2026-10-14 — four words-wide buttons wrapped).
 */
/**
 * An icon with its label. Given `why`, the title names the action and then
 * says what it does: where the label is hidden (the settings footer),
 * the tooltip is the caption (board #3498).
 */
function iconButton(
  el: Element,
  icon: string,
  label: string,
  why?: string
): void {
  el.replaceChildren(
    icons[icon]!(),
    Object.assign(document.createElement('span'), {
      className: 'label',
      textContent: label,
    })
  )
  el.setAttribute('aria-label', label)
  if (why !== undefined) el.setAttribute('title', `${label}: ${why}`)
}

/** The toolbar parts that belong to the board view: hidden in the overview and when signed out. */
const BOARD_TOOLBAR_PARTS = [
  'slice',
  'filter',
  'filterTags',
  'filterMenu',
  'count',
] as const

interface BoardState {
  filter: string
  tiles: Tile[]
  syncStatus: string
  signedInAs: string
  projectForm: ProjectForm
  search: string
  laneKey: string
  count: number
  laneIds: string[]
  byLane: Record<string, Task[]>
  selectedId: string
  num: number
  title: string
  body: string
  tags: string
  closed: boolean
  laneValue: string
  laneChoices: string[]
  projectValue: string
  /** what the slice menu offers: the projects, and the statuses (status and owner modes share them) */
  sliceChoices: { project: string[]; status: string[] }
  projectNames: string[]
  rows: Row[]
  newComment: string
  bodyDraft: string
}

let instances = 0

/** The only URL scheme untrusted content may point at: http(s). */
const httpOnly = (url: string): boolean => /^https?:\/\//i.test(url)

/** What an attachment's URL points at, by its path's extension (board #2436). */
export function attachmentKind(
  url: string
): 'image' | 'video' | 'audio' | 'pdf' | 'link' {
  let pathname = ''
  try {
    pathname = new URL(url).pathname.toLowerCase()
  } catch {
    return 'link'
  }
  if (/\.(png|jpe?g|gif|webp|avif|svg)$/.test(pathname)) return 'image'
  if (/\.(mp4|webm|mov|m4v)$/.test(pathname)) return 'video'
  if (/\.(mp3|m4a|ogg|oga|wav|flac)$/.test(pathname)) return 'audio'
  if (/\.pdf$/.test(pathname)) return 'pdf'
  return 'link'
}

/**
 * A showcase's description (board #2439): **bold** is the only markdown,
 * and it's built from text nodes and <strong>, never parsed as HTML.
 */
function renderShowcaseText(el: HTMLElement, text: string): void {
  el.textContent = ''
  const { strong } = elements
  const parts = text.split(/\*\*(.+?)\*\*/)
  parts.forEach((part, i) => {
    if (!part) return
    el.append(i % 2 ? strong(part) : document.createTextNode(part))
  })
}

/** One slide of the carousel: a task's showcase, or a public project's. */
interface CarouselSlide {
  /** the task, when this board can open it ('' signed out) */
  id: string
  num: number
  project: string
  look: { name: string; icon: string; color: string; background: string } | null
  title: string
  text: string
  link: string
  /** what decides whether the carousel is rebuilt */
  key: unknown
  picture: () => HTMLElement | null
}

/**
 * A showcase's picture (or video), filling its container. A picture URL
 * needn't end in .png: anything that isn't a video is shown as an image
 * (owner, 2026-09-30: a showcase must have one).
 */
function showcaseMedia(
  url: string,
  alt: string,
  still: boolean
): HTMLElement | null {
  if (!url || !isSafeNavigationUrl(url) || !httpOnly(url)) return null
  const { img, video } = elements
  if (attachmentKind(url) === 'video')
    // muted and looping; still when motion is unwelcome
    return video({
      src: url,
      muted: true,
      loop: true,
      playsInline: true,
      autoplay: !still,
      preload: 'metadata',
      class: 'showcase-media',
      'aria-label': alt,
    })
  return img({ src: url, alt, loading: 'lazy', class: 'showcase-media' })
}

/**
 * An attachment in the transcript (`url\nname`): an image or a video
 * inline, constrained in size, an audio player, or a link chip — every one
 * opening the original in a new tab. A URL reaches the DOM only when it is
 * http(s) and kilpi calls it safe; anything else is its name as text. An
 * SVG is an <img>, never inlined, so its scripts cannot run. Draggable out:
 * the link goes with it (a file drag-out comes with uploads).
 */
function renderAttachment(
  el: HTMLElement,
  media: string,
  fileUrl: (file: AttachedFile) => Promise<string | null>
): void {
  if (el.dataset.media === media) return
  el.dataset.media = media
  el.textContent = ''
  if (!media) return
  let parsed: { name?: string; url?: string; file?: AttachedFile }
  try {
    parsed = JSON.parse(media)
  } catch {
    return
  }
  const name = String(parsed.name ?? '')
  const file = parsed.file
  const { span } = elements
  // a stored file: fetched with the reader's credential, shown from a
  // local URL (a private file has no URL a tag could load; board #2436)
  if (file) {
    const kind = fileKind(file.type)
    el.append(span({ class: 'chip' }, `${fileIcon(kind)} ${name} …`))
    void fileUrl(file).then((url) => {
      if (el.dataset.media !== media) return
      el.textContent = ''
      if (!url) {
        el.append(
          span(
            { class: 'chip gone' },
            `${fileIcon(kind)} ${name} — not readable here`
          )
        )
        return
      }
      el.append(...attachmentView(kind, url, name, file.type))
    })
    return
  }
  const url = String(parsed.url ?? '')
  if (!isSafeNavigationUrl(url) || !httpOnly(url)) {
    el.textContent = name || url
    return
  }
  const kind = attachmentKind(url)
  el.append(...attachmentView(kind, url, name || url, ''))
}

/** What a content type is, for showing it. */
function fileKind(type: string): 'image' | 'video' | 'audio' | 'pdf' | 'link' {
  if (type.startsWith('image/')) return 'image'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('audio/')) return 'audio'
  if (type === 'application/pdf') return 'pdf'
  return 'link'
}

const fileIcon = (kind: string) =>
  kind === 'image'
    ? '🖼'
    : kind === 'video'
    ? '🎬'
    : kind === 'audio'
    ? '🎵'
    : kind === 'pdf'
    ? '📄'
    : '📎'

/**
 * An attachment's inline view: an image or video (size-constrained), an
 * audio player, or a chip; each opens the original in a new tab, and a
 * stored file drags out as itself (to the desktop, another app).
 */
function attachmentView(
  kind: 'image' | 'video' | 'audio' | 'pdf' | 'link',
  url: string,
  name: string,
  type: string
): Node[] {
  const { a, img, video, audio, span } = elements
  const link = (...children: (Node | string)[]) => {
    const anchor = a(
      {
        href: url,
        target: '_blank',
        rel: 'noopener noreferrer',
        title: `${name} — open in a new tab, or drag it out`,
        draggable: true,
      },
      ...children
    ) as HTMLAnchorElement
    // drag out: the file itself (Chrome's DownloadURL), and the link
    anchor.addEventListener('dragstart', (e: DragEvent) => {
      const dt = e.dataTransfer
      if (!dt) return
      dt.setData(
        'DownloadURL',
        `${type || 'application/octet-stream'}:${name}:${url}`
      )
      if (!url.startsWith('blob:')) dt.setData('text/uri-list', url)
    })
    return anchor
  }
  if (kind === 'image') {
    // the picture itself, not a link around it: dragging from inside a link
    // drags the link, so Safari and iOS handed over a URL instead of the
    // image (owner, 2026-09-30). A click opens the original in a new tab.
    const picture = img({
      src: url,
      alt: name,
      loading: 'lazy',
      tabindex: 0,
      role: 'link',
      title: `${name} — open in a new tab, or drag it out`,
      class: 'open-original',
    }) as HTMLImageElement
    const open = () => window.open(url, '_blank', 'noopener,noreferrer')
    picture.addEventListener('click', open)
    picture.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') open()
    })
    // Chrome also takes the file by name (Safari drags the picture itself)
    picture.addEventListener('dragstart', (e: DragEvent) =>
      e.dataTransfer?.setData(
        'DownloadURL',
        `${type || 'image/png'}:${name}:${url}`
      )
    )
    return [picture]
  }
  if (kind === 'video')
    return [
      video({ src: url, controls: true, muted: true, preload: 'metadata' }),
      link(span({ class: 'chip' }, `▶ ${name}`)),
    ]
  if (kind === 'audio')
    return [
      audio({ src: url, controls: true, preload: 'none' }),
      link(span({ class: 'chip' }, `🎵 ${name}`)),
    ]
  let host = ''
  try {
    host = url.startsWith('blob:') ? '' : new URL(url).host
  } catch {
    /* an http(s) or blob URL: the callers checked */
  }
  return [
    link(
      span(
        { class: 'chip' },
        `${kind === 'pdf' ? '📄' : type ? '📎' : '🔗'} ${name}`,
        ...(host ? [span({ class: 'host' }, host)] : [])
      )
    ),
  ]
}

/**
 * What a login request asks for, read from the platform's own approval
 * page (it has no JSON form yet — tosijs-platform#34): the label and the
 * facts it lists. null when the page has nothing to approve.
 */
export function parseConsentPage(
  html: string
): { label: string; facts: [string, string][] } | null {
  if (/Nothing to approve/i.test(html)) return null
  const text = (s: string) =>
    s
      .replace(/<[^>]*>/g, '')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')
      .trim()
  const label = /<p class="label">([\s\S]*?)<\/p>/.exec(html)?.[1]
  if (label === undefined) return null
  const facts: [string, string][] = []
  for (const m of html.matchAll(/<dt>([\s\S]*?)<\/dt>\s*<dd>([\s\S]*?)<\/dd>/g))
    facts.push([text(m[1]!), text(m[2]!)])
  return { label: text(label), facts }
}

/** An icon slot: an emoji (or initials) as text, an image if it looks like a URL. */
function renderIcon(el: Element, icon: string): void {
  const { img } = elements
  // an image only from http(s) or an inline image: a setting can come from a
  // repo's owner-controlled homepage, so no other scheme reaches an <img>
  if (httpOnly(icon) || /^(data:image\/|\/)/i.test(icon)) {
    el.replaceChildren(img({ src: icon, alt: '' }))
  } else {
    el.textContent = icon
  }
}

/** An anchor that is hidden when it has no href. */
function linkBinding(label: string) {
  return (el: Element, href: string) => {
    const a = el as HTMLAnchorElement
    a.textContent = label
    // http(s) only: a project's site is copied from a repo's homepage field
    const safe = httpOnly(href)
    if (safe) a.href = href
    else a.removeAttribute('href')
    a.hidden = !safe
  }
}

/**
 * Background and ink for a project: its own settings, else the palette by
 * name. A custom color gets the ink that reads on it (a picked light color
 * must not get white text); a gradient or image keeps the palette's.
 */
function projectLook(
  profile: Pick<ProjectProfile, 'name' | 'color' | 'background'> &
    Partial<ProjectProfile>
): { bg: string; ink: string } {
  const palette = Object.hasOwn(PALETTE, profile.color)
    ? PALETTE[profile.color]
    : undefined
  const fallback = colorFor(profile.name)
  const bg =
    profile.background || (palette ? palette.bg : profile.color || fallback.bg)
  const ink =
    palette?.ink ??
    (profile.color || profile.background ? inkOn(bg) : undefined) ??
    (profile.color ? '#ffffff' : fallback.ink)
  return { bg, ink }
}

/**
 * Styles for what the board puts in the document rather than in itself —
 * the ask dialog and the scope menu's popped options — installed once.
 * `tosi-dialog` and `tosi-menu` are light DOM, so plain selectors reach in.
 */
/**
 * A card's height, for a virtual lane (variable-height mode: cards render
 * at their natural height; these only estimate the scroll area). The
 * minimum is a one-line title with its chips.
 */
const CARD_MIN_HEIGHT = 76
const CARD_HEIGHT_ESTIMATE = 120

const DOCUMENT_STYLES = 'tosi-virta-document'
function useDocumentStyles(): void {
  if (document.getElementById(DOCUMENT_STYLES)) return
  const button = {
    padding: '8px 16px',
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    font: 'inherit',
  }
  StyleSheet(DOCUMENT_STYLES, {
    // every dialog the board opens has a readable width, never the screen's
    // (owner, 2026-10-02: the approval dialog spanned a phone); long words
    // and URLs wrap
    'tosi-dialog > dialog[open]': {
      maxWidth: 'min(560px, calc(100vw - 32px))',
      boxSizing: 'border-box',
      overflowWrap: 'anywhere',
    },
    // a menu's height stays well inside the SMALL viewport (owner,
    // 2026-10-03): tosijs-ui caps it at 100vh less its top, and on iOS
    // Safari 100vh is the viewport with the toolbars hidden, so a long menu
    // ran under the top or bottom bar and its first or last items couldn't
    // be reached. svh is the viewport with them shown; the menu scrolls.
    // (The body prefix outranks tosijs-ui's own rule, whatever the order.)
    'body .tosi-menu, body .xin-menu': {
      maxHeight:
        'min(calc(var(--max-height, 100svh) - 8px), 70svh, calc(100svh - 96px))',
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    },
    'tosi-dialog.virta-dialog > dialog[open]': {
      width: 'min(320px, 90vw)',
      boxSizing: 'border-box',
    },
    '.virta-dialog-fields': {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    },
    '.virta-dialog-fields label': {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      textAlign: 'left',
      fontSize: '85%',
    },
    '.virta-dialog-fields input': {
      width: '100%',
      boxSizing: 'border-box',
      padding: '8px 10px',
      borderRadius: 8,
      border: '1px solid rgba(127,127,127,0.45)',
      background: 'transparent',
      color: 'inherit',
      font: 'inherit',
    },
    'tosi-dialog.virta-dialog footer button': {
      ...button,
      background: 'rgba(127,127,127,0.25)',
      color: 'inherit',
    },
    // tosi-dialog's own OK, the primary action
    'tosi-dialog.virta-dialog footer button[part="ok"]': {
      background: PALETTE.sky!.bg,
      color: PALETTE.sky!.ink,
    },
    '.virta-option-icon': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      flexShrink: 0,
      overflow: 'hidden',
      fontSize: 12,
    },
    '.virta-option-icon img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: 4,
    },
  })
}

/** A toast, when the page has a notification host; the returned function dismisses it. */
function notify(spec: Parameters<typeof postNotification>[0]): () => void {
  try {
    return postNotification(spec)
  } catch {
    return () => {} // no notification host on this page
  }
}

/** A tile never takes the board's own color (it would vanish): the next palette color instead. */
function apartFrom(
  boardBg: string,
  look: { bg: string; ink: string }
): { bg: string; ink: string } {
  return look.bg === boardBg ? nextColor(look.bg) : look
}

/**
 * A card's title line: the title, or — for a task that is only a
 * description — the description's first line, marked as a stand-in. Two
 * bindings feed it (title and body), so each keeps its half on the element.
 */
function renderCardTitle(
  el: Element,
  key: 'title' | 'body',
  value: string
): void {
  const node = el as HTMLElement & { _title?: string; _body?: string }
  if (key === 'title') node._title = String(value ?? '')
  else node._body = String(value ?? '')
  const title = (node._title ?? '').trim()
  const standIn = (node._body ?? '')
    .split('\n')
    .map((line) => line.replace(/^[#>*\-\s]+/, '').trim())
    .find(Boolean)
  node.textContent = title || standIn || ''
  node.classList.toggle('stand-in', !title && !!standIn)
}

/**
 * Every tag in use, grouped for a menu: `key:value` tags by key (keys and
 * values sorted), bare tags on their own. The filter menu and the card's
 * add-tag menu both read it — the same data, one grouping.
 */
function tagsByKey(
  tasks: Task[],
  keep: (tag: string) => boolean = () => true
): { keyed: Map<string, string[]>; bare: string[] } {
  const keyed = new Map<string, Set<string>>()
  const bare = new Set<string>()
  for (const task of tasks)
    for (const tag of task.tags) {
      if (!keep(tag)) continue
      const { key, value } = parseTag(tag)
      if (value === null) bare.add(key)
      else {
        let values = keyed.get(key)
        if (!values) keyed.set(key, (values = new Set()))
        values.add(value)
      }
    }
  return {
    keyed: new Map(
      Array.from(keyed.keys())
        .sort()
        .map((k) => [k, Array.from(keyed.get(k)!).sort()])
    ),
    bare: Array.from(bare).sort(),
  }
}

/** What the library's columns can be (board #2491): a column per value of one key. */
const LIBRARY_GROUPS = ['type', 'topic', 'rung', 'mode'] as const
type LibraryGroup = (typeof LIBRARY_GROUPS)[number]
/** The columns each grouping always shows, in order (topics come from the items). */
const LIBRARY_LANES: Record<LibraryGroup, readonly string[]> = {
  type: ENTITY_TYPES,
  topic: [],
  rung: RUNGS,
  mode: MODES,
}

/** The keys the card and the draft offer as pickers (board #2435). */
const PICKER_KEYS = ['kind', 'priority'] as const
type PickerKey = (typeof PICKER_KEYS)[number]
/** The conventional values each picker offers before any task uses them. */
const PICKER_VALUES: Record<PickerKey, readonly string[]> = {
  kind: ['feature', 'bug', 'plan', 'chore'],
  priority: PRIORITY_ORDER,
}

/**
 * The kinds, as every kind menu shows them (owner, 2026-09-29; board
 * #2435): the common four at the top, the rest in submenus by theme, and
 * anything else in use under "other". One grouping for the picker and the
 * tag menus.
 */
const KIND_THEMES: readonly (readonly [string, readonly string[]])[] = [
  ['quality', ['tests', 'perf', 'security', 'validation', 'refactor']],
  ['docs & DX', ['docs', 'dx', 'demo', 'tooling']],
  ['design & decisions', ['design', 'question', 'rfc', 'decision', 'epic']],
  ['process', ['process', 'practice', 'release']],
]
/** Words used as kinds that are something else: never offered as a kind. */
const NOT_KINDS = new Set([
  // upstream is a relation (upstream:<repo>#n), not a kind (owner, 2026-09-28)
  'upstream',
])

/** The kinds grouped: top, themes (each non-empty), other — from what is in use plus the conventions. */
function kindGroups(inUse: Iterable<string>): {
  top: string[]
  themes: [string, string[]][]
  other: string[]
} {
  const known = new Set([
    ...PICKER_VALUES.kind,
    ...KIND_THEMES.flatMap(([, v]) => v),
  ])
  return {
    top: [...PICKER_VALUES.kind],
    themes: KIND_THEMES.map(([t, v]) => [t, [...v]] as [string, string[]]),
    other: Array.from(new Set(inUse))
      .filter((k) => !known.has(k) && !NOT_KINDS.has(k))
      .sort(),
  }
}
/** tosi-select shows nothing for '': a picker's "none" gets a sentinel. */
const NO_VALUE = '\u2014'

/** The keys every board uses: top level in the tag menus, in this order (board #2425). */
const COMMON_KEYS = ['status', 'priority', 'kind', 'size', 'project', 'owner']

/** Keys whose value is a task (`continues:#12`): offered only for the open tasks in view. */
const TASK_LINK_KEYS = ['continues', 'continued-by']

/** Links to other tasks and projects, gathered under "related to" (board #2425). */
const LINK_KEYS = [...TASK_LINK_KEYS, ...RELATION_KEYS]

/**
 * The tag menus' shape (board #2425): the common keys at the top — status
 * always, with every conventional status, so nobody has to find it in
 * another menu — the links under "related to", everything else under
 * "other". A task link offers only the open tasks in view (every task ever
 * does not scale); a project link offers every project.
 */
function tagMenuGroups(
  keyed: Map<string, string[]>,
  inView: readonly Task[]
): {
  common: [string, string[]][]
  related: [string, string[]][]
  other: [string, string[]][]
} {
  const values = new Map(keyed)
  values.set(
    'status',
    orderLanes(
      new Set([
        ...STATUS_ORDER.filter((s) => s !== INBOX_STATUS),
        ...(keyed.get('status') ?? []),
      ])
    ).filter(Boolean)
  )
  const projects = keyed.get('project') ?? []
  for (const key of RELATION_KEYS)
    values.set(
      key,
      Array.from(new Set([...projects, ...(keyed.get(key) ?? [])])).sort()
    )
  const open = inView.filter((t) => t.closedAt === null).map((t) => `#${t.num}`)
  for (const key of TASK_LINK_KEYS) values.set(key, open)
  const pick = (keys: string[]): [string, string[]][] =>
    keys
      .filter((k) => (values.get(k) ?? []).length > 0)
      .map((k) => [k, values.get(k)!])
  return {
    common: pick(COMMON_KEYS),
    related: pick(LINK_KEYS),
    other: pick(
      Array.from(keyed.keys()).filter(
        (k) => !COMMON_KEYS.includes(k) && !LINK_KEYS.includes(k)
      )
    ),
  }
}

/**
 * A kind submenu's items, grouped as the picker groups them (board #2435):
 * `values` are the kinds to offer; `conventional` adds the ones no task
 * uses yet (the card menu, where you choose one; the filter menu offers
 * only what can match).
 */
function kindMenuItems(
  values: readonly string[],
  item: (kind: string) => MenuItem,
  conventional: boolean
): MenuItem[] {
  const offered = new Set(values)
  const { top, themes, other } = kindGroups(values)
  const keep = (k: string) => conventional || offered.has(k)
  const themed = themes
    .map(([caption, kinds]) => [caption, kinds.filter(keep)] as const)
    .filter(([, kinds]) => kinds.length > 0)
  return [
    ...top.filter(keep).map(item),
    ...(themed.length || other.length ? [null] : []),
    ...themed.map(
      ([caption, kinds]): MenuItem => ({ caption, menuItems: kinds.map(item) })
    ),
    ...(other.length
      ? [{ caption: 'other', menuItems: other.map(item) } as MenuItem]
      : []),
  ]
}

/** How the source filter names a `source:` value (board #2425). */
const SOURCE_NAMES: Readonly<Record<string, string>> = {
  github: 'GitHub',
  notes: 'notes files',
}

/** Grow a textarea with what is in it (a phone has no room for a scrollbar in a box). */
function fitTextarea(input: HTMLTextAreaElement): void {
  input.style.height = 'auto'
  input.style.height = `${input.scrollHeight + 2}px`
}

/** The settings header wears the look the form describes, as it is edited. */
function paintProjectLook(header: Element, f: ProjectForm): void {
  const name = String(f.name ?? '')
  const icon = String(f.icon ?? '').trim()
  const look = projectLook({
    ...emptySettings(),
    name,
    icon,
    color: String(f.color ?? '').trim(),
    background: String(f.background ?? '').trim(),
    updatedAt: '',
    updatedBy: '',
  })
  const el = header as HTMLElement
  el.style.background = look.bg
  el.style.color = look.ink
  const slot = el.querySelector('.icon')
  if (slot) renderIcon(slot, icon || initialsOf(name))
}

/** What a color picker can show for a setting: `#rrggbb`, or nothing for a gradient, image or named css color. */
function pickerHex(value: string): string | undefined {
  const v = value.trim()
  if (Object.hasOwn(PALETTE, v)) return PALETTE[v]!.bg
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v)
  if (!m) return undefined
  return m[1]!.length === 3
    ? `#${m[1]!.replace(/./g, (c) => c + c)}`.toLowerCase()
    : v.toLowerCase()
}

/**
 * What marked emits for GitHub-flavoured markdown, and the attributes each
 * may keep. Everything else in a rendered body is unwrapped (its text stays,
 * the element goes) — in particular every custom element: kilpi is a
 * denylist that lets unknown elements through by design, and a body that
 * carries `<tosi-md>` (registered by the doc site and by tosijs-ui pages)
 * or `<tosi-virta>` would upgrade the moment it touched the document and
 * render its own contents as raw HTML (0.5.0 re-review, B1). `style`,
 * `class`, `id` and the like are not in any list: a body must not restyle
 * the board or paint over it (M1).
 */
const NONE: ReadonlySet<string> = new Set()
const MARKDOWN_TAGS: Readonly<Record<string, ReadonlySet<string>>> = {
  p: NONE,
  a: new Set(['href', 'title']),
  img: new Set(['src', 'alt', 'title']),
  ul: NONE,
  ol: new Set(['start']),
  li: NONE,
  blockquote: NONE,
  pre: NONE,
  code: NONE,
  em: NONE,
  strong: NONE,
  del: NONE,
  h1: NONE,
  h2: NONE,
  h3: NONE,
  h4: NONE,
  h5: NONE,
  h6: NONE,
  hr: NONE,
  br: NONE,
  table: NONE,
  thead: NONE,
  tbody: NONE,
  tr: NONE,
  th: new Set(['align']),
  td: new Set(['align']),
  input: new Set(['type', 'checked', 'disabled']),
}
/** Elements whose contents are not prose either: dropped whole, never unwrapped. */
const DROP_WHOLE = new Set([
  'script',
  'style',
  'template',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'form',
  'textarea',
  'select',
  'button',
  'link',
  'meta',
  'base',
  'noscript',
])

/** Keep only what markdown produces: unknown elements lose their tag, every element loses unlisted attributes. */
function restrictToMarkdown(root: DocumentFragment): void {
  for (const el of Array.from(root.querySelectorAll('*'))) {
    if (DROP_WHOLE.has(el.localName)) {
      el.remove()
      continue
    }
    // own-property only: a body can name `constructor` or `__proto__` as a tag
    const allowed = Object.hasOwn(MARKDOWN_TAGS, el.localName)
      ? MARKDOWN_TAGS[el.localName]
      : undefined
    // a customized built-in (`is=`) is a custom element too
    if (!allowed || el.hasAttribute('is')) {
      const parent = el.parentNode
      if (parent) {
        while (el.firstChild) parent.insertBefore(el.firstChild, el)
        parent.removeChild(el)
      }
      continue
    }
    for (const name of el.getAttributeNames())
      if (!allowed.has(name)) el.removeAttribute(name)
    if (el.localName === 'input') {
      // only the task-list checkbox marked emits, and it stays inert
      if (el.getAttribute('type') !== 'checkbox') el.remove()
      else el.setAttribute('disabled', '')
    }
  }
}

/**
 * A task body is untrusted: it came from a GitHub issue author, a notes
 * file, a dropped document, or any token holder. Markdown renders to HTML,
 * the HTML is sanitized in place by kilpi (scripts, handlers and unsafe
 * URLs gone), then cut down to what markdown produces (no custom elements,
 * no style/class/id — `restrictToMarkdown`), and links and images are held
 * to http(s) — before anything reaches the document. Never assign a body
 * to innerHTML by another route. Under a Trusted Types policy the parse
 * throws; the body is then shown as text.
 */
function renderMarkdown(source: string, into: HTMLElement): void {
  // whatever fails in here — a Trusted Types policy on the parse, a body
  // that trips the renderer — the body is shown as text, never left as the
  // previous task's, and never thrown into the caller
  try {
    const html = marked.parse(source, { async: false }) as string
    const template = document.createElement('template')
    template.innerHTML = html
    sanitizeInPlace(template.content)
    restrictToMarkdown(template.content)
    for (const a of Array.from(template.content.querySelectorAll('a[href]'))) {
      const href = a.getAttribute('href') ?? ''
      if (!isSafeNavigationUrl(href) || !httpOnly(href)) {
        a.removeAttribute('href')
      } else {
        a.setAttribute('rel', 'noopener noreferrer')
        a.setAttribute('target', '_blank')
      }
    }
    for (const img of Array.from(template.content.querySelectorAll('img'))) {
      if (!httpOnly(img.getAttribute('src') ?? '')) img.remove()
    }
    into.replaceChildren(template.content)
  } catch {
    into.textContent = source
  }
}

/** the lane select's value for "no status" (tosi-select cannot show '') */
const INBOX_VALUE = '(inbox)'
/** the project select's value for "every project" (same reason) */
const NO_PROJECT = NONE_VALUE
/**
 * The toolbar's modes, the first level of the slice menu. In
 * `project` mode the scope is a project — or all of them — and the lanes
 * are statuses. In `status` mode the scope is one status and the lanes are
 * projects; in `owner` mode the scope is one status and the lanes are
 * owners. Only project mode has "all". Any other tag key still works
 * through `lane` directly.
 */
const AXES = ['project', 'status', 'owner'] as const
/** Each mode's icon in the slice menu: on its submenu and on its leaves, so a leaf needs no prefix. */
const AXIS_ICONS: Record<(typeof AXES)[number], string> = {
  project: 'folder',
  status: 'flag',
  owner: 'user',
}
/** the status a status- or owner-mode board opens on */
const DEFAULT_STATUS_SCOPE = 'in-progress'
const EMPTY_LANE_NAMES: Record<string, string> = {
  status: 'inbox',
  project: 'no project',
  owner: 'nobody',
  working: 'nobody',
  // the library's groupings (board #2491)
  topic: 'no topic',
  rung: 'no rung',
  mode: 'no mode',
}
function laneFor(axis: string): string {
  if (axis === 'project') return 'status'
  if (axis === 'status') return 'project'
  return axis
}
/** The slice menu's value: the mode and the slice of it (`project:` is all projects). */
function sliceValue(axis: string, scope: string): string {
  return `${axis}:${scope}`
}
function scopeKeyFor(axis: string): string {
  return axis === 'project' ? 'project' : 'status'
}

/** Chip colors for tosi-tag-list's `Tag` objects, from the registry. */
function tagChipColors(tag: string): { color: string; background: string } {
  const c = tagColor(tag)
  return { color: c.ink, background: c.bg }
}

/**
 * tosi-tag-list colors chips only through `availableTags` entries; a chip for
 * a tag that is not (yet) in that list renders plain, so paint them directly.
 */
function colorTagChips(tagList: HTMLElement): void {
  for (const chip of Array.from(tagList.querySelectorAll('tosi-tag'))) {
    const tag = (
      (chip as HTMLElement & { caption?: string }).caption ??
      chip.textContent ??
      ''
    ).trim()
    // filter terms that are not tags keep the list's own neutral color
    if (/^[-#~]/.test(tag) || tag.startsWith('is:')) continue
    const c = tagColor(tag)
    const el = chip as HTMLElement
    // tosi-tag paints from its own variables; an inline color would not reach the caption
    el.style.setProperty('--tag-bg', c.bg)
    el.style.setProperty('--tag-text-color', c.ink)
    el.style.setProperty('--tag-close-button-color', c.ink)
    el.style.background = c.bg
    el.style.color = c.ink
    el.style.fontWeight = c.loud ? '700' : '500'
  }
}

/** A colored dot for a menu item: a term's tag color, grey for negations and `is:`. */
function swatchFor(term: string): HTMLElement {
  const { span } = elements
  const tag = term.startsWith('-') ? term.slice(1) : term
  const c =
    term.startsWith('-') || term.startsWith('is:')
      ? { bg: '#dfe1e6' }
      : tagColor(tag)
  return span({
    class: 'menu-swatch',
    style: {
      display: 'inline-block',
      width: '10px',
      height: '10px',
      borderRadius: '5px',
      background: c.bg,
      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
    },
  })
}

/** The in-app drag type for a card; text/markdown and text/plain ride along for other apps. */
export const TASK_MIME = 'application/x-virta-task'

// Theme tokens with fallbacks — a page without tosijs-ui's theme defines none
// of these, and `var(--x)` with no default collapses padding and gaps to zero.
const t = {
  xs: varDefault.tosiSpacingXs!('4px'),
  sm: varDefault.tosiSpacingSm!('8px'),
  md: varDefault.tosiSpacing!('12px'),
  lg: varDefault.tosiSpacingLg!('16px'),
  radius: varDefault.tosiBorderRadius!('4px'),
  radiusLg: varDefault.tosiBorderRadiusLg!('12px'),
  font: varDefault.tosiFontFamily!('system-ui, sans-serif'),
  text: varDefault.tosiText!('#172b4d'),
  bg: varDefault.tosiBg!('#ffffff'),
}

/**
 * The board's surfaces: cards, lanes, windows and their text. Surface and
 * text read the page's tosijs-ui theme (`--tosi-bg`, `--tosi-text`, which
 * the doc-system flips in dark mode) and fall back to light values — or
 * dark ones under a `.darkmode` ancestor, the ecosystem's dark-mode switch,
 * for a page with no theme. Everything else — lanes, quieter inks, borders,
 * chips — is mixed from those two, so it follows either theme without a
 * second palette (and never picks up a site's brand-tinted inset).
 */
/** Text mixed into the page color: the lanes and card surfaces. */
const lift = (pct: number) =>
  `color-mix(in srgb, ${vars.virtaText} ${pct}%, ${vars.virtaBase})`
/** Text mixed into the card surface: what sits on a card (inks, borders, chips). */
const mix = (pct: number) =>
  `color-mix(in srgb, ${vars.virtaText} ${pct}%, ${vars.virtaSurface})`
// Depth runs opposite ways: in light, cards are the page color on grey
// lanes; in dark, lanes lift a little off the page and cards lift more.
const surfaceTokens = (dark: boolean) => ({
  _virtaBase: `var(--tosi-bg, ${dark ? '#1d2125' : '#ffffff'})`,
  _virtaText: `var(--tosi-text, ${dark ? '#b6c2cf' : '#172b4d'})`,
  _virtaSurface: dark ? lift(11) : vars.virtaBase,
  _virtaLane: dark ? lift(4) : lift(6),
})
const ui = {
  surface: vars.virtaSurface,
  lane: vars.virtaLane,
  text: vars.virtaText,
  /** secondary text: counts, captions, meta */
  muted: vars.virtaMuted,
  /** tertiary text: card numbers */
  faint: vars.virtaFaint,
  /** chip text, section heads */
  subtle: vars.virtaSubtle,
  border: vars.virtaBorder,
  /** neutral chips and plain buttons */
  chip: vars.virtaChip,
  chipHover: vars.virtaChipHover,
  focus: vars.virtaFocus,
  shadow: vars.virtaShadow,
}

/** Stamp a transcript row's id onto its element so a click can be resolved without DOM indices. */
function bindRowId(el: HTMLElement, _key: string): void {
  bind(el, '^.id', {
    toDOM(element: Element, id: string) {
      ;(element as HTMLElement).dataset.rowId = id
    },
  })
}

/** Path-safe lane id: tag values may contain `/`, `.`, spaces. */
function safeId(value: string): string {
  // Injective: `_` is the escape introducer (so it is escaped too) and every
  // escape is exactly `_` + 4 hex digits, so no two values share a slot.
  return value === ''
    ? 'inbox'
    : 'v_' +
        value.replace(
          /[^a-zA-Z0-9-]/g,
          (c) => `_${c.charCodeAt(0).toString(16).padStart(4, '0')}`
        )
}

/** The `docs` attribute as an address to go to: http(s) only, relative resolved; '' otherwise. */
export function docsHref(value: string): string {
  if (!value.trim()) return ''
  try {
    const url = new URL(value, location.href)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : ''
  } catch {
    return ''
  }
}

function timeAgo(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000))
  if (s < 60) return `${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 48) return `${h}h`
  return `${Math.round(h / 24)}d`
}

export class TosiVirta extends Component {
  static override preferredTagName = 'tosi-virta'
  static override initAttributes = {
    lane: 'status',
    filter: '',
    color: 'sky',
    identity: 'you',
    db: '',
    seed: 0,
    snapshot: '',
    showcases: SHOWCASE_COUNT,
    host: '',
    poll: 0,
    auth: 'google password token',
    hide: HIDDEN_BY_DEFAULT.join(' '),
    show: '',
    axis: 'project',
    scope: '',
    sort: 'priority',
    // what the library's columns are (board #2491): type, topic, rung, mode
    group: 'type',
    hash: false,
    view: 'board',
    // where the account menu's Docs item goes, for a page with no site
    // header to get back by ('' leaves it out)
    docs: '',
  }

  static override lightStyleSpec: TosiStyleSheet = {
    ':host': {
      ...surfaceTokens(false),
      containerType: 'inline-size',
      containerName: 'virta',
      _virtaMuted: mix(72),
      _virtaFaint: mix(66),
      _virtaSubtle: mix(78),
      _virtaBorder: mix(14),
      _virtaChip: mix(10),
      _virtaChipHover: mix(18),
      _virtaFocus: '#4c9aff',
      _virtaShadow: 'rgba(9,30,66,0.3)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      height: '100%',
      minHeight: 360,
      boxSizing: 'border-box',
      fontFamily: t.font,
      color: t.text,
      // the board color: set per instance from the palette in render()
      background: 'var(--virta-bg, #546678)',
      borderRadius: t.radiusLg,
      overflow: 'hidden',
      outline: 'none',
    },
    '.darkmode :host': {
      ...surfaceTokens(true),
      _virtaShadow: 'rgba(0,0,0,0.5)',
    },
    // hidden means hidden: the segmented control, the select and the tag
    // list set their own display, which beat the attribute — so a
    // signed-out toolbar still showed them (owner, 2026-10-14)
    ':host .toolbar > [hidden]': { display: 'none !important' },
    ':host .toolbar': {
      display: 'flex',
      gap: t.sm,
      alignItems: 'center',
      padding: `${t.sm} ${t.md}`,
      background: 'rgba(0,0,0,0.18)',
      color: 'var(--virta-ink, #fff)',
    },
    ':host .toolbar input': {
      flex: '0 1 260px',
      minWidth: 72,
      height: 28,
      boxSizing: 'border-box',
      font: 'inherit',
      fontSize: '90%',
      background: 'rgba(255,255,255,0.22)',
      border: 'none',
      borderRadius: t.radius,
      color: 'var(--virta-ink, #fff)',
      padding: `${t.xs} ${t.sm}`,
    },
    ':host .toolbar input::placeholder': {
      color: 'var(--virta-ink, #fff)',
      opacity: 0.7,
    },
    ':host .toolbar input:focus': {
      background: t.bg,
      color: t.text,
      outline: 'none',
    },
    ':host .count': { fontSize: '85%', whiteSpace: 'nowrap', opacity: 0.85 },
    // tosi-select for project and sort: sized like the search box
    ':host .toolbar .slice, :host .sort': {
      '--tosi-select-touch-size': '28px',
      '--tosi-select-field-width': 'auto',
      '--tosi-select-padding': '0 8px',
      '--tosi-select-value-padding': '0',
      flex: '0 0 auto',
      maxWidth: 220,
      minWidth: 0,
    },
    // elastic: on a narrow screen the slice and the tag list give way, so
    // the view switch and the account button always stay on screen
    // never narrower than its icon and a few letters of the slice
    ':host .toolbar .slice': { flex: '0 1 auto', minWidth: 104 },
    ':host .toolbar .slice button svg, :host .toolbar .slice button .virta-option-icon':
      {
        width: 16,
        height: 16,
        flex: '0 0 16px',
        marginRight: 6,
        stroke: 'currentColor',
      },
    ':host .toolbar .slice [part="value"]': {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    ':host .toolbar .slice button, :host .sort button': {
      border: 'none',
      borderRadius: t.radius,
      background: 'rgba(255,255,255,0.22)',
      color: 'var(--virta-ink, #fff)',
      font: 'inherit',
      fontFamily: t.font,
      fontSize: '90%',
      cursor: 'pointer',
    },
    ':host .toolbar .slice button': {
      fontWeight: 600,
      maxWidth: '100%',
      minWidth: 0,
    },
    ':host .toolbar .slice button:hover, :host .sort button:hover': {
      background: 'rgba(255,255,255,0.32)',
    },
    ':host .lane > h3 .add': {
      display: 'none',
      width: 22,
      height: 22,
      border: 'none',
      borderRadius: 11,
      background: 'transparent',
      color: ui.muted,
      fontSize: '120%',
      lineHeight: 1,
      cursor: 'pointer',
    },
    ':host.has-project .lane > h3 .add': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    ':host .lane > h3 .add:hover': { background: ui.border, color: ui.text },
    ':host .lane > h3 .archive-all': {
      border: 'none',
      background: 'transparent',
      color: ui.muted,
      fontSize: '75%',
      fontWeight: 400,
      cursor: 'pointer',
      padding: '2px 6px',
      borderRadius: 4,
    },
    ':host .lane > h3 .archive-all:hover': {
      background: ui.border,
      color: ui.text,
    },
    ':host .window .title-input': {
      flex: '1 1 auto',
      minWidth: 0,
      font: 'inherit',
      fontSize: '115%',
      fontWeight: 600,
      color: 'inherit',
      background: 'transparent',
      border: '1px solid transparent',
      borderRadius: 4,
      padding: '2px 6px',
    },
    ':host .window .title-input:hover': { borderColor: ui.border },
    ':host .window .title-input:focus': {
      background: ui.surface,
      borderColor: ui.focus,
      outline: 'none',
    },
    ':host .window .body-wrap': { position: 'relative' },
    // a draft (the lane's +) is a title, a description and Create /
    // Discard until Create: nothing about a task that does not exist yet
    ':host .window.draft .card-only, :host .window.draft .header .num': {
      display: 'none',
    },
    ':host .window .draft-actions': { display: 'none' },
    ':host .window.draft .draft-actions': {
      display: 'flex',
      gap: t.sm,
      justifyContent: 'flex-end',
    },
    ':host .window .body[hidden], :host .window .body-input[hidden]': {
      display: 'none',
    },
    ':host .window .body-input': {
      width: '100%',
      minHeight: 96,
      boxSizing: 'border-box',
      borderRadius: 6,
      border: `1px solid ${ui.border}`,
      padding: t.sm,
      font: 'inherit',
      lineHeight: 1.45,
      background: ui.surface,
      color: ui.text,
      resize: 'vertical',
    },
    ':host .window .body-input:focus': {
      borderColor: ui.focus,
      outline: 'none',
    },
    ':host .toolbar .filter-tags [part="tagMenu"]': { display: 'none' },
    ':host .toolbar .filter-tags[editable]': { gridTemplateColumns: '0px 1fr' },
    ':host .toolbar .filter-menu': {
      flex: '0 0 auto',
      height: 28,
      minWidth: 28,
      border: 'none',
      borderRadius: t.radius,
      background: 'rgba(255,255,255,0.22)',
      color: 'var(--virta-ink, #fff)',
      cursor: 'pointer',
      fontSize: '110%',
    },
    ':host .toolbar .clear-filter[hidden]': { display: 'none' },
    ':host .toolbar .filter-menu:hover': {
      background: 'rgba(255,255,255,0.35)',
    },
    // the tag list is a grid with a hidden lead button; lay it out as one 28px flex row
    ':host .toolbar .filter-tags': {
      flex: '1 1 auto',
      minWidth: 0,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      height: 28,
      gap: 4,
    },
    ':host .toolbar .filter-tags > button:first-child': { display: 'none' },
    // same treatment for the window's list: one 28px flex row, input and menu button sized to it
    // the card's tags wrap: a task can carry many, and a phone is narrow
    // (the toolbar's filter keeps its one-line strip)
    ':host .window .tag-list': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      minHeight: 28,
      gap: 4,
      '--input-bg': ui.surface,
      '--brand-color': ui.chip,
      '--brand-text-color': ui.text,
    },
    ':host .window .tag-list > button:first-child': { display: 'none' },
    // a chip's remove button is tosi-tag's own (light DOM): the window's
    // button style must not reach it — its padding left the × no width
    ':host .window tosi-tag button': {
      padding: 0,
      height: 'auto',
      minWidth: 16,
      background: 'transparent',
    },
    ':host .window .tag-list [part="tagContainer"]': {
      flex: '1 1 auto',
      flexWrap: 'wrap',
      height: 'auto',
      minWidth: 0,
    },
    ':host .window .tag-list [part="tagInput"]': {
      flex: '1 1 120px',
      minWidth: 96,
      maxWidth: 220,
    },
    ':host .window .tag-list [part="tagMenu"]': {
      height: 28,
      width: 28,
      lineHeight: '28px',
    },
    ':host .toolbar .filter-tags [part="tagContainer"]': { flex: '1 1 auto' },
    // tosi-tag-list sizes itself from theme vars the host page may not define;
    // chips get 4px vertical / 6px horizontal padding and center in a 28px row
    ':host .tag-list, :host .filter-tags': {
      '--spacing': '8px',
      '--line-height': '20px',
      '--font-size': '12px',
      '--touch-size': '28px',
      '--tag-list-bg': 'transparent',
      '--input-bg': 'rgba(255,255,255,0.22)',
      '--brand-color': 'rgba(255,255,255,0.3)',
      '--brand-text-color': 'var(--virta-ink, #fff)',
    },
    ':host .tag-list [part="tagContainer"], :host .filter-tags [part="tagContainer"]':
      {
        minHeight: 28,
        boxSizing: 'border-box',
        padding: '2px 3px',
        alignItems: 'center',
        gap: 4,
      },
    ':host .tag-list tosi-tag, :host .filter-tags tosi-tag': {
      height: 'auto',
      lineHeight: '16px',
      padding: '4px 6px',
      alignItems: 'center',
    },
    ':host .tag-list [part="tagInput"], :host .filter-tags [part="tagInput"]': {
      height: 28,
      boxSizing: 'border-box',
      padding: '0 8px',
      fontSize: '12px',
    },
    // the value span sets its own face; keep both selects in the board's sans
    ':host .toolbar .slice [part="value"], :host .sort [part="value"]': {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: 'var(--virta-ink, #fff)',
    },
    // one line that scrolls sideways when it doesn't fit (a phone): its
    // controls keep their size, and no segment label wraps
    ':host .lanes-row': {
      display: 'flex',
      gap: t.sm,
      alignItems: 'center',
      padding: `0 ${t.md} ${t.sm}`,
      background: 'rgba(0,0,0,0.18)',
      color: 'var(--virta-ink, #fff)',
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollbarWidth: 'none',
      whiteSpace: 'nowrap',
      // a scroller may shrink below its content in the board's column
      // (min-height stops being auto); this row keeps its height
      flexShrink: 0,
    },
    ':host .lanes-row::-webkit-scrollbar': { display: 'none' },
    ':host .lanes-row > *': { flexShrink: 0 },
    ':host .sort-label': {
      marginLeft: t.sm,
      display: 'inline-flex',
      alignItems: 'center',
      gap: t.xs,
      fontSize: '90%',
      whiteSpace: 'nowrap',
      color: 'var(--virta-ink, #fff)',
    },
    ':host .lane-toggles, :host .show-toggles': {
      height: 28,
      display: 'inline-flex',
      alignItems: 'center',
      '--segmented-option-padding': '0 10px',
      '--segmented-options-background': 'rgba(0,0,0,0.15)',
      '--segmented-option-current-background': 'rgba(255,255,255,0.9)',
      '--segmented-option-current-color': '#172b4d',
      '--segmented-option-color': 'var(--virta-ink, #fff)',
      '--segmented-options-border-radius': '6px',
      fontSize: '90%',
    },
    ':host .view-switch': {
      height: 36,
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0,
      '--segmented-option-padding': '8px 14px',
      '--segmented-options-background': 'rgba(0,0,0,0.15)',
      '--segmented-option-current-background': 'rgba(255,255,255,0.9)',
      '--segmented-option-current-color': '#172b4d',
      '--segmented-option-color': 'var(--virta-ink, #fff)',
      '--segmented-option-icon-color': 'currentColor',
      '--segmented-options-border-radius': '6px',
      fontSize: '90%',
    },
    ':host .view-toggle': {
      height: 28,
      border: 'none',
      borderRadius: t.radius,
      background: 'rgba(255,255,255,0.22)',
      color: 'var(--virta-ink, #fff)',
      padding: `0 ${t.sm}`,
      cursor: 'pointer',
      font: 'inherit',
      fontSize: '90%',
    },
    ':host .view-toggle:hover': { background: 'rgba(255,255,255,0.32)' },
    ':host .tile.unassigned .gear': { display: 'none' },
    // the library's own controls, and the board's, each in their view
    ':host .library-only': { display: 'none' },
    ':host.library .library-only': { display: 'inline-flex' },
    ':host.library .lane-toggles, :host.library .show-toggles, :host.library .show-toggles-label':
      { display: 'none' },
    ':host .overview-scroll': {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
    },
    ':host .overview': {
      flex: '0 0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: t.md,
      padding: t.md,
      alignContent: 'start',
    },
    // the showcase carousel (board #2439): a hero, not another tile —
    // taller, the picture filling it and the words over it (owner,
    // 2026-09-30); one slide at a time, swiped or rotated
    ':host .carousel': {
      flex: '0 0 auto',
      position: 'relative',
      // twice the gap between tiles below it (owner, 2026-09-30); the
      // sides come from each slide's own padding, so several side by side
      // stand the same distance apart as the carousel does from the tiles
      // (owner, 2026-10-13)
      margin: `${t.md} 0`,
      height: 'clamp(240px, 38vh, 380px)',
      color: '#fff',
      transition: 'height 0.25s ease, margin 0.25s ease, opacity 0.25s ease',
    },
    ':host .carousel[hidden]': {
      display: 'none',
    },
    // tosijs-ui's carousel: a native horizontal scroll, so a finger can
    // swipe it (owner, 2026-10-01); it fills the hero, dots over the picture
    ':host .carousel tosi-carousel': {
      position: 'absolute',
      inset: 0,
      _carouselButtonColor: 'rgba(255,255,255,0.5)',
      _carouselButtonHoverColor: 'rgba(255,255,255,0.8)',
      _carouselButtonActiveColor: '#fff',
      _carouselDotCurrentColor: '#fff',
      _carouselDotSize: 9,
    },
    ':host .carousel tosi-carousel::part(pager), :host .carousel tosi-carousel::part(scroller), :host .carousel tosi-carousel::part(grid)':
      {
        height: '100%',
      },
    ':host .carousel tosi-carousel::part(scroller)': {
      overscrollBehaviorX: 'contain',
    },
    // the dots over the picture, top right; tosijs-ui gives each a
    // finger-sized hit area (tosijs-ui#204)
    ':host .carousel tosi-carousel::part(progress)': {
      position: 'absolute',
      // inside the last card, which starts a slide's padding in
      right: `calc(${t.lg} + ${t.md})`,
      top: t.sm,
      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
    },
    ':host .carousel .slide': {
      width: '100%',
      height: '100%',
      boxSizing: 'border-box',
      // room for the card's shadow below it
      padding: `0 ${t.md} 12px`,
      cursor: 'pointer',
      outline: 'none',
    },
    // each showcase is its own rounded card
    ':host .carousel .slide-card': {
      position: 'relative',
      width: '100%',
      height: '100%',
      borderRadius: 16,
      overflow: 'hidden',
      background: '#111',
      boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
    },
    ':host .carousel .slide:focus-visible .slide-card': {
      boxShadow: `inset 0 0 0 3px ${ui.focus}`,
    },
    ':host .carousel .showcase-media': {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
    },
    // the words on a translucent panel, so the picture shows through
    // (owner, 2026-10-01)
    ':host .carousel .showcase-words': {
      position: 'absolute',
      left: t.lg,
      bottom: t.lg,
      maxWidth: 'min(70%, calc(100% - 120px))',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: t.xs,
      padding: `${t.sm} ${t.md}`,
      borderRadius: 10,
      background: 'rgba(0, 0, 0, 0.42)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
    },
    // a heading is one line, cut with an ellipsis (owner, 2026-10-01)
    ':host .carousel .showcase-words > strong:first-child': {
      fontSize: '150%',
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    ':host .carousel .showcase-text': {
      fontSize: '110%',
      lineHeight: 1.4,
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    ':host .carousel .showcase-num': { opacity: 0.75, fontSize: '85%' },
    ':host .carousel .showcase-project': {
      position: 'absolute',
      right: t.lg,
      bottom: t.lg,
      width: 52,
      height: 52,
      borderRadius: 12,
      border: '2px solid rgba(255,255,255,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      padding: 0,
      fontSize: '26px',
      fontWeight: 700,
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
    },
    ':host .carousel .showcase-project img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    ':host .showcase-text': { lineHeight: 1.4 },
    ':host .showcase-words': { minWidth: 0 },
    ':host .window .measure-panel': {
      display: 'flex',
      flexWrap: 'wrap',
      gap: t.sm,
      alignItems: 'flex-start',
      margin: `${t.sm} 0`,
    },
    ':host .window .measure-panel[hidden]': { display: 'none' },
    ':host .window .family-panel': {
      display: 'flex',
      flexDirection: 'column',
      gap: t.xs,
      margin: `${t.sm} 0`,
    },
    ':host .window .family-panel[hidden]': { display: 'none' },
    ':host .window .family-part': {
      display: 'flex',
      gap: t.sm,
      alignItems: 'baseline',
    },
    ':host .window .family-status': {
      flex: '0 0 auto',
      minWidth: '5.5em',
      fontSize: '80%',
      opacity: 0.75,
    },
    ':host .window .family-note': { opacity: 0.7, fontSize: '85%' },
    ':host .window .conflict-panel': {
      display: 'flex',
      flexDirection: 'column',
      gap: t.sm,
      alignItems: 'flex-start',
      border: `2px solid ${ui.focus}`,
      borderRadius: 8,
      padding: t.sm,
      margin: `${t.sm} 0`,
    },
    ':host .window .conflict-panel[hidden]': { display: 'none' },
    ':host .window .conflict': {
      display: 'flex',
      flexDirection: 'column',
      gap: t.xs,
      alignSelf: 'stretch',
      background: ui.surface,
      borderRadius: 6,
      padding: t.sm,
    },
    ':host .window .conflict-by': { color: ui.muted, fontSize: '85%' },
    ':host .window .conflict-actions': { display: 'flex', gap: t.sm },
    ':host .window .practice-panel': {
      display: 'flex',
      flexDirection: 'column',
      gap: t.xs,
      margin: `${t.sm} 0`,
    },
    ':host .window .practice-panel[hidden]': { display: 'none' },
    ':host .window .practice-suggestion': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: t.sm,
      background: ui.surface,
      borderRadius: 8,
      padding: `${t.xs} ${t.sm}`,
    },
    ':host .window .practice-suggestion > span': { flex: '1 1 200px' },
    ':host .window .measure': {
      flex: '1 1 260px',
      display: 'flex',
      flexDirection: 'column',
      gap: t.xs,
      background: ui.surface,
      borderRadius: 8,
      padding: t.sm,
      position: 'relative',
    },
    ':host .window .measure-version': {
      position: 'absolute',
      top: t.xs,
      right: t.sm,
      color: ui.muted,
      fontSize: '80%',
    },
    ':host .window .measure-missing': { color: ui.muted, fontStyle: 'italic' },
    ':host .window .practice-record': {
      flex: '1 1 100%',
      color: ui.muted,
      fontSize: '90%',
    },
    // a closed card's showcase: shown, or offered
    ':host .window .showcase-panel': {
      display: 'flex',
      flexDirection: 'column',
      gap: t.sm,
      margin: `${t.sm} 0`,
    },
    ':host .window .showcase-panel[hidden]': { display: 'none' },
    ':host .window .showcase-card': {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 40%) 1fr',
      background: ui.surface,
      borderRadius: 8,
      overflow: 'hidden',
    },
    ':host .window .showcase-card .showcase-media': {
      width: '100%',
      maxHeight: 160,
      objectFit: 'cover',
      display: 'block',
    },
    ':host .window .showcase-actions': { display: 'flex', gap: t.sm },
    ':host .overview > .virtual-list-padding': { display: 'none' },
    // a virtual lane's spacers stand in for the cards not built: in a flex
    // column they would shrink to nothing, and the lane couldn't scroll to
    // its end (2026-10-03)
    ':host .cards > .virtual-list-padding': { flex: '0 0 auto' },
    ':host .overview-scroll[hidden], :host .board[hidden], :host .lanes-row[hidden]':
      {
        display: 'none',
      },
    ':host .tile': {
      borderRadius: 12,
      padding: t.md,
      display: 'flex',
      flexDirection: 'column',
      gap: t.xs,
      cursor: 'pointer',
      boxShadow: '0 1px 1px #0002',
      // three lines of text, comfortably: head + two clamped lines + links
      minHeight: 180,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    },
    ':host .tile:hover': { boxShadow: `0 4px 12px ${ui.shadow}` },
    ':host .tile-head': {
      display: 'flex',
      alignItems: 'center',
      gap: t.sm,
      marginBottom: 6,
    },
    ':host .tile .icon': {
      width: 32,
      height: 32,
      borderRadius: 8,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255,255,255,0.25)',
      fontSize: '18px',
      fontWeight: 700,
      overflow: 'hidden',
      flex: '0 0 auto',
    },
    ':host .tile .icon img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    ':host .tile .name': {
      fontWeight: 700,
      fontSize: '110%',
      flex: '1 1 auto',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    ':host .tile .version': {
      fontSize: '75%',
      padding: '1px 6px',
      borderRadius: 8,
      background: 'rgba(255,255,255,0.25)',
      flex: '0 0 auto',
      maxWidth: '45%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    ':host.signed-out .tile .gear': { display: 'none' },
    ':host.signed-out .tile': { cursor: 'default' },
    ':host .tile .gear': {
      background: 'transparent',
      border: 'none',
      color: 'inherit',
      opacity: 0.7,
      cursor: 'pointer',
      fontSize: '120%',
      lineHeight: 1,
      padding: 2,
    },
    ':host .tile .gear:hover': { opacity: 1 },
    ':host .tile .gear svg': {
      width: 16,
      height: 16,
      fill: 'currentColor',
    },
    ':host .tile .description': {
      opacity: 0.9,
      fontSize: '90%',
      lineHeight: '1.3',
      display: '-webkit-box',
      WebkitLineClamp: '2',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    ':host .tile .summary': {
      fontSize: '90%',
      lineHeight: '1.3',
      display: '-webkit-box',
      WebkitLineClamp: '2',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    ':host .tile .activity': {
      fontSize: '80%',
      opacity: 0.8,
      lineHeight: '1.3',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    ':host .tile .links': {
      display: 'flex',
      gap: t.sm,
      marginTop: 'auto',
      paddingTop: 8,
    },
    ':host .tile .links a': {
      color: 'inherit',
      fontSize: '80%',
      textDecoration: 'none',
      padding: '2px 8px',
      borderRadius: 8,
      background: 'rgba(255,255,255,0.25)',
    },
    ':host .tile .links a[hidden]': { display: 'none' },
    ':host .tile.retired': { opacity: 0.55, filter: 'saturate(0.7)' },
    ':host .tile.retired:hover': { opacity: 0.8 },
    ':host .window.project-window': { width: 'min(480px, 100%)' },
    // the header wears the project's look (set from the form as it is edited)
    ':host .project-window .header.look': {
      borderBottom: 'none',
      padding: `${t.md} ${t.lg}`,
      gap: t.md,
    },
    ':host .project-window .look .icon': {
      width: 36,
      height: 36,
      flex: '0 0 auto',
      borderRadius: 8,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      background: 'rgba(255,255,255,0.25)',
      fontSize: '20px',
      fontWeight: 700,
    },
    ':host .project-window .look .icon img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    ':host .project-window .project-name': {
      fontWeight: 700,
      fontSize: '120%',
      flex: '1 1 auto',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    ':host .project-window .look .dismiss': {
      background: 'rgba(255,255,255,0.25)',
      color: 'inherit',
      width: 28,
      padding: 0,
    },
    ':host .project-window .scroll': { gap: t.lg },
    // caption | control, one row per setting, under a section head
    ':host .project-window .fields': {
      display: 'grid',
      gridTemplateColumns: 'max-content 1fr',
      alignItems: 'center',
      columnGap: t.md,
      rowGap: t.sm,
    },
    ':host .project-window .fields h4': {
      gridColumn: '1 / -1',
      margin: 0,
      fontSize: '75%',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: ui.muted,
    },
    ':host .project-window .field': { display: 'contents' },
    ':host .project-window .caption': {
      fontSize: '90%',
      color: ui.subtle,
      textAlign: 'right',
    },
    ':host .project-window .field-row': {
      display: 'flex',
      alignItems: 'center',
      gap: t.sm,
      minWidth: 0,
    },
    ':host .project-window .field-row input:not([type])': {
      flex: '1 1 auto',
      minWidth: 0,
      height: 32,
      boxSizing: 'border-box',
      border: `1px solid ${ui.border}`,
      borderRadius: 6,
      padding: `0 ${t.sm}`,
      background: ui.surface,
      color: ui.text,
      font: 'inherit',
      fontSize: '90%',
    },
    ':host .project-window .field-row input:not([type]):focus': {
      borderColor: ui.focus,
      outline: 'none',
    },
    ':host .project-window .field-row input::placeholder': {
      color: ui.faint,
    },
    ':host .project-window .field-row input[type="color"]': {
      flex: '0 0 32px',
      width: 32,
      height: 32,
      boxSizing: 'border-box',
      padding: 2,
      border: `1px solid ${ui.border}`,
      borderRadius: 6,
      background: ui.surface,
      cursor: 'pointer',
    },
    ':host .project-window .field-row input[type="color"].unset': {
      opacity: 0.35,
    },
    ':host .project-window .field-row .pick-file': {
      flex: '0 0 auto',
      height: 32,
      whiteSpace: 'nowrap',
    },
    ':host .project-window .footer': {
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      gap: t.sm,
      padding: `${t.sm} ${t.lg}`,
      background: ui.surface,
      borderTop: `1px solid ${ui.border}`,
    },
    ':host .project-window .footer .spacer': { flex: '1 1 auto' },
    // the footer's quiet actions are icons; each title is its caption,
    // so save and cancel always fit (board #3498)
    ':host .project-window .footer .label': { display: 'none' },
    ':host .project-window button.quiet': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: t.xs,
      whiteSpace: 'nowrap',
      background: 'transparent',
      color: ui.muted,
      padding: `0 ${t.xs}`,
    },
    ':host .project-window button.quiet:hover': {
      background: ui.chip,
      color: ui.text,
    },
    // the primary action wears the board's own color
    ':host .project-window button.primary, :host .window .draft-actions button.primary':
      {
        background: 'var(--virta-deep, #455563)',
        color: 'var(--virta-ink, #fff)',
        fontWeight: 600,
        padding: `0 ${t.md}`,
      },
    ':host .project-window button.primary:hover, :host .window .draft-actions button.primary:hover':
      {
        background: 'var(--virta-deep, #455563)',
        filter: 'brightness(1.1)',
      },
    ':host .board': {
      flex: '1 1 auto',
      display: 'flex',
      gap: t.sm,
      padding: `${t.xs} ${t.sm} ${t.sm}`,
      overflowX: 'auto',
      overflowY: 'hidden',
      alignItems: 'flex-start',
      minHeight: 0,
    },
    // a strip above the (empty) lanes, not a filler: the lanes stay as drop targets
    ':host .sync': {
      fontSize: '80%',
      whiteSpace: 'nowrap',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      opacity: 0.8,
      color: 'var(--virta-ink, #fff)',
    },
    ':host .sync:empty': { display: 'none' },
    ':host .account': {
      width: 28,
      height: 28,
      flexShrink: 0,
      // the right end, even when it is all the toolbar holds
      marginLeft: 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: 14,
      background: 'rgba(255,255,255,0.22)',
      color: 'var(--virta-ink, #fff)',
      fontSize: '75%',
      fontWeight: 700,
      cursor: 'pointer',
      padding: 0,
    },
    ':host .account[hidden]': { display: 'none' },
    ':host .account svg': { width: 16, height: 16, fill: 'currentColor' },
    ':host .account:hover': { filter: 'brightness(1.1)' },
    ':host .notice': {
      flex: '0 0 auto',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: t.sm,
      margin: t.md,
      padding: `${t.md} ${t.lg}`,
      borderRadius: 8,
      background: ui.surface,
      color: ui.text,
      boxShadow: `0 2px 8px ${ui.shadow}`,
    },
    ':host .notice[hidden]': { display: 'none' },
    ':host .splash': {
      flex: '1 1 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: t.md,
      color: 'var(--virta-ink, #fff)',
    },
    ':host .splash[hidden]': { display: 'none' },
    // room around it: the spinner sits past the mark's corner (owner, 2026-10-14)
    ':host .splash .mark': {
      _tosiIconSize: '160px',
      height: 160,
      width: 160,
      margin: 40,
    },
    ':host .splash .mark.busy': { position: 'relative', display: 'block' },
    // where the composite put it: 28% of the mark, on its top-right corner
    ':host .splash .mark.busy .spinner': {
      _tosiIconSize: '45px',
      position: 'absolute',
      left: 120,
      top: -5,
      width: 45,
      height: 45,
      display: 'flex',
      animation: 'virta-spin 2s linear infinite',
      willChange: 'transform',
    },
    '@keyframes virta-spin': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    // !important: a composite icon is a span with an inline display, which
    // beats any rule — both marks showed at once (owner's iPhone, 2026-10-14)
    ':host .splash:not(.busy) .mark.busy, :host .splash.busy .mark.idle': {
      display: 'none !important',
    },
    ':host .splash [part="splashText"]': { opacity: 0.85 },
    ':host .empty': {
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: t.sm,
      padding: `${t.sm} ${t.md}`,
      color: 'var(--virta-ink, #fff)',
      opacity: 0.9,
      fontSize: '90%',
    },
    ':host .empty[hidden]': { display: 'none' },
    ':host .empty button': {
      background: 'rgba(255,255,255,0.22)',
      color: 'inherit',
      border: 'none',
      borderRadius: t.radius,
      padding: `${t.xs} ${t.sm}`,
      cursor: 'pointer',
    },
    ':host .lane': {
      overflow: 'hidden',
      flex: '0 0 272px',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100%',
      minHeight: 0,
      background: ui.lane,
      color: ui.text,
      borderRadius: 12,
      boxShadow: '0 1px 1px #0002',
    },
    ':host .lane > h3': {
      margin: 0,
      padding: `${t.sm} ${t.md} ${t.xs}`,
      fontSize: '90%',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: t.xs,
    },
    ':host .lane > h3 .swatch': {
      width: 10,
      height: 10,
      borderRadius: 5,
      flex: '0 0 auto',
    },
    ':host .lane > h3 .name': { flex: '1 1 auto' },
    ':host .lane > h3 .lane-count': {
      color: ui.muted,
      fontWeight: 400,
      fontSize: '85%',
    },
    // the lane's scrollbar has a gutter of its own and paints over the
    // cards (board #3479): an overlay thumb (~7px) sat on each card's
    // right edge, and WebKit paints a positioned card (.card is relative)
    // over it; a stacking context keeps the cards beneath their scroller
    ':host .cards': {
      flex: '1 1 auto',
      overflowY: 'auto',
      overflowX: 'hidden',
      isolation: 'isolate',
      padding: `0 10px ${t.xs} ${t.xs}`,
      display: 'flex',
      flexDirection: 'column',
      gap: t.xs,
      minHeight: 24,
    },
    ':host .card': {
      position: 'relative',
      background: ui.surface,
      borderRadius: 8,
      padding: `${t.sm} ${t.sm} ${t.xs}`,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: 5,
      boxShadow: '0 1px 1px rgba(9,30,66,0.25)',
      border: '2px solid transparent',
      transition: 'box-shadow 120ms, transform 120ms',
    },
    ':host .lane.drag-target': {
      outline: '2px dashed rgba(255,255,255,0.6)',
      outlineOffset: -2,
    },
    ':host .lane.drag-over': { background: ui.chipHover },
    ':host .card.drag-source': { opacity: 0.4, transform: 'rotate(2deg)' },
    ':host .workers': { display: 'flex', gap: 4, flexWrap: 'wrap' },
    ':host .workers[hidden]': { display: 'none' },
    ':host .avatar': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 22,
      height: 22,
      borderRadius: 11,
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.5px',
      boxShadow: '0 1px 2px rgba(9,30,66,0.35)',
      animation: 'virta-jiggle 1.6s ease-in-out infinite',
      transformOrigin: '50% 60%',
    },
    '@keyframes virta-jiggle': {
      '0%, 100%': { transform: 'rotate(0deg)' },
      '20%': { transform: 'rotate(-8deg) translateY(-1px)' },
      '40%': { transform: 'rotate(7deg)' },
      '60%': { transform: 'rotate(-4deg) translateY(-1px)' },
      '80%': { transform: 'rotate(3deg)' },
    },
    // a narrow board (a phone, or a narrow column on a wide page): the view
    // switch compacts and the count (the least useful thing on a small
    // screen) goes, so the slice keeps a readable width. A container query,
    // so it follows the board's width, not the window's
    '@container virta (max-width: 540px)': {
      ':host .toolbar': { gap: t.xs, padding: `${t.sm} ${t.sm}` },
      ':host .view-switch': {
        height: 32,
        '--segmented-option-padding': '6px 8px',
      },
      ':host .count': { display: 'none' },
      ':host .toolbar input': { minWidth: 56 },
      // the card and settings windows take the width a phone has
      ':host .overlay': { padding: t.xs },
      ':host .window .scroll': { padding: t.md },
      ':host .window .header': { padding: `${t.sm} ${t.md}` },
      // the select's chevron gives its room to the slice's name
      ':host .toolbar .slice button > svg:last-child': { display: 'none' },
    },
    // the narrowest phones: every control gives a little more, so the
    // account button still fits
    '@container virta (max-width: 360px)': {
      ':host .view-switch': { '--segmented-option-padding': '6px 4px' },
      ':host .toolbar .slice': { minWidth: 84 },
      ':host .toolbar input': { minWidth: 40 },
    },
    '@media (prefers-reduced-motion: reduce)': {
      ':host .avatar': { animation: 'none' },
      ':host .carousel': { transition: 'none' },
    },
    ':host .card:hover': {
      boxShadow: `0 3px 6px ${ui.shadow}`,
      transform: 'translateY(-1px)',
    },
    // a task another project owns, shown on this project's board
    ':host .card.elsewhere': {
      opacity: 0.8,
      border: `2px dashed ${ui.border}`,
    },
    ':host .card.elsewhere::before': {
      content: '"in " attr(data-owner)',
      fontSize: '68%',
      fontWeight: 600,
      color: ui.muted,
    },
    ':host .card.selected': { borderColor: 'var(--virta-deep, #455563)' },
    // a parent's progress, bottom right of its card (board #755)
    ':host .card .parts': {
      position: 'absolute',
      bottom: 6,
      right: 8,
      padding: '0 6px',
      borderRadius: 999,
      fontSize: '68%',
      fontVariantNumeric: 'tabular-nums',
      background: 'rgba(127,127,127,0.25)',
    },
    ':host .card .parts[hidden]': { display: 'none' },
    ':host .card .num': {
      position: 'absolute',
      top: 4,
      right: 8,
      color: ui.faint,
      fontSize: '68%',
      fontVariantNumeric: 'tabular-nums',
    },
    ':host .card .title': {
      fontSize: '100%',
      fontWeight: 500,
      lineHeight: 1.3,
      paddingRight: 34,
      color: ui.text,
    },
    ':host .card .title.stand-in': { fontStyle: 'italic', opacity: 0.8 },
    ':host .tag.loud': {
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '66%',
      letterSpacing: '0.3px',
    },
    ':host .card .tags, :host .window .tags': {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 3,
    },
    ':host .tag': {
      fontSize: '72%',
      lineHeight: '16px',
      padding: '0 6px',
      borderRadius: 3,
      background: ui.chip,
      color: ui.subtle,
      fontWeight: 500,
    },
    ':host .tag.status': { background: ui.border },
    ':host .overlay': {
      position: 'absolute',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: t.lg,
      overflowY: 'auto',
      zIndex: 2,
    },
    ':host .overlay[hidden]': { display: 'none' },
    ':host .window': {
      background: ui.lane,
      color: ui.text,
      width: 'min(720px, 100%)',
      maxHeight: '100%',
      borderRadius: 12,
      boxShadow: `0 8px 24px ${ui.shadow}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    },
    ':host .window .scroll': {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      padding: t.lg,
      display: 'flex',
      flexDirection: 'column',
      gap: t.sm,
    },
    // children never shrink: an item with overflow hidden (tosi-tag-list) would
    // otherwise be squeezed to nothing when the transcript makes the window tall
    ':host .window .scroll > *': { flexShrink: 0 },
    ':host .window .header': {
      flex: '0 0 auto',
      display: 'flex',
      gap: t.sm,
      alignItems: 'center',
      padding: `${t.sm} ${t.lg}`,
      background: ui.surface,
      borderBottom: `1px solid ${ui.border}`,
    },
    ':host .window h2': {
      margin: 0,
      fontSize: '115%',
      flex: '1 1 auto',
      color: 'inherit',
      minWidth: 0,
    },
    ':host .window .num': { color: ui.muted },
    ':host .window .body': {
      display: 'block',
      margin: `${t.xs} 0`,
      lineHeight: 1.45,
      cursor: 'text',
      minHeight: '1.4em',
    },
    ':host .window .body > :first-child': { marginTop: 0 },
    ':host .window .actions': {
      display: 'flex',
      gap: t.sm,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    ':host .window button': {
      background: ui.chip,
      border: 'none',
      borderRadius: t.radius,
      height: 28,
      padding: `0 ${t.sm}`,
      cursor: 'pointer',
      color: ui.text,
      font: 'inherit',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '90%',
    },
    ':host .window .pickers': {
      display: 'flex',
      gap: t.sm,
      alignItems: 'center',
      flexWrap: 'wrap',
      margin: `${t.xs} 0`,
    },
    ':host .window .actions label, :host .window .pickers label': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: t.xs,
      fontSize: '90%',
    },
    ':host .window .lane-select': {
      '--tosi-select-touch-size': '28px',
      '--tosi-select-field-width': 'auto',
      '--tosi-select-padding': '0 8px',
      '--tosi-select-value-padding': '0',
      flex: '0 0 auto',
    },
    ':host .window .lane-select button': {
      background: ui.surface,
      border: `1px solid ${ui.border}`,
    },
    ':host .window .lane-select [part="value"]': {
      color: ui.text,
      fontFamily: 'system-ui, -apple-system, sans-serif',
    },
    ':host .window button:hover': { background: ui.chipHover },
    ':host .window button.dismiss': {
      marginLeft: 'auto',
      fontSize: '120%',
      lineHeight: 1,
    },
    ':host .window h3': {
      margin: `${t.sm} 0 0`,
      fontSize: '95%',
      color: ui.subtle,
    },
    ':host .transcript': {
      display: 'flex',
      flexDirection: 'column',
      gap: t.xs,
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    ':host .entry': {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: t.xs,
      fontSize: '90%',
      background: ui.surface,
      borderRadius: 6,
      padding: `${t.xs} ${t.sm}`,
    },
    ':host .entry .meta': {
      color: ui.muted,
      fontSize: '80%',
      gridColumn: '1 / -1',
    },
    ':host .entry.group': {
      color: ui.muted,
      cursor: 'pointer',
      fontStyle: 'italic',
      background: 'transparent',
    },
    ':host .entry.commented .text': { whiteSpace: 'pre-wrap' },
    ':host .entry .action': {
      color: ui.muted,
      fontSize: '80%',
      cursor: 'pointer',
      textDecoration: 'underline',
      alignSelf: 'start',
    },
    ':host .entry .action:empty': { display: 'none' },
    // an attachment (board #2436): inline but size-constrained; the
    // original opens in a new tab
    ':host .entry .media': {
      gridColumn: '1 / -1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: t.xs,
    },
    ':host .entry .media:empty': { display: 'none' },
    ':host .window.drop-target': {
      outline: `3px dashed ${ui.focus}`,
      outlineOffset: -6,
    },
    ':host .entry .media img, :host .entry .media video': {
      display: 'block',
      maxWidth: '100%',
      maxHeight: 180,
      borderRadius: 4,
      background: ui.chip,
    },
    ':host .entry .media audio': { maxWidth: '100%' },
    ':host .entry .media .open-original': { cursor: 'zoom-in' },
    ':host .entry .media a': {
      color: 'inherit',
      textDecoration: 'none',
      border: 'none',
      boxShadow: 'none',
    },
    ':host .entry .media .chip': {
      display: 'inline-flex',
      gap: t.sm,
      alignItems: 'baseline',
      background: ui.chip,
      borderRadius: 4,
      padding: `2px ${t.sm}`,
    },
    ':host .entry .media .chip .host': { color: ui.muted, fontSize: '85%' },
    ':host .entry.retracted .text': {
      color: ui.muted,
      fontStyle: 'italic',
      cursor: 'pointer',
    },
    ':host .entry.revealed .text': {
      textDecoration: 'line-through',
      color: ui.muted,
      cursor: 'pointer',
    },
    ':host .entry.closed .text, :host .row.reopened .text': { fontWeight: 600 },
    // the field on its own row; comment and attach side by side under it
    ':host .comment': {
      display: 'flex',
      flexWrap: 'wrap',
      gap: t.xs,
      alignItems: 'flex-start',
    },
    ':host .comment textarea': {
      flex: '1 1 100%',
      width: '100%',
      minHeight: 60,
      boxSizing: 'border-box',
      borderRadius: 6,
      border: `1px solid ${ui.border}`,
      padding: t.sm,
      fontFamily: 'inherit',
      background: ui.surface,
      color: ui.text,
    },
  }

  // Installed by static initAttributes; declared here for the type only.
  declare lane: string
  declare filter: string
  declare color: string
  declare identity: string
  declare db: string
  declare seed: number
  declare snapshot: string
  /** what the library's columns are (board #2491): type, topic, rung or mode */
  declare group: string
  /** how many recent showcases the carousel shows (board #2439); 0 hides it */
  declare showcases: number
  declare host: string
  declare poll: number
  declare auth: string
  declare hide: string
  /** A project board's view toggles (board #753): \`no-others\` hides other projects' tasks, \`stakeholders\` keeps only tasks another project cares about. */
  declare show: string
  declare axis: string
  declare scope: string
  declare sort: string
  declare hash: boolean
  declare view: string
  /** Where the account menu's Docs item goes (a page without the site header); '' for none. */
  declare docs: string

  private _store: VirtaStore | null = null
  private stop: (() => void) | null = null
  private state: BoardState
  private key: string
  private expanded = new Set<string>()
  /** Retracted comments whose text the reader asked to see (#759). */
  private revealed = new Set<string>()
  private visible: Task[] = []
  private plainRows: Row[] = []
  /** id of the card being dragged from this board, if any */
  private dragging: string | null = null
  /** true while showCard() is writing the tag list, so its change event is not an edit */
  private syncingTags = false
  /** the filter text last decomposed into the search box + tag list */
  private appliedFilter: string | null = null
  private toggleChoicesKey = ''
  private appliedHide: string | null = null
  private appliedShow: string | null = null
  /** The lane select's choices, and the log position and lane key they were computed at (#52). */
  private laneChoiceList: string[] = []
  private laneChoicesAt = ''
  /** What each choice select was last given, so an unchanged list is not reassigned. */
  private renderedChoices = new WeakMap<TosiSelect, string>()
  private appliedAxis: string | null = null
  private appliedScope: string | null = null
  private appliedSort: string | null = null
  private appliedView: string | null = null
  private hashSync: HashState | null = null
  private chipObserver: MutationObserver | null = null
  private stopHash: (() => void) | null = null
  private sliceChoicesKey = ''
  /** Each lane's tasks as last drawn (plain arrays, what `s.byLane` was given): an optimistic move reads these, never the proxy. */
  private laneTasks = new Map<string, Task[]>()
  /** An open draft from a lane's +: the tags it will be created with. Nothing is written until it says something. */
  private draftTags: string[] | null = null
  private projectNamesKey = ''
  private syncingToggles = false
  private syncingShow = false
  private showChoicesKey = ''

  constructor() {
    super()
    this.key = `virta${++instances}`
    const { [this.key]: state } = tosi({
      [this.key]: {
        filter: '',
        search: '',
        laneKey: 'status',
        count: 0,
        laneIds: [] as string[],
        byLane: {} as Record<string, Task[]>,
        selectedId: '',
        num: 0,
        title: '',
        body: '',
        tags: '',
        closed: false,
        laneValue: '',
        laneChoices: [] as string[],
        projectValue: '',
        sliceChoices: { project: [] as string[], status: [] as string[] },
        projectNames: [] as string[],
        rows: [] as Row[],
        newComment: '',
        bodyDraft: '',
        tiles: [] as Tile[],
        syncStatus: '',
        signedInAs: '',
        projectForm: {
          name: '',
          icon: '',
          color: '',
          background: '',
          description: '',
          site: '',
          package: '',
          repo: '',
          version: '',
        } as ProjectForm,
      } as BoardState & Record<string, unknown>,
    })
    this.state = state as unknown as BoardState
  }

  get store(): VirtaStore | null {
    return this._store
  }

  set store(store: VirtaStore | null) {
    this.detach()
    // a store's files come with its connect; a new store starts without
    // (the host board sets its client after this)
    if (store !== this._store) this.forgetFiles()
    this._store = store
    // a board that loaded has nothing refused to explain (#761)
    if (store) this.refusal = ''
    this.attach()
    this.refresh()
  }

  /** Subscribe to the current store. Symmetric with `detach()`; safe to call twice. */
  private attach(): void {
    if (this._store && !this.stop)
      this.stop = this._store.observe((changed) => this.refresh(changed))
  }

  private detach(): void {
    if (this.stop) this.stop()
    this.stop = null
  }

  private get s(): BoardState {
    return this.state
  }

  override content = () => {
    const { div, input, span, h3, button, textarea, aside, ul, label } =
      elements
    const path = (p: string) => `${this.key}.${p}`
    const s = this.state as unknown as Record<string, unknown>
    return [
      { tabindex: 0, onKeydown: (e: KeyboardEvent) => this.handleKey(e) },
      div(
        { class: 'toolbar' },
        tosiSegmented({
          part: 'viewSwitch',
          class: 'view-switch',
          title: 'overview (tiles), board (lanes) or library (practices)',
          choices: [
            { value: 'overview', caption: '', icon: icons.grid() },
            { value: 'board', caption: '', icon: icons.columns() },
            // the library: what isn't a task — practices, policies,
            // findings — on the same board (owner, 2026-09-30; #2491)
            { value: 'library', caption: '', icon: icons.book() },
          ],
          value: 'board',
          onChange: (e: Event) => {
            const value = (e.target as TosiSegmented).value
            if (
              value === 'board' ||
              value === 'overview' ||
              value === 'library'
            )
              this.setView(value)
          },
        }),
        // the mode and the slice of it, as one menu: project › tosijs,
        // status › ready, owner › ready. One control instead of two keeps
        // the toolbar narrow enough for a phone (the account button must
        // stay on screen)
        tosiSelect({
          part: 'slice',
          class: 'slice',
          // the chosen leaf's icon says the mode: a project's own, ⚑ status, 👤 owner
          showIcon: true,
          title:
            'project (lanes by status), or one status by project or by owner',
          bind: {
            value: s.sliceChoices,
            binding: (
              el: Element,
              v: { project: string[]; status: string[] }
            ) => this.renderSlice(el as TosiSelect, v),
          },
          onChange: (e: Event) => {
            const raw = String((e.target as TosiSelect).value)
            const i = raw.indexOf(':')
            if (i > 0) this.pickSlice(raw.slice(0, i), raw.slice(i + 1))
          },
        }),
        input({
          part: 'filter',
          type: 'search',
          placeholder: 'search — words, or project:x, -status, #38',
          bindValue: s.search,
          onInput: () => this.searchTyped(),
          onChange: () => this.composeFilter(),
          // `#183` and Enter opens that task, if it exists (board #2438)
          onKeydown: (e: KeyboardEvent) => {
            if (e.key === 'Enter') this.openNamed()
            // Escape empties the whole filter, text and tags (board #2461)
            if (e.key === 'Escape' && String(this.s.filter)) {
              e.stopPropagation()
              this.clearFilter()
            }
          },
        }),
        tosiTagList({
          part: 'filterTags',
          class: 'filter-tags',
          editable: true,
          placeholder: '',
          onChange: () => this.composeFilter(),
        }),
        // our own menu button: the tag list's button opens its flat list from a
        // document-level handler we cannot get ahead of (tosijs-ui#172)
        button(
          {
            part: 'filterMenu',
            class: 'filter-menu',
            title: 'pick tags',
            onClick: () => this.popFilterMenu(),
          },
          '▾'
        ),
        // clears the whole filter — the search text and the tag chips; the
        // browser's own × on a search field clears only the text (#2461)
        button(
          {
            part: 'clearFilter',
            class: 'filter-menu clear-filter',
            title: 'clear the filter (Escape in the search box)',
            bind: {
              value: s.filter,
              binding: (el: Element, v: string) => {
                ;(el as HTMLElement).hidden = !String(v ?? '').trim()
              },
            },
            onClick: () => this.clearFilter(),
          },
          '×'
        ),
        span({ part: 'count', class: 'count', bindText: path('count') }),
        span({
          part: 'sync',
          class: 'sync',
          bindText: path('syncStatus'),
          title: 'the host this board syncs with',
        }),
        button(
          {
            part: 'account',
            class: 'account',
            hidden: true,
            title: 'account',
            onClick: () => this.popAccountMenu(),
          },
          icons.user()
        )
      ),
      div(
        { part: 'lanesRow', class: 'lanes-row' },
        // the library's columns: by type, or by topic, rung or mode (#2491)
        label(
          { class: 'sort-label library-only' },
          'group by ',
          tosiSelect({
            part: 'libraryGroup',
            class: 'sort',
            title: 'what the library columns are',
            options: LIBRARY_GROUPS.map((g) => ({ caption: g, value: g })),
            value: 'type',
            onChange: (e: Event) => {
              const value = (e.target as TosiSelect).value
              if (value !== this.group) {
                this.appliedGroup = value
                this.group = value
                this.refresh()
              }
            },
          })
        ),
        tosiSegmented({
          part: 'laneToggles',
          class: 'lane-toggles',
          multiple: true,
          onChange: () => this.lanesToggled(),
        }),
        // what else to show, beside the lanes (#765): the same state the
        // filter menu edits — the archived:any term, and the show attribute
        label(
          { class: 'sort-label show-toggles-label' },
          'show ',
          tosiSegmented({
            part: 'showToggles',
            class: 'show-toggles',
            multiple: true,
            onChange: () => this.showToggled(),
          })
        ),
        label(
          { class: 'sort-label' },
          'sort ',
          tosiSelect({
            part: 'sort',
            class: 'sort',
            title: 'sort order',
            options: (Object.keys(SORT_LABELS) as SortKey[]).map((k) => ({
              caption: SORT_LABELS[k],
              value: k,
            })),
            onChange: (e: Event) =>
              this.setSort((e.target as TosiSelect).value),
          })
        )
      ),
      // signed in, but refused: say who, why, and who can fix it — with
      // the way out right here (#761); never an empty page
      div(
        { part: 'notice', class: 'notice', hidden: true },
        span({ part: 'noticeText' }),
        button({ onClick: () => void this.signOut() }, 'sign out')
      ),
      // the first load of a host board, or nothing to show: the virta mark
      // in the middle of the page — with the icon system's loader spinning
      // on it while loading — not a word at the toolbar's edge (owner,
      // 2026-10-14)
      div(
        { part: 'splash', class: 'splash', hidden: true },
        // the loader is an HTML box turned by a CSS animation, which the
        // compositor runs: the icon system's spin rule animates the <svg>,
        // which WebKit runs on the main thread, so any work while the
        // board loads froze it (board #3507)
        span(
          { class: 'mark busy' },
          icons.virta!(),
          span({ class: 'spinner' }, icons.loader!())
        ),
        icons.virta!({ class: 'mark idle' }),
        span({ part: 'splashText' })
      ),
      div(
        { part: 'empty', class: 'empty', hidden: true },
        span({ part: 'emptyText' }),
        button({ onClick: () => this.clearView() }, 'clear filter')
      ),
      // what shipped, above the tiles only (board #2439)
      div({
        part: 'carousel',
        class: 'carousel',
        hidden: true,
        onClick: (e: Event) => this.carouselClicked(e),
      }),
      div({
        part: 'board',
        class: 'board',
        onClick: (e: Event) => this.selectFromEvent(e),
        onDragstart: (e: DragEvent) => this.handleDragStart(e),
        onDragend: () => {
          this.dragging = null
        },
      }),
      // the tiles and the carousel scroll together: it scrolls out of
      // view above them (owner, 2026-09-30)
      div(
        { part: 'overviewScroll', class: 'overview-scroll', hidden: true },
        div(
          {
            part: 'overview',
            class: 'overview',
            onClick: (e: Event) => this.overviewClicked(e),
          },
          ...(
            this.state.tiles as unknown as { listBinding: Function }
          ).listBinding(
            ({ div, span, a, button }: typeof elements, tile: Tile) =>
              div(
                {
                  class: 'tile',
                  bind: [
                    {
                      value: tile.key,
                      binding: (el: Element, v: string) => {
                        ;(el as HTMLElement).dataset.project = v
                        el.classList.toggle('unassigned', v === NONE_VALUE)
                      },
                    },
                    {
                      value: tile.retired,
                      binding: (el: Element, v: boolean) => {
                        ;(el as HTMLElement).classList.toggle('retired', !!v)
                      },
                    },
                    {
                      value: tile.bg,
                      binding: (el: Element, v: string) => {
                        ;(el as HTMLElement).style.background = v
                      },
                    },
                    {
                      value: tile.ink,
                      binding: (el: Element, v: string) => {
                        ;(el as HTMLElement).style.color = v
                      },
                    },
                  ],
                },
                div(
                  { class: 'tile-head' },
                  span({
                    class: 'icon',
                    bind: {
                      value: tile.icon,
                      binding: (el: Element, v: string) => renderIcon(el, v),
                    },
                  }),
                  span({ class: 'name', bindText: tile.name }),
                  span({
                    class: 'version',
                    bind: {
                      value: tile.version,
                      binding: (el: Element, v: string) => {
                        el.textContent = v
                        ;(el as HTMLElement).hidden = v === ''
                      },
                    },
                  }),
                  button(
                    { class: 'gear', title: 'project settings' },
                    icons.settings()
                  )
                ),
                span({
                  class: 'description',
                  bind: {
                    value: tile.description,
                    binding: (el: Element, v: string) => {
                      el.textContent = v
                      ;(el as HTMLElement).hidden = v === ''
                    },
                  },
                }),
                span({ class: 'summary', bindText: tile.summary }),
                span({ class: 'activity', bindText: tile.activity }),
                div(
                  { class: 'links' },
                  a({
                    target: '_blank',
                    rel: 'noopener',
                    bind: { value: tile.site, binding: linkBinding('site') },
                  }),
                  a({
                    target: '_blank',
                    rel: 'noopener',
                    bind: {
                      value: tile.packageUrl,
                      binding: linkBinding('npm'),
                    },
                  }),
                  a({
                    target: '_blank',
                    rel: 'noopener',
                    bind: { value: tile.repoUrl, binding: linkBinding('repo') },
                  })
                )
              ),
            {
              idPath: 'name',
              nonVirtualReason:
                'one tile per project, a wrapping grid: tens, never thousands',
            }
          )
        )
      ),
      this.projectPanel(),
      aside(
        {
          part: 'panel',
          class: 'overlay',
          hidden: true,
          onClick: (e: Event) => {
            if (e.target === e.currentTarget) this.select('')
          },
        },
        div(
          {
            part: 'cardWindow',
            class: 'window',
            onDragover: (e: DragEvent) => this.attachDragOver(e),
            onDragleave: (e: DragEvent) => this.attachDragLeave(e),
            onDrop: (e: DragEvent) => this.attachDrop(e),
          },
          div(
            { class: 'header' },
            span({ class: 'num' }, '#', span({ bindText: path('num') })),
            input({
              part: 'titleInput',
              class: 'title-input',
              bindValue: s.title,
              placeholder: 'task title',
              onChange: () => this.titleEdited(),
            }),
            button(
              {
                part: 'dismiss',
                class: 'dismiss',
                onClick: () => this.select(''),
              },
              '×'
            )
          ),
          div(
            { class: 'scroll', part: 'scroll' },
            Object.assign(
              tosiTagList({
                part: 'tagList',
                class: 'tag-list card-only',
                editable: true,
                textEntry: true,
                placeholder: 'add tag',
                onChange: () => this.tagsEdited(),
              }),
              // the same key › value menu as the filter (tosi-tag-list
              // lets its menu be replaced)
              { popSelectMenu: () => this.popCardTagMenu() }
            ),
            // concurrent edits, kept beside the body: choose or merge (#2473)
            div({
              part: 'conflictPanel',
              class: 'conflict-panel card-only',
              hidden: true,
            }),
            // the description: rendered to read, a field to write in — tap
            // it to edit in place, leave the field to save; an empty one is
            // just the field. No edit / save round trip (#144)
            div(
              { class: 'body-wrap' },
              div({
                part: 'body',
                class: 'body markdown',
                title: 'tap to edit',
                onClick: (e: Event) => this.bodyClicked(e),
              }),
              textarea({
                part: 'bodyInput',
                class: 'body-input',
                hidden: true,
                bindValue: s.bodyDraft,
                placeholder: 'description (markdown)',
                onInput: (e: Event) =>
                  fitTextarea(e.target as HTMLTextAreaElement),
                onChange: () => void this.bodyCommitted(),
                onKeydown: (e: KeyboardEvent) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation()
                    this.revertBody()
                  }
                },
              })
            ),
            // kind and priority, in the draft as on the card (board #2435)
            div(
              { class: 'pickers' },
              ...PICKER_KEYS.map((key) =>
                label(
                  `${key} `,
                  tosiSelect({
                    part: `${key}Select`,
                    class: 'lane-select',
                    onChange: (e: Event) => {
                      const raw = (e.target as TosiSelect).value
                      this.pickTag(key, raw === NO_VALUE ? '' : raw)
                    },
                  })
                )
              )
            ),
            div(
              // status and project, in the draft too (board #2438)
              { class: 'actions' },
              label(
                // the lane key it sets: "status" on the default board (#173)
                span({ part: 'laneLabel' }, 'status'),
                ' ',
                tosiSelect({
                  part: 'laneSelect',
                  class: 'lane-select',
                  bind: {
                    value: s.laneChoices,
                    binding: (el: Element, v: string[]) =>
                      this.renderChoices(el as TosiSelect, v),
                  },
                  onChange: (e: Event) => {
                    const raw = (e.target as TosiSelect).value
                    const value = raw === INBOX_VALUE ? '' : raw
                    if (value !== String(this.s.laneValue)) this.moveLane(value)
                  },
                })
              ),
              label(
                'project ',
                tosiSelect({
                  part: 'projectSelect',
                  class: 'lane-select',
                  title:
                    'hand it to another project: the one it leaves stays interested (delete the interested: tag to end that)',
                  bind: {
                    value: s.projectNames,
                    binding: (el: Element, v: string[]) =>
                      this.renderProjectChoices(el as TosiSelect, v),
                  },
                  onChange: (e: Event) => {
                    const raw = (e.target as TosiSelect).value
                    const value = raw === NO_PROJECT ? '' : raw
                    if (value !== String(this.s.projectValue))
                      this.moveProject(value)
                  },
                })
              ),
              div({
                class: 'workers',
                bind: {
                  value: s.tags,
                  binding: (el: Element, v: string) =>
                    this.renderWorkers(el, v),
                },
              })
            ),
            // a draft is nothing until Create: no number, no record, no
            // event (owner, 2026-09-26; board #1075)
            // cancel on the left, the default on the right (#1075)
            div(
              { class: 'draft-actions' },
              button(
                {
                  part: 'discardDraftButton',
                  title: 'Escape',
                  onClick: () => this.discardDraft(),
                },
                'Discard'
              ),
              button(
                {
                  part: 'createDraft',
                  class: 'primary',
                  title: 'Enter (⌘/Ctrl+Enter in the description)',
                  onClick: () => void this.createDraft(),
                },
                'Create'
              )
            ),
            // what practices proposed or did here, for a person to resolve (#2474)
            div({
              part: 'practicePanel',
              class: 'practice-panel card-only',
              hidden: true,
            }),
            // what it is part of, and its own parts (board #755)
            div({
              part: 'familyPanel',
              class: 'family-panel card-only',
              hidden: true,
            }),
            // a skill: the version served, who runs which, edits not served (#2477)
            div({
              part: 'skillPanel',
              class: 'family-panel card-only',
              hidden: true,
            }),
            // a practice's measure: how it's judged, and when to retire it (#2491)
            div({
              part: 'measurePanel',
              class: 'measure-panel card-only',
              hidden: true,
            }),
            // a finished task's showcase: shown, edited, or offered (#2439)
            div({
              part: 'showcasePanel',
              class: 'showcase-panel card-only',
              hidden: true,
            }),
            h3({ class: 'card-only' }, 'transcript'),
            ul(
              {
                class: 'transcript card-only',
                part: 'transcript',
                onClick: (e: Event) => this.toggleFromEvent(e),
              },
              ...(
                this.state.rows as unknown as { listBinding: Function }
              ).listBinding(
                ({ li, span }: typeof elements, row: Row) =>
                  li(
                    {
                      class: 'entry',
                      bind: [
                        {
                          value: row.type,
                          binding: (el: Element, v: string) =>
                            el.classList.toggle('group', v === 'group'),
                        },
                        {
                          value: row.state,
                          binding: (el: Element, v: string) => {
                            el.classList.toggle(
                              'retracted',
                              String(v) === 'retracted'
                            )
                            el.classList.toggle(
                              'revealed',
                              String(v) === 'revealed'
                            )
                          },
                        },
                      ],
                      apply: (el: Element) =>
                        bindRowId(el as HTMLElement, this.key),
                    },
                    span({
                      class: 'text',
                      bindText: row.text,
                    }),
                    span({ class: 'action', bindText: row.action }),
                    span({
                      class: 'media',
                      bind: {
                        value: row.media,
                        binding: (el: Element, v: string) =>
                          renderAttachment(
                            el as HTMLElement,
                            String(v ?? ''),
                            (file) => this.fileUrl(file)
                          ),
                      },
                    }),
                    span({ class: 'meta', bindText: row.by })
                  ),
                { idPath: 'id' }
              )
            ),
            div(
              { class: 'comment card-only' },
              textarea({
                part: 'comment',
                placeholder: 'comment…',
                bindValue: s.newComment,
              }),
              button(
                { part: 'send', onClick: () => this.sendComment() },
                'comment'
              ),
              // a file without dragging: a phone has no drag and drop, and
              // its picker offers the camera and the photo library (#2436)
              button(
                {
                  part: 'attachFile',
                  class: 'attach-file',
                  title: 'attach a file (or drop one on the card)',
                  onClick: () =>
                    (this.parts.filePicker as HTMLInputElement).click(),
                },
                '📎 attach'
              ),
              input({
                part: 'filePicker',
                type: 'file',
                multiple: true,
                hidden: true,
                accept:
                  'image/*,video/*,audio/*,application/pdf,.md,.markdown,.txt,text/plain,text/markdown',
                onChange: (e: Event) => {
                  const picker = e.target as HTMLInputElement
                  const files = Array.from(picker.files ?? [])
                  const id = String(this.s.selectedId)
                  if (files.length && id) void this.uploadFiles(id, files)
                  // the same file again is a new choice
                  picker.value = ''
                },
              })
            )
          )
        )
      ),
    ]
  }

  override connectedCallback(): void {
    super.connectedCallback()
    this.s.laneKey = this.lane

    dragAndDrop.init()
    // every dialog, the confirms included, gets the board's dialog styles
    useDocumentStyles()
    if (this.hash && !this.hashSync) {
      // the URL hash, when present, wins over the attributes it mirrors
      this.hashSync = hashState({ namespace: 'virta' })
      // the view the page was written with is where a bare URL lands
      this.homeView = this.view
      this.readHash(this.hashSync.values, { onlyPresent: true })
      this.pendingApproval = this.hashSync.values.approve ?? ''
      this.stopHash = this.hashSync.observe((values) => {
        if (this.writingHash) return
        this.readHash(values)
        if (values.approve && values.approve !== this.pendingApproval) {
          this.pendingApproval = values.approve
          void this.offerApproval()
        }
        this.refresh()
      })
    }
    // a URL that names the lanes wins; otherwise the slice's remembered ones
    if (
      this.hashSync?.values.hide === undefined &&
      String(this.hide ?? '') === HIDDEN_BY_DEFAULT.join(' ')
    )
      this.recallHide()
    // tosi-tag-list renders its chips on its own tick; recolor whenever they change
    const windowTags = this.parts.tagList as HTMLElement
    const filterTags = this.parts.filterTags as HTMLElement
    if (!this.chipObserver) {
      this.chipObserver = new MutationObserver(() => {
        colorTagChips(windowTags)
        colorTagChips(filterTags)
      })
      this.chipObserver.observe(windowTags, { childList: true, subtree: true })
      this.chipObserver.observe(filterTags, { childList: true, subtree: true })
    }
    this.applyFilterAttribute()
    this.appliedHide = this.hide
    this.appliedShow = this.show
    this.appliedAxis = this.axis
    this.appliedScope = this.scope
    this.appliedSort = this.sort
    this.appliedView = this.view
    ;(this.parts.sort as TosiSelect).value = this.sortKey()
    // A remount must re-subscribe: disconnect detaches, connect attaches.
    this.attach()
    if (
      !this._store &&
      (this.db || this.seed > 0 || this.snapshot || this.host)
    )
      void this.autoStore()
    this.refresh()
  }

  /**
   * A board with no store given but a `db`, `seed` or `snapshot` attribute
   * makes its own: an IndexedDB log named `db` (persistent), else an
   * in-memory one. An empty log is filled from `snapshot` (a URL of a JSON
   * list of event drafts: real projects, scrubbed — bin/demo-snapshot.ts),
   * else seeded with `seed` generated tasks. This is what a demo page needs.
   */
  private async autoStore(): Promise<void> {
    if (this.host) {
      await this.connectStore()
      return
    }
    const log = this.db ? new IndexedDBLog(this.db) : new MemoryLog()
    const store = new VirtaStore({ log, identity: this.identity || 'you' })
    await store.load()
    if (store.size === 0 && this.snapshot) {
      try {
        const res = await fetch(this.snapshot)
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
        await store.import((await res.json()) as EventDraft[])
      } catch (e) {
        this.complain(e)
      }
    }
    if (store.size === 0 && this.seed > 0) {
      await store.import(seedEvents({ tasks: this.seed, seed: 3 }))
    }
    if (!this._store) this.store = store
  }

  // ── a host ───────────────────────────────────────────────────────────────

  private renderedBody = ''
  private replica: IndexedDBLog | MemoryLog | null = null
  /** The stored token the live store authenticated with, or '' for a session (T5: refuse/sign-out keys on this, not on `session`). */
  private usedToken = ''
  /** What the replica was built for: `session:<uid>` or `token:<token>`, or '' before the first attach (re-review 7, B1). */
  private replicaCredential = ''
  /** Every connect and every replica teardown bumps this: a load superseded mid-flight must not attach (0.5.0 re-review 6, B3). */
  private connectGen = 0
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private visibilityListener: (() => void) | null = null

  private static tokenKey(host: string): string {
    return `virta:token:${host.replace(/\/$/, '')}`
  }

  /** The token for a host, from this browser's storage; never from markup or the URL. */
  static tokenFor(host: string): string {
    try {
      return localStorage.getItem(TosiVirta.tokenKey(host)) ?? ''
    } catch {
      return ''
    }
  }

  static forgetToken(host: string): void {
    try {
      localStorage.removeItem(TosiVirta.tokenKey(host))
    } catch {
      /* no storage */
    }
  }

  /**
   * A `host` board: a store over the host's log with this browser's
   * IndexedDB as the replica — named for the host (or `db` as the name's
   * base), always keyed by the credential (`replicaDbName`). No token
   * yet means the connect strip, not an error. `seed` is ignored — a shared
   * host is not a demo.
   */
  /** Swapped for a fake in tests; otherwise Firebase Auth over the CDN SDK. */
  static authFactory: (host: string) => Promise<AuthProvider> = async (
    host
  ) => {
    const projectId = projectIdFromHost(host)
    if (!projectId)
      throw new Error(`virta: cannot tell the Firebase project from ${host}`)
    return firebaseAuth(await fetchWebConfig(projectId))
  }

  private authProvider: AuthProvider | null = null
  private session: AuthSession | null = null

  private modes(): Set<string> {
    return new Set(
      String(this.auth ?? '')
        .split(/\s+/)
        .filter(Boolean)
    )
  }

  private async provider(): Promise<AuthProvider | null> {
    const modes = this.modes()
    if (!modes.has('google') && !modes.has('password')) return null
    if (!this.authProvider) {
      try {
        this.authProvider = await TosiVirta.authFactory(
          this.host.replace(/\/$/, '')
        )
      } catch (e) {
        this.complain(e)
        return null
      }
    }
    return this.authProvider
  }

  private complain(e: unknown): void {
    const message = e instanceof Error ? e.message : String(e)
    this.s.syncStatus = message.slice(0, 80)
    notify({ message, type: 'error', duration: 8 })
  }

  /** The avatar: initials when signed in, the user icon when not. */
  private renderAccount(): void {
    const button = this.parts.account as HTMLElement
    button.hidden = !this.host
    const who = String(this.s.signedInAs ?? '')
    button.replaceChildren()
    if (who) {
      const look = colorFor(who)
      button.textContent = initialsOf(who.replace(/@.*$/, ''))
      button.style.background = look.bg
      button.style.color = look.ink
      // the email lives in the tooltip only — never in the menu or the status
      button.title = who
    } else {
      button.append(icons.user())
      button.style.background = ''
      button.style.color = ''
      button.title = this._store
        ? 'connected with a token — account'
        : 'sign in'
    }
  }

  /** The account menu: ways in when signed out; who you are and the way out when in. */
  private popAccountMenu(): void {
    const modes = this.modes()
    const who = String(this.s.signedInAs ?? '')
    const items: MenuItem[] = []
    // any connected board has a replica to reload, a person's or a token's
    const reload: MenuItem = {
      caption: 'Reload from server',
      icon: 'refreshCw',
      action: () => void this.reloadFromHost(),
    }
    if (who) {
      if (this._store)
        items.push(
          {
            caption: 'Import from GitHub…',
            icon: 'github',
            action: () => void this.askImport(),
          },
          reload
        )
      items.push(null, {
        caption: 'sign out',
        icon: 'logOut',
        action: () => void this.signOut(),
      })
    } else {
      if (modes.has('google'))
        items.push({
          caption: 'Sign in with Google',
          icon: 'google',
          action: () => void this.signIn('google'),
        })
      if (modes.has('password'))
        items.push({
          caption: 'Sign in with email…',
          icon: 'logIn',
          action: () => void this.askPassword(),
        })
      if (modes.has('token'))
        items.push({
          caption: 'Use an agent token…',
          icon: 'key',
          action: () => void this.askToken(),
        })
      if (this._store)
        items.push(reload, {
          caption: 'forget this token',
          icon: 'logOut',
          action: () => void this.signOut(),
        })
    }
    // a page without the site header gets back to the docs from here;
    // only an http(s) or same-site address is a way out
    const docs = docsHref(String(this.docs ?? ''))
    if (docs)
      items.push(null, {
        caption: 'Docs',
        icon: 'bookOpen',
        action: () => location.assign(docs),
      })
    popMenu({
      target: this.parts.account as HTMLElement,
      width: 'auto',
      menuItems: items,
    })
  }

  /** Import from GitHub, from the account menu: the same dialog plumbing the other asks use. */
  private async askImport(): Promise<void> {
    const answer = await this.askDialog('Import from GitHub', [
      {
        name: 'repo',
        label: 'repository',
        type: 'text',
        autocomplete: 'off',
      },
    ])
    if (!answer) return
    await this.runImport(answer.repo ?? '')
  }

  /** A small modal on tosi-dialog: labelled fields, cancel / ok; null when cancelled. */
  private async askDialog(
    title: string,
    fields: {
      name: string
      label: string
      type: string
      autocomplete?: string
      /** what the field starts with (an edit) */
      value?: string
      /** the most characters it takes */
      maxLength?: number
    }[]
  ): Promise<Record<string, string> | null> {
    const { h3, label, input, button, div } = elements
    const inputs = new Map<string, HTMLInputElement>()
    // the dialog lives in the document, outside this board's styles
    useDocumentStyles()
    const dialog = tosiDialog(
      { class: 'virta-dialog', removeOnClose: true },
      h3({ slot: 'header' }, title),
      div(
        { class: 'virta-dialog-fields' },
        ...fields.map((f) => {
          const el = input({
            type: f.type,
            placeholder: f.label,
            value: f.value ?? '',
            ...(f.maxLength ? { maxLength: f.maxLength } : {}),
            autocomplete: f.autocomplete ?? 'off',
            onKeydown: (e: KeyboardEvent) => {
              if (e.key === 'Enter') dialog.close('ok')
            },
          }) as HTMLInputElement
          inputs.set(f.name, el)
          return label(f.label, el)
        })
      ),
      // tosi-dialog brings its own OK (close reason 'ok'); only cancel is ours
      button(
        { slot: 'footer', onClick: () => dialog.close('cancel') },
        'cancel'
      )
    ) as TosiDialog
    document.body.append(dialog)
    const reason = await dialog.showModal()
    if (reason !== 'ok') return null
    const out: Record<string, string> = {}
    for (const [name, el] of inputs) out[name] = el.value
    return out
  }

  private async askPassword(): Promise<void> {
    const answer = await this.askDialog('Sign in with email', [
      {
        name: 'email',
        label: 'email',
        type: 'email',
        autocomplete: 'username',
      },
      {
        name: 'password',
        label: 'password',
        type: 'password',
        autocomplete: 'current-password',
      },
    ])
    if (!answer) return
    await this.signIn(
      'password',
      (answer.email ?? '').trim(),
      answer.password ?? ''
    )
  }

  private async askToken(): Promise<void> {
    const answer = await this.askDialog('Use an agent token', [
      { name: 'token', label: 'tsp_… from bun run login', type: 'password' },
    ])
    if (!answer) return
    await this.connect((answer.token ?? '').trim())
  }

  /**
   * A `host` board: a store over the host's log with this browser's
   * IndexedDB as the replica — named for the host (or `db` as the name's
   * base), always keyed by the credential (`replicaDbName`). The bearer
   * is a signed-in person's ID token (Google, or email and password — the
   * `auth` attribute lists what the account menu offers) or an agent token
   * kept in this browser. Nothing yet means the account menu, not an error.
   * `seed` is ignored — a shared host is not a demo.
   */
  private async connectStore(): Promise<void> {
    this.connecting++
    if (this.hydrated) this.applyView()
    try {
      await this.connectHost()
    } finally {
      this.connecting--
      if (this.hydrated) this.applyView()
    }
  }

  /** Connects in flight: while any is, the page shows the loading sign. */
  private connecting = 0
  /** Nobody is signed in and no token is kept: the toolbar is the sign-in button alone. */
  private anonymous = false
  /** Signed out, the public projects have been asked for and not answered yet. */
  private facePending = false

  private async connectHost(): Promise<void> {
    let gen = ++this.connectGen
    this.anonymous = false
    this.facePending = false
    // a live store makes this connect a same-credential no-op; returning
    // here also keeps `this.replica` from being re-pointed at a cache the
    // live store is not using (an orphaned open connection — re-review 7)
    if (this._store) return
    const host = this.host.replace(/\/$/, '')
    // the base is part of the credential snapshot too: the database the
    // drop retires and the one this connect opens must agree (re-review 10)
    const db = this.db
    const modes = this.modes()
    // a session this browser already has
    if (!this.session) {
      const provider = await this.provider()
      if (provider) {
        try {
          const current = await provider.current()
          // only adopt the session if this connect is still the one being
          // built — a teardown may have landed while the lookup was out (B3)
          if (gen === this.connectGen) this.session = current
        } catch (e) {
          // a superseded connect's failing lookup is not this board's news (B3)
          if (gen === this.connectGen) this.complain(e)
        }
      }
    }
    // a teardown or a newer connect landed while the session was looked up:
    // bail before touching any state (B3)
    if (gen !== this.connectGen) return
    // ONE credential snapshot for everything this connect derives: the
    // bearer, the replica name, the identity and the credential guard must
    // never disagree about who this connect is (re-review 9, M1 — the
    // bearer was a lazy closure over the mutable field while the name was
    // an eager snapshot, so a sign-in landing mid-load could write one
    // account's rows into another account's database)
    const session = this.session
    const token = session
      ? () => session.idToken()
      : modes.has('token')
      ? TosiVirta.tokenFor(host)
      : ''
    if (!token) {
      this.s.syncStatus = 'not signed in'
      this.anonymous = true
      this.renderAccount()
      // the loading sign stays until the public face is known; not awaited,
      // so a sign-out or a connect never waits on an anonymous read
      this.facePending = true
      void this.loadPublicFace(host, gen)
      return
    }
    // the replica database is keyed by base AND principal, so a different
    // credential opens a different database by construction — a reload, a
    // second tab, or a sign-in can never replay what the last credential
    // was allowed to read (re-review 8, B1; the delete below is hygiene,
    // not correctness). The element-level drop severs a connect the same
    // tab supersedes mid-load: the delete closes its connection, and its
    // pending append rejects instead of writing (re-review 7, B1).
    const credential = session
      ? `session:${session.user.uid}`
      : `token:${String(token)}`
    const principal = session
      ? `s:${session.user.uid}`
      : `t:${tokenFingerprint(String(token))}`
    if (this.replica && this.replicaCredential !== credential) {
      await this.dropReplica()
      // a principal change may have landed while the drop was in flight —
      // that connect is the newer one and must win (re-review 9, M1)
      const stillCurrent = session
        ? this.session === session
        : TosiVirta.tokenFor(host) === token
      if (!stillCurrent) return
      // this connect's own drop bumped the generation: it is the new one
      gen = ++this.connectGen
    }
    const client = new ServiceClient({ host, token })
    // the replica: IndexedDB in a browser; memory where there is none
    // (tests, workers). The author's `db` name joins the host as the BASE,
    // namespaced by the principal — `db` alone would let two hosts share
    // one replica (re-review 10, M2), and the raw name would escape the
    // per-principal key (re-review 9, B1)
    const cache =
      typeof indexedDB === 'undefined'
        ? new MemoryLog()
        : new IndexedDBLog(
            replicaDbName(
              db ? `${db}@${new URL(host).host}` : new URL(host).host,
              principal
            )
          )
    this.replica = cache
    this.publicClient = client
    this.publicFace = null
    this.publicWritten = null
    this.publicRetryAt = 0
    const log = new CachedLog({
      remote: new RemoteLog({ client }),
      cache,
      // the host is down: the replica answers, and the toolbar says so
      onRemoteError: (e) => {
        this.s.syncStatus = this.describeSyncError(e)
      },
    })
    const identity = session
      ? session.user.email || session.user.name
      : this.identity || 'you'
    const store = new VirtaStore({ log, identity })
    this.s.syncStatus = 'loading…'
    try {
      await store.load()
    } catch (e) {
      // a teardown (sign-out, refusal, a newer connect) landed while this
      // load was in flight: whatever happened after it is the truth — this
      // connect must not act on the dropped credential (re-review 6, B3)
      if (gen !== this.connectGen) {
        if (cache instanceof IndexedDBLog) await cache.close()
        return
      }
      if (isAuthRefusal(e)) {
        // a refused credential: the same outcome as mid-session (`refused`),
        // and a stored token that never authenticated is forgotten there too
        await this.refused(e, identity, 0, typeof token === 'string')
      } else if (TosiVirta.isWithdrawn(e)) {
        // another tab deleted the database while this load was in flight:
        // not an outage — try again with a fresh replica
        this.replica = null
        await this.rebuildStore()
        return
      } else if (TosiVirta.isHostReset(e)) {
        // the host no longer holds the log this replica copied (a reset or
        // a restart): rebuild from the host, as "Reload from server" does.
        // The fresh replica is empty, so this cannot loop (board #16)
        this.s.syncStatus = 'the host was reset — reloading…'
        await this.dropReplica()
        await this.connectStore()
        return
      } else {
        this.s.syncStatus = this.describeSyncError(e)
      }
      this.renderAccount()
      return
    }
    if (gen !== this.connectGen) {
      // nobody attached this connect's store; its cache must not stay open
      // on the database name (B3's second half)
      if (cache instanceof IndexedDBLog) await cache.close()
      return
    }
    if (this._store) {
      // a racing connect attached first: this one's cache is unused — close
      // it like the generation bail does (re-review 8)
      if (cache instanceof IndexedDBLog) await cache.close()
      return
    }
    this.store = store
    // files are the host's too: read and stored with this connect's
    // credential (board #2436)
    this.fileService = client
    this.hostClient = client
    // what was drawn before the files could be fetched draws again
    this.carouselKey = ''
    this.refresh()
    // a login waiting on this person, from a link the CLI printed
    void this.offerApproval()
    // what the live store authenticated with: a stored token string, or a
    // session (whose id token is not ours to forget)
    this.usedToken = typeof token === 'string' ? token : ''
    this.replicaCredential = credential
    this.s.signedInAs = session ? identity : ''
    // a load that answered from the replica already wrote `offline: …`; keep it
    this.s.syncStatus = this.liveStatus(log)
    this.renderAccount()
    this.startPolling(store)
  }

  /**
   * The toolbar's status after a load or a sync: `live` (with who), unless
   * the replica answered because the host failed — then what `onRemoteError`
   * wrote (`offline: …`) stays until a sync succeeds. One place, so the
   * connect and the poll cannot disagree.
   */
  private liveStatus(log: { stale?: boolean }): string {
    if (log.stale) return String(this.s.syncStatus)
    return 'live'
  }

  /** Sign in: Google's popup, or the email and password given. */
  async signIn(
    how: 'google' | 'password',
    email = '',
    password = ''
  ): Promise<void> {
    const provider = await this.provider()
    if (!provider) return
    try {
      this.session =
        how === 'google'
          ? await provider.google()
          : await provider.password(email, password)
    } catch (e) {
      this.complain(e)
      return
    }
    // a session is a different principal than a stored token: the replica
    // goes first, exactly as a connect with a different token does (T5)
    if (this._store) {
      this.stopPolling()
      this.store = null
      this.usedToken = ''
      await this.dropReplica()
    }
    await this.connectStore()
  }

  /** What the host refused, shown on the page while it stands (#761). */
  private refusal = ''

  async signOut(): Promise<void> {
    this.refusal = ''
    this.stopPolling()
    const session = this.session
    this.session = null
    this.s.signedInAs = ''
    this.store = null
    // the token is forgotten before the wait, so a stalled sync cannot hold
    // a sign-out open with the credential still in storage
    if (this.usedToken) {
      TosiVirta.forgetToken(this.host.replace(/\/$/, ''))
      this.usedToken = ''
    }
    await this.dropReplica()
    if (session) {
      try {
        await session.signOut()
      } catch {
        /* signed out locally regardless */
      }
    }
    await this.connectStore()
  }

  /**
   * Drop this credential's replica and load the whole log from the host
   * again, keeping the credential. For a host whose log was reset: a
   * replica only ever asks for what comes after its cursor, and nothing
   * yet tells it the host started over (tosijs-platform#14), so without
   * this it keeps every deleted task. The drop is sign-out's own, so a
   * sync still in flight cannot land its page in the fresh replica.
   */
  async reloadFromHost(): Promise<void> {
    if (!this._store) return
    this.stopPolling()
    this.store = null
    this.s.syncStatus = 'reloading…'
    await this.dropReplica()
    await this.connectStore()
  }

  /** The agent-token path: keep the token in this browser and build the store. */
  async connect(token: string): Promise<void> {
    if (!token) return
    // a different principal than the live store's is a principal change:
    // the replica goes first (the browser twin of `virta login` dropping
    // the file replica). The comparison is what the store actually
    // authenticated with (`usedToken`), not what is in storage — a sign-in
    // leaves the stored token untouched (re-review 7)
    if (this.usedToken !== token) {
      this.stopPolling()
      this.store = null
      await this.dropReplica()
    }
    try {
      localStorage.setItem(TosiVirta.tokenKey(this.host), token)
    } catch {
      /* no storage: the store below still works for this page's life */
    }
    await this.connectStore()
  }

  /**
   * Other writers' events arrive by asking the host for the delta: on an
   * interval (`poll` seconds, 10 by default), after a tab comes back into
   * view, and after every write the store makes anyway. A hidden tab does
   * not poll.
   */
  private startPolling(store: VirtaStore): void {
    this.stopPolling()
    const seconds = this.poll > 0 ? this.poll : 10
    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return
      void this.syncNow(store)
    }
    this.pollTimer = setInterval(tick, seconds * 1000)
    this.visibilityListener = () => {
      if (!document.hidden) void this.syncNow(store)
    }
    document.addEventListener('visibilitychange', this.visibilityListener)
  }

  private stopPolling(): void {
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.pollTimer = null
    if (this.visibilityListener)
      document.removeEventListener('visibilitychange', this.visibilityListener)
    this.visibilityListener = null
  }

  /** The sync in flight, if any: one at a time, and a teardown waits for it. */
  private inFlight: Promise<void> | null = null
  /** The token of the sync whose body is on the stack right now, so a teardown inside that sync never waits on itself. */
  private syncToken = 0
  /** How long a replica teardown waits for a sync in flight (ms) before the terminal `close()` takes over. */
  replicaWaitMs = 1000

  async syncNow(store = this._store): Promise<void> {
    if (!store || this.inFlight) return
    const mine = ++this.syncToken
    const run = (async () => {
      try {
        await store.sync()
        // torn down meanwhile (sign-out, another token): nothing to report
        if (store !== this._store) return
        this.s.syncStatus = this.liveStatus(store.log as { stale?: boolean })
      } catch (e) {
        if (store !== this._store) return
        if (isAuthRefusal(e)) await this.refused(e, '', mine)
        else if (TosiVirta.isWithdrawn(e)) await this.rebuildStore()
        else this.s.syncStatus = this.describeSyncError(e)
      }
    })()
    this.inFlight = run
    try {
      await run
    } finally {
      if (this.inFlight === run) this.inFlight = null
    }
  }

  /**
   * Wait for a sync in flight (unless the caller is that sync) so it cannot
   * land on a replica being torn down. Bounded: a stalled poll must not
   * hold a sign-out open — `close()` is terminal, so a straggler that slips
   * past the bound cannot reopen the database either.
   */
  private async settled(unless = 0): Promise<void> {
    if (this.inFlight && unless !== this.syncToken)
      await Promise.race([
        this.inFlight.catch(() => undefined),
        new Promise<void>((r) => setTimeout(r, this.replicaWaitMs)),
      ])
  }

  /**
   * The host refused the credential mid-session: not an outage. A stored
   * token is forgotten, polling stops, the store is let go (the connect
   * strip is back), and the status says so; a signed-in session is kept
   * and told (its roles may have changed). Nothing is served or polled with
   * a dead secret.
   */
  /** A replica withdrawn by another tab (a sign-out or token switch there): the closed log, direct or wrapped in a ReplayError. */
  /** The host is not the log the replica copied: `HostResetError`, direct or wrapped in a ReplayError. */
  private static isHostReset(e: unknown): boolean {
    return (
      e instanceof HostResetError ||
      (e instanceof ReplayError && e.cause instanceof HostResetError)
    )
  }

  private static isWithdrawn(e: unknown): boolean {
    return (
      e instanceof ClosedLogError ||
      (e instanceof ReplayError && e.cause instanceof ClosedLogError)
    )
  }

  /**
   * Another tab deleted the replica out from under this board: not an
   * outage. Drop what pointed at it and build the store again — from the
   * host if the credential is still here (another tab's token switch), or
   * back to the connect strip if the sign-out elsewhere took it too.
   */
  private async rebuildStore(): Promise<void> {
    this.stopPolling()
    this.store = null
    this.replica = null
    // the marker must never describe a replica that is gone (re-review 10)
    this.replicaCredential = ''
    await this.connectStore()
  }

  /**
   * The host refused the credential mid-session: not an outage. A stored
   * token is forgotten, polling stops, the store is let go (the connect
   * strip is back), and the status says so; a signed-in session is kept
   * and told (its roles may have changed). Nothing is served or polled with
   * a dead secret. `unless` is the sync token of the caller when the
   * refusal came from a sync's own body; `stored` says the credential was
   * a stored token (mid-session, what `usedToken` says; at load, what the
   * connect was about to use).
   */
  private async refused(
    e: unknown,
    identity = '',
    unless = 0,
    stored = this.usedToken !== ''
  ): Promise<void> {
    const host = this.host.replace(/\/$/, '')
    const code =
      e instanceof ServiceError
        ? e.code || String(e.status)
        : e instanceof Error
        ? e.message.slice(0, 60)
        : String(e)
    this.stopPolling()
    this.store = null
    // a session is still who you are (the avatar keeps the initials); a
    // token was never a person
    this.s.signedInAs = this.session
      ? identity || String(this.s.signedInAs ?? '')
      : ''
    // the token is forgotten before the wait, so a stalled sync cannot hold
    // the refusal open with the dead credential still in storage
    if (stored) {
      TosiVirta.forgetToken(host)
      this.usedToken = ''
    }
    // what this credential was allowed to read must not outlive it
    await this.dropReplica(unless)
    if (!stored && this.session)
      this.refusal = `${
        identity || 'This account'
      } has no access to this board: the host answered "${code}". Ask the board's owner to add you, or sign out and use another account.`
    if (!stored && this.session) {
      this.complain(
        `${projectIdFromHost(host) ?? host} refused ${
          identity || 'this account'
        } (${code}): no role there, or not any more`
      )
    }
    this.s.syncStatus = `refused: ${code}`
    this.renderAccount()
    this.applyView()
  }

  /**
   * Drop this element's replica. The per-principal NAME (`replicaDbName`)
   * is what isolates credentials by construction — a different credential
   * opens a different database, whatever this delete does; this is the
   * same tab's hygiene: it severs a connect superseded mid-load (its
   * pending append rejects instead of writing) and retires the database
   * promptly. Sign-out, a refusal, and a connect with a different
   * credential all come through here.
   */
  private async dropReplica(unless = 0): Promise<void> {
    // a teardown invalidates any connect still loading — its credential was
    // just dropped, and it must not attach when the load settles (B3)
    this.connectGen++
    // a sync still in flight would append the old credential's page after
    // the delete; wait for it (bounded — see `settled`)
    await this.settled(unless)
    const replica = this.replica
    this.replica = null
    this.replicaCredential = ''
    if (replica instanceof IndexedDBLog) {
      const name = replica.name
      await replica.close()
      await new Promise<void>((resolve) => {
        const req = indexedDB.deleteDatabase(name)
        req.onsuccess = req.onerror = req.onblocked = () => resolve()
      })
    }
  }

  /** A board write that nobody awaits: its failure goes to the status line, never to the console alone. */
  private attempt(write: Promise<unknown>): void {
    write.catch((e: unknown) => {
      // the replica withdrawn under the write (another tab): rebuild, not an outage
      if (TosiVirta.isWithdrawn(e)) void this.rebuildStore()
      else this.complain(e)
    })
  }

  private describeSyncError(e: unknown): string {
    if (e instanceof ClosedLogError)
      return 'offline: closed (the replica was withdrawn by another tab)'
    if (e instanceof ServiceError) return `offline: ${e.code || e.status}`
    return `offline: ${e instanceof Error ? e.message.slice(0, 60) : String(e)}`
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    // removal is a teardown: a connect still loading must not attach and
    // poll a board nobody can see — the same generation bail a sign-out
    // uses (board #30); coming back into the document connects afresh
    this.connectGen++
    this.stopPolling()
    this.detach()
    if (this.stopHash) this.stopHash()
    this.stopHash = null
    this.hashSync?.stop()
    this.hashSync = null
    this.chipObserver?.disconnect()
    this.chipObserver = null
    this.carouselObserver?.disconnect()
    this.carouselObserver = null
    if (this.publicTimer) clearTimeout(this.publicTimer)
    this.publicTimer = null
    this.forgetFiles()
  }

  override render(): void {
    super.render()
    const c = boardColor(this.color)
    this.style.setProperty('--virta-bg', c.bg)
    this.style.setProperty('--virta-deep', c.deep)
    this.style.setProperty('--virta-ink', c.ink)
    // the filter first: every refresh below writes the component's filter
    // back to the attribute, so a filter set together with another
    // attribute (scope, axis…) must be taken before any of them refreshes
    if (this.hydrated && this.filter !== this.appliedFilter) {
      this.applyFilterAttribute()
      this.refresh()
    }
    // tosi() boxes scalars: compare the string, never the box.
    if (String(this.s.laneKey) !== this.lane) {
      this.s.laneKey = this.lane
      this.refresh()
    }
    if (this.hydrated && this.view !== this.appliedView) {
      this.appliedView = this.view
      this.refresh()
    }
    if (this.hydrated && this.sort !== this.appliedSort) {
      this.appliedSort = this.sort
      const sortSelect = this.parts.sort as HTMLSelectElement
      if (sortSelect.value !== this.sort) sortSelect.value = this.sortKey()
      this.refresh()
    }
    if (this.hydrated && this.axis !== this.appliedAxis) {
      this.appliedAxis = this.axis
      this.syncAxisControls()
      this.refresh()
    }
    if (this.hydrated && this.scope !== this.appliedScope) {
      this.appliedScope = this.scope
      this.syncAxisControls()
      this.refresh()
    }
    if (this.hydrated && this.hide !== this.appliedHide) {
      this.appliedHide = this.hide
      this.refresh()
    }
    if (this.hydrated && this.show !== this.appliedShow) {
      this.appliedShow = this.show
      this.refresh()
    }
    if (this.hydrated && this.group !== this.appliedGroup) {
      this.appliedGroup = this.group
      const select = this.parts.libraryGroup as TosiSelect
      if (select.value !== this.group) select.value = this.group
      this.refresh()
    }
  }

  // ── filter ───────────────────────────────────────────────────────────────

  /** The `filter` attribute is the source of truth; split it into the search box and the tag list. */
  private applyFilterAttribute(): void {
    const text = this.filter
    this.appliedFilter = text
    this.s.filter = text
    const parsed = parseFilter(text)
    this.s.search = parsed.text.join(' ')
    const terms = formatFilter({ ...parsed, text: [] })
      .split(' ')
      .filter(Boolean)
    const tagList = this.parts.filterTags as TosiTagList
    if (tagList.tags.join('\n') !== terms.join('\n')) tagList.tags = terms
  }

  /** Empty the filter: the search text and the tag chips, at once (board #2461). */
  private clearFilter(): void {
    this.s.search = ''
    ;(this.parts.filterTags as TosiTagList).tags = []
    this.composeFilter()
    ;(this.parts.filter as HTMLInputElement).focus()
  }

  private searchTyped = debounce(() => {
    this.composeFilter()
    this.openNamedSoon()
  }, 200)

  /**
   * `#1392` names one task: once typing pauses, open it as well as
   * filtering to it (owner, 2026-09-29; board #2438). Not on each
   * keystroke: `#13` on the way to `#1392` must not open #13. Enter opens
   * at once.
   */
  private openNamedSoon = debounce(() => this.openNamed(), 700)

  /**
   * Open the entry the search box names (`#n`), if it exists and isn't
   * open — wherever it lives (owner, 2026-10-14; board #3304): when the
   * view and scope in front of you don't show it, the board goes to where
   * it does — the library for a skill, a practice or guidance; the board
   * of its project (all projects if it has none) for a task.
   */
  private openNamed(): void {
    const input = this.parts.filter as HTMLInputElement
    const m = /^#(\d+)$/.exec(input.value.trim())
    const store = this._store
    const task = m && store ? store.byNum(Number(m[1])) : undefined
    if (!task || String(this.s.selectedId) === task.id) return
    this.goTo(task)
  }

  /**
   * Open an entry where it lives: if the view and scope in front of you
   * don't show it, go to the library (a skill, a practice, guidance) or to
   * its project's board (all projects if it has none) first. A `#n` typed
   * or in the URL opens it whatever you were looking at (board #3304).
   */
  private goTo(task: Task): void {
    const store = this._store
    if (!store) return
    // shown is what refresh laid out: the lanes on screen, done included,
    // a hidden lane's cards not (review 2026-10-16)
    const shown =
      this.view !== 'overview' && this.visible.some((t) => t.id === task.id)
    if (!shown) {
      const type = tagValues(task.tags, TYPE_KEY)[0] ?? TASK_TYPE
      this.setAxis('project')
      this.setScope(tagValues(task.tags, PROJECT_KEY)[0] ?? '')
      this.setView(type === TASK_TYPE ? 'board' : 'library')
    }
    // still not on screen: its lane is hidden (freezer, archive), so show
    // that lane for this look — not remembered as the slice's choice
    if (!this.visible.some((t) => t.id === task.id)) {
      const own =
        tagValues(task.tags, this.lane)[0] ??
        (this.lane === 'status' ? INBOX_STATUS : '')
      const hidden = this.hiddenLanes()
      if (hidden.includes(own)) {
        const text = hidden
          .filter((v) => v !== own)
          .map((v) => (v === '' ? '(inbox)' : v))
          .join(' ')
        this.appliedHide = text
        this.hide = text
        this.refresh()
      }
    }
    this.select(task.id)
  }

  /**
   * Search box + tag list → one filter string. A typed word that looks like a
   * term (`project:x`, `-status`, `#38`) is a term; anything else is `~word`.
   */
  private composeFilter(): void {
    if (!this.hydrated) return
    const terms = (this.parts.filterTags as TosiTagList).tags
    const words = String(this.s.search ?? '')
      .split(/\s+/)
      .filter(Boolean)
    // terms first (tag list, then typed), free-text words last
    const parts = [...terms]
    const typed = words.map((w) => (/[:#~]|^-/.test(w) ? w : `~${w}`))
    for (const term of [
      ...typed.filter((t) => !t.startsWith('~')),
      ...typed.filter((t) => t.startsWith('~')),
    ]) {
      if (!parts.includes(term)) parts.push(term)
    }
    const text = parts.join(' ')
    if (text === String(this.s.filter)) return
    this.appliedFilter = text
    this.s.filter = text
    this.refresh()
  }

  /** The tag list's menu: every key as a submenu of its values, bare tags, the closed switch. */
  private popFilterMenu = (): void => {
    const tagList = this.parts.filterTags as TosiTagList
    const store = this._store
    if (!store) return
    const { keyed, bare } = tagsByKey(store.tasks)
    const has = (term: string) => tagList.tags.includes(term)
    const toggle = (term: string) => {
      tagList.tags = has(term)
        ? tagList.tags.filter((t) => t !== term)
        : [...tagList.tags, term]
      this.composeFilter()
    }
    const setIs = (term: string | null) => {
      tagList.tags = [
        ...tagList.tags.filter((t) => !t.startsWith('is:')),
        ...(term ? [term] : []),
      ]
      this.composeFilter()
    }
    const setArchived = (term: string | null) => {
      tagList.tags = [
        ...tagList.tags.filter((t) => t !== 'archived' && t !== 'archived:any'),
        ...(term ? [term] : []),
      ]
      this.composeFilter()
    }
    const onProject =
      scopeKeyFor(this.axis) === 'project' &&
      !!this.scope &&
      this.scope !== NONE_VALUE
    const item = (caption: string, term: string): MenuItem => ({
      caption,
      icon: swatchFor(term),
      checked: () => has(term),
      action: () => toggle(term),
    })
    // a task link offers only the links in use, to open tasks in view
    const inUse = new Map(keyed)
    const { common, related, other } = tagMenuGroups(keyed, this.visible)
    // source has its own submenu here
    const otherKeys = other.filter(([k]) => k !== 'source')
    const keyMenu = ([key, values]: [string, string[]]): MenuItem => ({
      caption: key,
      menuItems: [
        item(`any ${key}`, key),
        item(`no ${key}`, `-${key}`),
        null,
        ...(key === 'kind'
          ? kindMenuItems(values, (v) => item(v, `kind:${v}`), false)
          : values
              .filter(
                (v) =>
                  !TASK_LINK_KEYS.includes(key) ||
                  (inUse.get(key) ?? []).includes(v)
              )
              .map((v) => item(this.valueCaption(key, v), `${key}:${v}`))),
      ],
    })
    const menuItems: MenuItem[] = [
      item('inbox', `${this.lane}:${INBOX_STATUS}`),
      null,
      ...common.map(keyMenu),
      ...(related.length > 0
        ? [{ caption: 'related to', menuItems: related.map(keyMenu) }]
        : []),
      // where a task came from: made on the board, or imported — a
      // submenu, since more sources may come (owner, 2026-09-28; #2425)
      ...(keyed.has('source')
        ? [
            {
              caption: 'source',
              menuItems: [
                item('virta (made on the board)', '-source'),
                null,
                ...keyed
                  .get('source')!
                  .map((v) => item(SOURCE_NAMES[v] ?? v, `source:${v}`)),
              ],
            } as MenuItem,
          ]
        : []),
      ...(otherKeys.length + bare.length > 0
        ? [
            {
              caption: 'other',
              menuItems: [
                ...otherKeys.map(keyMenu),
                ...(otherKeys.length && bare.length ? [null] : []),
                ...bare.map((b) => item(b, b)),
              ],
            },
          ]
        : []),
      null,
      {
        caption: 'archived tasks',
        menuItems: [
          {
            caption: 'hide (default)',
            checked: () => !has('archived') && !has('archived:any'),
            action: () => setArchived(null),
          },
          {
            caption: 'only archived',
            checked: () => has('archived'),
            action: () => setArchived('archived'),
          },
          {
            caption: 'show both',
            checked: () => has('archived:any'),
            action: () => setArchived('archived:any'),
          },
        ],
      },
      ...(onProject
        ? [
            {
              caption: 'this project',
              menuItems: [
                {
                  caption: 'own tasks',
                  checked: () => !this.showFlags().has('no-own'),
                  // own and related can't both be off (#765)
                  action: () =>
                    this.showFlags().has('no-others') &&
                    !this.showFlags().has('no-own')
                      ? undefined
                      : this.setShowFlag('no-own'),
                },
                {
                  caption: 'related tasks (owned by other projects)',
                  checked: () => !this.showFlags().has('no-others'),
                  action: () =>
                    this.showFlags().has('no-own') &&
                    !this.showFlags().has('no-others')
                      ? undefined
                      : this.setShowFlag('no-others'),
                },
                {
                  caption: 'only tasks another project cares about',
                  checked: () => this.showFlags().has('stakeholders'),
                  action: () => this.setShowFlag('stakeholders'),
                },
              ],
            } as MenuItem,
          ]
        : []),
      {
        caption: 'closed tasks',
        menuItems: [
          {
            caption: 'hide (default)',
            checked: () => !has('is:closed') && !has('is:any'),
            action: () => setIs(null),
          },
          {
            caption: 'only closed',
            checked: () => has('is:closed'),
            action: () => setIs('is:closed'),
          },
          {
            caption: 'show both',
            checked: () => has('is:any'),
            action: () => setIs('is:any'),
          },
        ],
      },
    ]
    popMenu({
      target: this.parts.filterMenu as HTMLElement,
      width: 'auto',
      menuItems,
    })
  }

  /** The board's view toggles, from the \`show\` attribute. */
  private showFlags(): Set<string> {
    return new Set(
      String(this.show ?? '')
        .split(/\s+/)
        .filter(Boolean)
    )
  }

  /** Flip one view toggle; the attribute (and so the URL) says it. */
  private setShowFlag(flag: string): void {
    const flags = this.showFlags()
    if (flags.has(flag)) flags.delete(flag)
    else flags.add(flag)
    const text = [...flags].sort().join(' ')
    this.appliedShow = text
    if (text !== String(this.show ?? '')) this.show = text
    this.refresh()
  }

  /**
   * The card's add-tag menu: the same key › value hierarchy as the filter
   * menu, over the same tags, without the filter-only entries (any / no /
   * inbox / closed). A pick goes through the tag list's own toggle, so it
   * is one write path with typing a tag.
   */
  private popCardTagMenu = (): void => {
    const tagList = this.parts.tagList as TosiTagList & {
      toggleTag(tag: string): void
    }
    const store = this._store
    if (!store) return
    const { keyed, bare } = tagsByKey(
      store.tasks,
      (t) => !t.startsWith('working:')
    )
    const item = (caption: string, tag: string): MenuItem => ({
      caption,
      icon: swatchFor(tag),
      checked: () => tagList.tags.includes(tag),
      action: () => tagList.toggleTag(tag),
    })
    const { common, related, other } = tagMenuGroups(
      keyed,
      this.visible.filter((t) => t.id !== String(this.s.selectedId))
    )
    const keyMenu = ([key, values]: [string, string[]]): MenuItem => ({
      caption: key,
      menuItems:
        key === 'kind'
          ? kindMenuItems(values, (v) => item(v, `kind:${v}`), true)
          : values.map((v) => item(this.valueCaption(key, v), `${key}:${v}`)),
    })
    popMenu({
      target: tagList.parts.tagMenu as HTMLElement,
      width: 'auto',
      menuItems: [
        ...common.map(keyMenu),
        ...(related.length > 0
          ? [{ caption: 'related to', menuItems: related.map(keyMenu) }]
          : []),
        ...(other.length + bare.length > 0
          ? [
              {
                caption: 'other',
                menuItems: [
                  ...other.map(keyMenu),
                  ...(other.length && bare.length ? [null] : []),
                  ...bare.map((b) => item(b, b)),
                ],
              },
            ]
          : []),
      ],
    })
  }

  /** A menu caption for a tag value: a task link names the task (`#12 title`). */
  private valueCaption(key: string, value: string): string {
    if (!TASK_LINK_KEYS.includes(key)) return value
    const n = Number(value.slice(1))
    const task = this._store?.tasks.find((t) => t.num === n)
    const title = task?.title ?? ''
    return title
      ? `${value} ${title.length > 40 ? `${title.slice(0, 39)}…` : title}`
      : value
  }

  // ── board ────────────────────────────────────────────────────────────────

  /** What the board shows for a filter, in the current view and scope. */
  private boardQuery(filter: string): string {
    const scopeKey = scopeKeyFor(this.axis)
    const scope = this.scope
    return this.view === 'library'
      ? // every type but tasks, in the project scoped to (if any)
        `${filter} -type:task${
          scopeKey === 'project' && scope && scope !== NONE_VALUE
            ? ` project:${scope}`
            : ''
        }`.trim()
      : scope
      ? `${filter} ${scopeKey}:${scope}`.trim()
      : filter
  }

  /** Recompute lanes from the store and the filter. Structural when lanes change; surgical otherwise. */
  refresh(changed?: readonly Task[]): void {
    if (!this.hydrated) return
    const store = this._store
    const filter = String(this.s.filter ?? '')
    if (this.filter !== filter) {
      this.appliedFilter = filter
      this.filter = filter
    }
    const axis = this.axis
    const scopeKey = scopeKeyFor(axis)
    // status and owner modes always scope to one status; only project mode has "all"
    if (axis !== 'project' && this.scope === '') {
      this.scope = DEFAULT_STATUS_SCOPE
      this.appliedScope = this.scope
      this.syncAxisControls()
    }
    const scope = this.scope
    // the library (board #2491): what isn't a task, in a column per type
    const library = this.view === 'library'
    // the lanes follow from the mode (see laneFor), or the type
    const group = LIBRARY_GROUPS.includes(this.group as LibraryGroup)
      ? (this.group as LibraryGroup)
      : 'type'
    const lane = library ? group : laneFor(axis)
    if (this.lane !== lane) {
      this.s.laneKey = lane
      this.lane = lane
    }
    const query = this.boardQuery(filter)
    // closed is done: a closed task lives in the done (or archive) lane, so
    // the board shows those alongside the open ones — unless the filter
    // says which it wants (is:open / is:closed / is:any)
    // a project's board also shows the tasks another project owns that it
    // is a party to (interested:, blocks:, delays:<this project>), marked
    // as theirs on the card (board #139)
    const flags = this.showFlags()
    const onProject = scopeKey === 'project' && !!scope && scope !== NONE_VALUE
    const related =
      store && onProject && !library && !flags.has('no-others')
        ? concerning(store.query(filter, null), scope)
        : []
    // unsorted: each lane sorts its own tasks below (board #36)
    const shown = !store
      ? []
      : /(^|\s)-?is:/.test(filter)
      ? store.query(query, null)
      : [
          ...store.query(query, null),
          ...store
            .query(`${query} is:closed`, null)
            .filter((t) => isFinished(t.tags)),
        ]
    // a project's own tasks that another project is waiting on or
    // interested in, when that's all the owner wants to see (#753)
    const owned =
      onProject && flags.has('no-own')
        ? []
        : onProject && flags.has('stakeholders')
        ? shown.filter((t) =>
            RELATION_KEYS.some((k) =>
              tagValues(t.tags, k).some((v) => v !== scope)
            )
          )
        : shown
    // one card per task: a filter that finds closed tasks itself (`#38`
    // means any state) overlaps the done-lane query above
    const tasks = [
      ...new Map([...owned, ...related].map((t) => [t.id, t])).values(),
    ]
    // a + on each lane needs a scope to put the task in; "all projects" has
    // none — but the library's + needs only the type (its column)
    this.classList.toggle('has-project', scope !== '' || library)
    // the slice menu offers both lists whatever the mode: the statuses
    // (status and owner modes scope to one) and the projects
    const projects = new Set<string>()
    const statuses = new Set<string>(STATUS_ORDER)
    let loose = false
    if (store)
      for (const t of store.tasks) {
        const own = tagValues(t.tags, 'project')
        for (const v of own) projects.add(v)
        for (const v of tagValues(t.tags, 'status')) statuses.add(v)
        if (own.length === 0 && t.closedAt === null) loose = true
      }
    statuses.delete(INBOX_STATUS)
    const statusChoices = [
      NONE_VALUE,
      ...orderLanes(statuses).filter((v) => v !== ''),
    ]
    // a retired project is not a scope anyone should pick — unless the
    // board is scoped to it
    let projectChoices = Array.from(projects)
      .sort()
      .filter((v) => !store || !isRetired(store.project(v)))
    if (loose) projectChoices.push(NONE_VALUE)
    const scopeList = scopeKey === 'project' ? projectChoices : statusChoices
    if (scope && !scopeList.includes(scope)) scopeList.push(scope)
    // the menu shows each project's icon, so an icon (or retire) change
    // must rebuild it too, not only a change in the values
    const looks = store ? projectChoices.map((v) => store.project(v).icon) : []
    const choicesKey = [
      statusChoices.join('\n'),
      projectChoices.join('\n'),
      looks.join('\n'),
    ].join('\n\n')
    if (choicesKey !== this.sliceChoicesKey) {
      this.sliceChoicesKey = choicesKey
      this.s.sliceChoices = { project: projectChoices, status: statusChoices }
    }
    const names = Array.from(projects).sort()
    const namesKey = names.join('\n')
    if (namesKey !== this.projectNamesKey) {
      this.projectNamesKey = namesKey
      this.s.projectNames = names
    }
    // inbox is the implicit status: a task with no status tag lanes there
    const lanes = groupByTag(
      tasks,
      this.lane,
      this.lane === 'status' ? INBOX_STATUS : ''
    )
    // Every canonical status is a lane even when empty (a drop target); other
    // values appear when a task carries them. Hidden lanes are skipped. The
    // library shows each type's column, even empty: where to put things
    const known: readonly string[] = library
      ? LIBRARY_LANES[group]
      : this.lane === 'status'
      ? STATUS_ORDER
      : ['']
    for (const v of known) if (!lanes.has(v)) lanes.set(v, [])
    // grouped by type, every item has one; otherwise "no topic" stays, last
    if (library && group === 'type') lanes.delete('')
    const hidden = library ? new Set<string>() : new Set(this.hiddenLanes())
    const order = library
      ? [
          ...known,
          ...Array.from(lanes.keys())
            .filter((v) => v !== '' && !known.includes(v))
            .sort(),
          ...(lanes.has('') ? [''] : []),
        ]
      : orderLanes(lanes.keys()).filter((v) => !hidden.has(v))
    this.syncLaneToggles(Array.from(lanes.keys()), hidden)
    this.syncShowToggles(onProject)
    // the count is what is on screen: matching tasks in lanes that are shown
    this.visible = order.flatMap((v) => lanes.get(v)!)
    this.s.count = this.visible.length
    const narrowed = filter !== '' || scope !== ''
    const empty = this.parts.empty as HTMLElement
    empty.hidden = !(store && this.visible.length === 0 && narrowed)
    if (!empty.hidden) {
      ;(this.parts.emptyText as HTMLElement).textContent = scope
        ? `no ${axis === 'status' ? scope : `${scope}`} tasks match “${
            filter || 'everything'
          }”`
        : `no tasks match “${filter}”`
    }
    const ids = order.map(safeId)
    const byLane = this.s.byLane as unknown as Record<string, Task[]>
    // A lane is reassigned only when its membership or order changed; the
    // fold mutates Task objects in place, so a lane whose cards are the same
    // cards in the same order keeps its array, and only the tasks this write
    // changed are told (their card's own bindings re-run). With no change
    // list — a filter, sort or store change — every lane is reassigned.
    // One comment on a 400-task board: 68 ms → see board #51.
    const changedIds = changed ? new Set(changed.map((t) => t.id)) : null
    const previous = new Map(this.laneTasks)
    this.laneTasks.clear()
    for (let i = 0; i < order.length; i++) {
      const value = order[i]!
      const id = ids[i]!
      const sorted = sortTasks(lanes.get(value)!, this.sortKey())
      const before = previous.get(id)
      const same =
        changedIds !== null &&
        before !== undefined &&
        before.length === sorted.length &&
        before.every((t, j) => t === sorted[j])
      if (!same) {
        this.laneTasks.set(id, sorted)
        ;(byLane as Record<string, unknown>)[id] = sorted
        continue
      }
      // the lane keeps its array — the one `s.byLane` holds — in both places
      this.laneTasks.set(id, before)
      for (const task of sorted)
        if (changedIds.has(task.id))
          touch(`${this.key}.byLane.${id}[id=${task.id}]`)
    }
    for (const id of Object.keys(byLane))
      if (!ids.includes(id)) (byLane as Record<string, unknown>)[id] = []
    const current = (this.s.laneIds as unknown as string[]).slice()
    if (current.join('\n') !== ids.join('\n')) {
      this.s.laneIds = ids
      this.buildLanes(order, ids)
    }
    this.updateLaneCounts(order, ids, lanes)
    const selected = String(this.s.selectedId)
    if (selected) this.showCard(selected)
    if (this.pendingTask && !selected) this.openPendingTask()
    this.applyView()
    this.writeHash()
  }

  /** The empty state's escape hatch: drop the filter, keep the project. */
  private clearView(): void {
    this.setFilter('')
    this.refresh()
  }

  private setFilter(text: string): void {
    this.appliedFilter = text
    this.filter = text
    this.applyFilterAttribute()
  }

  // ── overview ─────────────────────────────────────────────────────────────

  /**
   * The axis select. Project mode opens on "all projects"; status and owner
   * modes keep the status already scoped to, or open on in-progress.
   * Returns whether anything changed: a programmatic value write also fires
   * the select's change, and that echo must not read as a menu pick.
   */
  private setAxis(axis: string): boolean {
    if (axis === this.appliedAxis && axis === this.axis) return false
    const wasStatus = scopeKeyFor(this.axis) === 'status'
    const scope =
      axis === 'project'
        ? ''
        : wasStatus && this.scope
        ? this.scope
        : DEFAULT_STATUS_SCOPE
    this.appliedAxis = axis
    this.appliedScope = scope
    if (this.axis !== axis) this.axis = axis
    if (this.scope !== scope) this.scope = scope
    this.recallHide()
    this.syncAxisControls()
    this.refresh()
    return true
  }

  /** The scope select: all of the axis, or one value of it. Returns whether anything changed (see `setAxis`). */
  private setScope(value: string): boolean {
    if (value === this.appliedScope && value === this.scope) return false
    this.appliedScope = value
    if (this.scope !== value) this.scope = value
    this.recallHide()
    this.refresh()
    this.syncAxisControls()
    return true
  }

  /** Attributes, the hash, or a tile click set the axis and scope from outside the menus. */
  private syncAxisControls(): void {
    if (!this.hydrated) return
    const slice = this.parts.slice as TosiSelect
    const wanted = sliceValue(this.axis, this.scope)
    if (slice.value !== wanted) slice.value = wanted
  }

  /**
   * A pick in the slice menu: the mode, then the slice of it. A pick is a
   * board-view concern — except project › all projects, which is the tile
   * view's own shape and applies in place. A programmatic value write also
   * fires the select's change; its echo changes nothing and goes nowhere.
   */
  private pickSlice(axis: string, scope: string): void {
    if (!(AXES as readonly string[]).includes(axis)) return
    const axisChanged = this.setAxis(axis)
    const scopeChanged = this.setScope(scope)
    if ((axisChanged || scopeChanged) && !(axis === 'project' && scope === ''))
      this.setView('board')
  }

  /** The view switch. The tile view IS projects | all projects, so entering it sets both. */
  private setView(view: string): void {
    if (view === 'overview' && this.view !== view) {
      this.setAxis('project')
      this.setScope('')
    }
    this.appliedView = view
    if (this.view !== view) this.view = view
    this.refresh()
  }

  /**
   * Show the board or the overview; rebuild tiles only when they are on
   * screen. Every store change (connect, teardown) and every view change
   * refreshes, so this is the one place the surface is decided:
   * - the overview needs only the view switch and the account button;
   * - a host board with no credential reads as "sign in", never as an empty
   *   project — what remains is the strip: the `not signed in` status and
   *   the account button, whose menu is the way in.
   */
  private applyView(): void {
    const signedOut = !!this.host && !this._store
    // signed out, the public projects' tiles are all there is to see: no
    // view to switch to, nothing to open (owner, 2026-10-13)
    const face = signedOut && !!this.publicFace?.length
    const overview = this.view === 'overview'
    const parts = this.parts as unknown as Record<string, HTMLElement>
    this.classList.toggle('signed-out', signedOut)
    parts.viewSwitch!.hidden = signedOut
    parts.overviewScroll!.hidden = face ? false : !overview || signedOut
    parts.board!.hidden = overview || signedOut
    parts.lanesRow!.hidden = overview || signedOut
    // the library groups its own way: no status lanes, no show toggles
    this.classList.toggle('library', this.view === 'library')
    if (overview) parts.empty!.hidden = true
    for (const p of BOARD_TOOLBAR_PARTS)
      parts[p]!.hidden = overview || signedOut
    // signed out, the toolbar is the sign-in button alone (owner,
    // 2026-10-13); loading, the sign in the page says it
    const loading =
      !this._store && (this.connecting > 0 || (signedOut && this.facePending))
    // nothing to show: signed out, and no project is public
    const bare = signedOut && !loading && !face && !this.refusal
    parts.splash!.hidden = !(loading || bare)
    parts.splash!.classList.toggle('busy', loading)
    parts.splashText!.textContent = loading
      ? 'loading the board…'
      : bare
      ? 'sign in to see the board'
      : ''
    parts.sync!.hidden =
      (overview && !signedOut) || (signedOut && (this.anonymous || loading))
    parts.clearFilter!.hidden = signedOut || !String(this.s.filter ?? '').trim()
    parts.notice!.hidden = !(signedOut && this.refusal)
    if (this.refusal) parts.noticeText!.textContent = this.refusal
    const view = this.parts.viewSwitch as TosiSegmented
    if (view.value !== this.view) view.value = this.view
    if (face) this.s.tiles = this.publicFace!.map((p) => this.publicTile(p))
    else if (overview) this.buildTiles()
    this.renderCarousel(overview, signedOut)
    this.schedulePublicSync()
  }

  // ── public projects (board #3267) ────────────────────────────────────────

  /** What a signed-out viewer sees: each public project's summary; null until asked. */
  private publicFace: PublicProject[] | null = null
  /** The signed-in client: what writes the summaries. */
  private publicClient: ServiceClient | null = null
  private publicTimer: ReturnType<typeof setTimeout> | null = null
  /** The summaries as last written, so an unchanged board asks the host nothing. */
  private publicWritten: string | null = null
  /** After a failed write: when to try again. */
  private publicRetryAt = 0
  /** A sync is on the wire: the next waits for it. */
  private publicSyncing = false

  /** Signed out: ask the host for its public projects, with no credential. */
  private async loadPublicFace(host: string, gen: number): Promise<void> {
    try {
      const face = await fetchPublic(new ServiceClient({ host }))
      if (gen !== this.connectGen || this._store) return
      this.publicFace = face
    } catch {
      // a host with no public projects (or an older manifest) refuses the
      // list: that is "nothing public", and the sign-in strip says the rest
    } finally {
      if (gen === this.connectGen) {
        this.facePending = false
        if (this.hydrated) this.applyView()
      }
    }
  }

  private publicTile(p: PublicProject): Tile {
    const parts = [`${p.open} open`]
    if (p.inProgress) parts.push(`${p.inProgress} in progress`)
    if (p.blocked) parts.push(`${p.blocked} blocked`)
    const color = apartFrom(
      boardColor(this.color).bg,
      projectLook({ name: p.name, color: p.color, background: p.background })
    )
    return {
      key: p.name,
      retired: p.retired,
      name: p.name,
      icon: p.icon || initialsOf(p.name),
      bg: color.bg,
      ink: color.ink,
      description: p.description,
      version: p.version,
      summary: parts.join(' · '),
      activity: p.activity
        ? `last activity ${timeAgo(p.activity)} ago`
        : 'no activity yet',
      site: p.site,
      packageUrl: p.package ? `https://www.npmjs.com/package/${p.package}` : '',
      repoUrl: p.repo
        ? /^https?:/.test(p.repo)
          ? p.repo
          : `https://github.com/${p.repo}`
        : '',
    }
  }

  /**
   * Signed in: keep the host's public summaries in line with the log. It
   * asks the host only when what they should say has changed since this
   * board last wrote them (and once after connecting, which also removes
   * the summary of a project made private elsewhere).
   */
  private schedulePublicSync(): void {
    const store = this._store
    const client = this.publicClient
    if (!store || !client || !this.host) return
    if (this.publicTimer || this.publicSyncing) return
    if (Date.now() < this.publicRetryAt) return
    const wanted = JSON.stringify(
      publicSummaries(store).map((p) => ({ ...p, seq: 0 }))
    )
    if (wanted === this.publicWritten) return
    this.publicTimer = setTimeout(() => {
      this.publicTimer = null
      if (this._store !== store || this.publicClient !== client) return
      // written only once the host says so: a page put to sleep mid-sync
      // tries again on its next change or load, never thinks it is done
      this.publicSyncing = true
      void syncPublic(store, client)
        .then(() => {
          this.publicWritten = wanted
        })
        .catch((e) => {
          // not written (offline, or a host without the collection yet):
          // try again later, not on every poll — and say why in the console
          console.warn('virta: public summaries not written', e)
          this.publicRetryAt = Date.now() + 60_000
        })
        .finally(() => {
          this.publicSyncing = false
        })
    }, 5000)
  }

  private buildTiles(): void {
    const store = this._store
    if (!store) return
    // a retired project leaves the overview but keeps its place in history:
    // the muted list at the bottom keeps it reachable (and unretirable)
    const tiles: Tile[] = []
    const retired: Tile[] = []
    for (const p of store.projects)
      (isRetired(p) ? retired : tiles).push(this.projectTile(p))
    // tasks with no project are called out, not required to have one
    const loose = store.query(`project:${NONE_VALUE}`)
    if (loose.length) {
      const doing = loose.filter((t) =>
        tagValues(t.tags, 'status').includes('in-progress')
      ).length
      const parts = [`${loose.length} open`]
      if (doing) parts.push(`${doing} in progress`)
      const latest = loose.reduce(
        (max, t) => (t.updatedAt > max ? t.updatedAt : max),
        ''
      )
      const none = apartFrom(boardColor(this.color).bg, PALETTE.slate!)
      tiles.unshift({
        key: NONE_VALUE,
        retired: false,
        name: 'no project',
        icon: '∅',
        bg: none.bg,
        ink: none.ink,
        description: 'tasks that belong to no project yet',
        version: '',
        summary: parts.join(' · '),
        activity: latest ? `last activity ${timeAgo(latest)} ago` : '',
        site: '',
        packageUrl: '',
        repoUrl: '',
      })
    }
    // one list: the retired tiles trail the active ones, muted by class
    this.s.tiles = [...tiles, ...retired]
  }

  private projectTile(p: ProjectProfile & { open: number }): Tile {
    const store = this._store!
    {
      const tasks = store.query(`project:${p.name}`)
      const doing = tasks.filter((t) =>
        tagValues(t.tags, this.lane).includes('in-progress')
      ).length
      const blocked = tasks.filter((t) => isBlocked(t.tags)).length
      const waiting = waitingOn(store.tasks, p.name).length
      const latest = store
        .query(`project:${p.name} is:any`)
        .reduce((max, t) => (t.updatedAt > max ? t.updatedAt : max), '')
      const parts = [`${p.open} open`]
      if (doing) parts.push(`${doing} in progress`)
      if (blocked) parts.push(`${blocked} blocked`)
      if (waiting) parts.push(`waiting on ${waiting} elsewhere`)
      // anyone can see this tile, signed in or not (board #3267)
      if (isPublic(p)) parts.push('public')
      const color = apartFrom(boardColor(this.color).bg, projectLook(p))
      const repoUrl = p.repo
        ? /^https?:/.test(p.repo)
          ? p.repo
          : `https://github.com/${p.repo}`
        : ''
      return {
        key: p.name,
        retired: isRetired(p),
        name: p.name,
        icon: p.icon || initialsOf(p.name),
        bg: color.bg,
        ink: color.ink,
        description: p.description,
        version: p.version,
        summary: parts.join(' · '),
        activity: latest
          ? `last activity ${timeAgo(latest)} ago`
          : 'no activity yet',
        site: p.site,
        packageUrl: p.package
          ? `https://www.npmjs.com/package/${p.package}`
          : '',
        repoUrl,
      }
    }
  }

  private overviewClicked(e: Event): void {
    const target = e.target as Element
    if (target.closest('a')) return
    const tile = target.closest('.tile') as HTMLElement | null
    if (!tile?.dataset.project) return
    // signed out there is nothing under a tile to open
    if (!this._store) return
    if (target.closest('.gear')) {
      // the unassigned tile is derived; there is nothing to set
      if (tile.dataset.project !== NONE_VALUE)
        this.openProjectSettings(tile.dataset.project)
      return
    }
    this.setAxis('project')
    this.setScope(tile.dataset.project)
    this.setView('board')
  }

  /**
   * The project settings panel. The header wears the project's own look and
   * follows the form as it is edited, so the tile is previewed where it is
   * set. Every setting is a text field — the pickers beside icon, color and
   * background only fill it in, so a palette name, a gradient or an image
   * URL survives an unrelated save. The site is ingest-inferred (the repo's
   * homepage) but stays editable.
   */
  private projectPanel(): Element {
    const { aside, div, span, button, input, label, h4, datalist, option } =
      elements
    const path = (p: string) => `${this.key}.${p}`
    const form = () => this.s.projectForm as unknown as ProjectForm
    const state = this.state.projectForm as unknown as Record<string, unknown>
    const field = (f: (typeof PROJECT_FIELDS)[number]) => {
      const text = input({
        part: `project-${f.key}`,
        placeholder: f.hint,
        bindValue: state[f.key],
      })
      // the palette names as suggestions (`list` is attribute-only)
      if (f.key === 'color') text.setAttribute('list', 'virta-palette-names')
      const helpers: HTMLElement[] = []
      if (f.key === 'icon') {
        // the native file input's own face ("Choose File No file chosen")
        // does not fit a field row: a button opens it
        const file = input({
          part: 'project-icon-file',
          type: 'file',
          accept: 'image/*',
          hidden: true,
          onChange: (e: Event) => void this.iconPicked(e),
        })
        helpers.push(
          file,
          button(
            {
              type: 'button',
              class: 'pick-file',
              title: 'use an image file as the icon',
              onClick: () => file.click(),
            },
            'choose file…'
          )
        )
      }
      if (f.key === 'color' || f.key === 'background') {
        const key = f.key
        helpers.push(
          input({
            part: `project-${key}-picker`,
            type: 'color',
            title: `pick a ${key}`,
            bind: {
              value: path(`projectForm.${key}`),
              binding: (el: Element, v: string) => {
                // a picker cannot show a gradient, an image or an unset
                // field: it reads as unset, never as black
                const hex = pickerHex(String(v ?? ''))
                if (hex) (el as HTMLInputElement).value = hex
                el.classList.toggle('unset', !hex)
              },
            },
            onInput: (e: Event) => {
              form()[key] = (e.target as HTMLInputElement).value
            },
          })
        )
      }
      return label(
        { class: 'field' },
        span({ class: 'caption' }, f.caption),
        div({ class: 'field-row' }, text, ...helpers)
      )
    }
    const sections = [...new Set(PROJECT_FIELDS.map((f) => f.section))]
    return aside(
      {
        part: 'projectPanel',
        class: 'overlay',
        hidden: true,
        onClick: (e: Event) => {
          if (e.target === e.currentTarget) this.closeProjectSettings()
        },
      },
      div(
        { class: 'window project-window' },
        div(
          {
            part: 'projectLook',
            class: 'header look',
            // the look is every one of these fields together: one binding
            // each, all painting the same header
            bind: (['name', 'icon', 'color', 'background'] as const).map(
              (key) => ({
                value: path(`projectForm.${key}`),
                binding: (el: Element) => paintProjectLook(el, form()),
              })
            ),
          },
          span({ class: 'icon' }),
          span({ class: 'project-name', bindText: path('projectForm.name') }),
          button(
            {
              class: 'dismiss',
              title: 'close',
              onClick: () => this.closeProjectSettings(),
            },
            '×'
          )
        ),
        div(
          { class: 'scroll', part: 'projectScroll' },
          ...sections.map((section) =>
            div(
              { class: 'fields' },
              h4(section),
              ...PROJECT_FIELDS.filter((f) => f.section === section).map(field)
            )
          ),
          datalist(
            { id: 'virta-palette-names' },
            ...PALETTE_NAMES.map((n) => option({ value: n }))
          )
        ),
        div(
          { class: 'footer' },
          button(
            {
              part: 'retireProject',
              class: 'quiet',
              onClick: () => void this.retireProject(),
              bind: {
                value: path('projectForm.retired'),
                binding: (el: Element, v: string) =>
                  iconButton(
                    el,
                    'archive',
                    isRetired({ retired: String(v ?? '') })
                      ? 'unretire'
                      : 'retire',
                    'a retired project leaves the scope menu and trails the overview; its history stays'
                  ),
              },
            },
            'retire'
          ),
          button(
            {
              part: 'publicProject',
              class: 'quiet',
              onClick: () => void this.togglePublicProject(),
              bind: {
                value: path('projectForm.public'),
                binding: (el: Element, v: string) => {
                  const why =
                    "a public project's tile shows for anyone, signed in or not: its name, description, links and counts — never its tasks"
                  if (isPublic({ public: String(v ?? '') }))
                    iconButton(el, 'lock', 'make private', why)
                  else iconButton(el, 'globe', 'make public', why)
                },
              },
            },
            'make public'
          ),
          button(
            {
              part: 'refreshProject',
              class: 'quiet',
              title:
                'refresh from GitHub: pull description, links, version and new issues from the repo',
              'aria-label': 'refresh from GitHub',
              onClick: () => this.refreshFromGitHub(),
            },
            icons.refreshCw!(),
            span({ class: 'label' }, 'refresh from GitHub')
          ),
          span({ class: 'spacer' }),
          button({ onClick: () => this.closeProjectSettings() }, 'cancel'),
          button(
            {
              part: 'saveProject',
              class: 'primary',
              onClick: () => this.saveProjectSettings(),
            },
            'save'
          )
        )
      )
    )
  }

  openProjectSettings(name: string): void {
    const store = this._store
    if (!store) return
    const p = store.project(name)
    const form = this.s.projectForm as unknown as ProjectForm
    form.name = name
    for (const key of PROJECT_FORM_KEYS) form[key] = p[key]
    ;(this.parts.projectPanel as HTMLElement).hidden = false
  }

  private closeProjectSettings(): void {
    ;(this.parts.projectPanel as HTMLElement).hidden = true
  }

  /**
   * A file-picked icon, written into the icon field as an inline image
   * (saved with the form): an SVG as it is; anything else center-cropped to
   * a square and downscaled to 64px.
   */
  private async iconPicked(e: Event): Promise<void> {
    const picker = e.target as HTMLInputElement
    const file = picker.files?.[0]
    // clear the picker so picking the same file again still fires change
    picker.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      this.complain(new Error('choose an image file for the icon'))
      return
    }
    // an SVG stays an SVG: sharp at any size (board #2544)
    if (contentTypeFor(file.name, file.type) === 'image/svg+xml') {
      const icon = iconDataUrl(
        file.name,
        new Uint8Array(await file.arrayBuffer()),
        file.type
      )
      if (!icon.ok) this.complain(new Error(icon.why))
      else (this.s.projectForm as unknown as ProjectForm).icon = icon.url
      return
    }
    const url = URL.createObjectURL(file)
    try {
      const image = new Image()
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve()
        image.onerror = () => reject(new Error('could not read that image'))
        image.src = url
      })
      const size = 64
      const side = Math.min(image.naturalWidth, image.naturalHeight)
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      canvas
        .getContext('2d')!
        .drawImage(
          image,
          (image.naturalWidth - side) / 2,
          (image.naturalHeight - side) / 2,
          side,
          side,
          0,
          0,
          size,
          size
        )
      const form = this.s.projectForm as unknown as ProjectForm
      form.icon = canvas.toDataURL('image/png')
    } catch (err) {
      this.complain(err)
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  /** The account menu's Import from GitHub…, or the settings panel's refresh: onboard / re-sync a repo. */
  async runImport(repo: string, project = ''): Promise<void> {
    const store = this._store
    repo = repo.trim()
    if (!store || !repo) return
    // an import can take a while (every issue, every comment): say so
    const done = notify({ message: `importing ${repo}…`, type: 'progress' })
    try {
      const result = await importProject(
        store,
        repo,
        project ? { project } : {}
      )
      notify({
        message: `${result.project}: ${result.issues} issues, ${result.created} new`,
        type: 'info',
        duration: 6,
      })
    } catch (e) {
      this.complain(e)
    } finally {
      done()
    }
  }

  private async refreshFromGitHub(): Promise<void> {
    const form = this.s.projectForm as unknown as ProjectForm
    const repo = String(form.repo ?? '').trim()
    const name = String(form.name)
    if (!repo || !this._store) return
    await this.saveProjectSettings()
    this.setView('overview')
    await this.runImport(repo, name)
  }

  /** Save the settings panel as one `project` event; `extra` rides along (retire uses it). */
  private async saveProjectSettings(
    extra: Record<string, string> = {}
  ): Promise<void> {
    const store = this._store
    const form = this.s.projectForm as unknown as ProjectForm
    const name = String(form.name)
    if (!store || !name) return
    const settings: Record<string, string> = {}
    for (const { key } of PROJECT_FIELDS)
      settings[key] = String(form[key] ?? '').trim()
    this.closeProjectSettings()
    await store.setProject(name, { ...settings, ...extra })
  }

  /**
   * Make the open project public or private; edits in the panel save with
   * it. Going public asks first: anyone can then see the tile (owner,
   * 2026-10-14). Going private does not — it only hides.
   */
  private async togglePublicProject(): Promise<void> {
    const store = this._store
    const name = String((this.s.projectForm as unknown as ProjectForm).name)
    if (!store || !name) return
    const going = !isPublic(store.project(name))
    if (
      going &&
      !(await TosiVirta.confirm(
        `Anyone will see ${name}'s tile, signed in or not: its name, icon, description, links, version, counts, last activity and its latest showcases (their words and pictures). Nothing else of its tasks. Make it public?`,
        'Make public'
      ))
    )
      return
    await this.saveProjectSettings({ public: going ? 'yes' : '' })
  }

  /**
   * Retire or unretire the project whose settings are open: it leaves or
   * rejoins the scope menu and the head of the overview; the history stays.
   * Edits made in the panel save with it — one event, nothing dropped.
   */
  private async retireProject(): Promise<void> {
    const store = this._store
    const name = String((this.s.projectForm as unknown as ProjectForm).name)
    if (!store || !name) return
    await this.saveProjectSettings({
      retired: isRetired(store.project(name)) ? '' : 'yes',
    })
  }

  private sortKey(): SortKey {
    return this.sort in SORT_LABELS ? (this.sort as SortKey) : 'priority'
  }

  private setSort(value: string): void {
    if (value === this.appliedSort && value === this.sort) return
    this.appliedSort = value
    if (this.sort !== value) this.sort = value
    this.refresh()
  }

  /** The view state that a URL or an attribute set can carry. */
  private static readonly HASH_KEYS = [
    'view',
    'axis',
    'scope',
    'filter',
    'sort',
    'hide',
    'show',
    'group',
  ] as const
  private static readonly HASH_DEFAULTS: Record<string, string> = {
    view: 'board',
    axis: 'project',
    scope: '',
    filter: '',
    sort: 'priority',
    hide: HIDDEN_BY_DEFAULT.join(' '),
    show: '',
    group: 'type',
  }

  /**
   * Apply hash values to the attributes. On first load only keys present in
   * the URL apply (an empty hash leaves the attributes alone); on a later
   * change a missing key means its default, so the back button restores.
   */
  private readHash(
    values: Record<string, string>,
    { onlyPresent = false } = {}
  ): void {
    // whether the URL names only the card, read from what it carried —
    // before the implied view below is added (board #3304; review 2026-10-16)
    const onlyCard = !TosiVirta.HASH_KEYS.some(
      (k) => values[k] !== undefined && values[k] !== ''
    )
    // a URL that names a project, a filter or a card but no view means the
    // board (every link written before the overview was home, the brief's
    // included); one that names nothing lands on the page's home view
    if (values.view === undefined) {
      const implied = this.impliedView(values)
      if (!onlyPresent || implied !== this.homeView)
        values = { ...values, view: implied }
    }
    for (const key of TosiVirta.HASH_KEYS) {
      if (onlyPresent && values[key] === undefined) continue
      const next = values[key] ?? TosiVirta.HASH_DEFAULTS[key]!
      if (this[key] !== next) {
        this[key] = next
        if (key === 'hide') this.appliedHide = next
        if (key === 'show') this.appliedShow = next
        if (key === 'view') this.appliedView = next
        if (key === 'axis') this.appliedAxis = next
        if (key === 'scope') this.appliedScope = next
        if (key === 'axis' || key === 'scope') this.syncAxisControls()
        if (key === 'sort') {
          this.appliedSort = next
          if (this.hydrated)
            (this.parts.sort as TosiSelect).value = this.sortKey()
        }
        if (key === 'filter' && this.hydrated) this.applyFilterAttribute()
        if (key === 'group' && this.hydrated)
          (this.parts.libraryGroup as TosiSelect).value = next
      }
    }
    // the open card: `task=<num>`; applied once the store holds it
    if (!onlyPresent || values.task !== undefined) {
      const num = Number(values.task ?? '')
      this.pendingTask = Number.isInteger(num) && num > 0 ? num : 0
      // a URL that names only the card goes to where it lives; one that
      // also says where to look is obeyed (board #3304)
      this.pendingJump = onlyCard
      this.openPendingTask()
    }
  }

  /** The view a URL with no `view` key shows: the page's own (its `view` attribute) when it names nothing else. */
  private homeView = 'board'

  private impliedView(values: Record<string, string | undefined>): string {
    const names =
      !!values.scope ||
      !!values.filter ||
      !!values.task ||
      (!!values.axis && values.axis !== TosiVirta.HASH_DEFAULTS.axis)
    return names ? 'board' : this.homeView
  }

  private pendingTask = 0
  /** whether the URL named only the card, so opening it may change the view */
  private pendingJump = false
  private writingHash = false

  /** Open the card the hash names, or close the card if the hash names none. */
  // ── approving a login (tosijs-platform#34) ─────────────────────────────

  /** the host's client, with this board's credential: for what isn't the log */
  hostClient: Pick<ServiceClient, 'request' | 'host'> | null = null
  /** a login request the URL asks this person to approve (`#?virta.approve=<id>`) */
  private pendingApproval = ''

  /**
   * Approve (or deny) a CLI or agent login from the board (owner,
   * 2026-10-01: the platform's approval page couldn't sign in on a phone;
   * the board already has). The request's details come from the platform's
   * own approval page, so the person sees what they're granting — the
   * label, roles, scope, expiry — before they choose. Only a signed-in
   * person can approve: the platform refuses a token.
   */
  private async offerApproval(): Promise<void> {
    const id = this.pendingApproval
    const client = this.hostClient
    if (!id || !client || !this.session) return
    this.pendingApproval = ''
    if (!/^[A-Za-z0-9]{6,64}$/.test(id)) return
    let details: { label: string; facts: [string, string][] } | null = null
    try {
      const res = await fetch(`${client.host}/authorize?request=${id}`)
      details = parseConsentPage(await res.text())
    } catch {
      details = null
    }
    if (!details) {
      this.complain(
        `no login request ${id} to approve (expired, or already answered)`
      )
      return
    }
    const facts = details.facts.map(([k, v]) => `${k}: ${v}`).join('\n')
    // a request lives 10 minutes: say how long is left, first
    const expires = details.facts.find(([k]) => /expires/i.test(k))?.[1] ?? ''
    const left = Date.parse(expires) - Date.now()
    if (Number.isFinite(left) && left <= 0) {
      this.complain(
        'that login request has expired (they last 10 minutes): run the login again for a new link'
      )
      return
    }
    const approve = await TosiVirta.confirm(
      `Approve this login?${
        Number.isFinite(left)
          ? ` (expires in ${Math.max(1, Math.round(left / 60000))} min)`
          : ''
      }\n\n${
        details.label
      }\n${facts}\n\nOnly approve a login you or your own agent started.`,
      'Approve'
    )
    try {
      const r = await client.request('POST', '/authorize', {
        query: { action: 'approve' },
        body: { requestId: id, approve },
      })
      const status = (r.json as { status?: string } | null)?.status ?? ''
      notify({
        message: approve
          ? status === 'approved'
            ? `approved: ${details.label} — the login will finish on its own`
            : `not approved: ${status || 'refused'}`
          : 'denied',
        type: approve && status === 'approved' ? 'success' : 'info',
        duration: 8,
      })
    } catch (e) {
      // the host's reason, in words: a bare 400 tells nobody what to do
      const text = e instanceof Error ? e.message : String(e)
      this.complain(
        /expired/i.test(text)
          ? 'that login request expired before it was approved (they last 10 minutes): run the login again for a new link'
          : /used|answered|approved|denied/i.test(text)
          ? 'that login request was already answered: run the login again if it still needs one'
          : e
      )
    }
  }

  private openPendingTask(): void {
    if (!this.hydrated || !this._store) return
    const wanted = this.pendingTask
    const current = this._store.get(String(this.s.selectedId))
    if (wanted === 0) {
      if (current) this.select('')
      return
    }
    if (current?.num === wanted) {
      this.pendingTask = 0
      return
    }
    const task = this._store.byNum(wanted)
    if (task) {
      this.pendingTask = 0
      if (this.pendingJump) this.goTo(task)
      else this.select(task.id)
    }
  }

  /** Mirror the view state into the hash; defaults are written as absent. */
  private writeHash(): void {
    if (!this.hashSync) return
    const patch: Record<string, string | undefined> = {}
    for (const key of TosiVirta.HASH_KEYS) {
      const value = String(this[key] ?? '')
      patch[key] = value === TosiVirta.HASH_DEFAULTS[key] ? undefined : value
    }
    const open = this._store?.get(String(this.s.selectedId))
    // a card the URL named but the store cannot resolve yet stays in the URL
    patch.task = open
      ? String(open.num)
      : this.pendingTask
      ? String(this.pendingTask)
      : undefined
    // the view is written only when the rest of the URL doesn't imply it
    patch.view =
      this.view === this.impliedView(patch) ? undefined : String(this.view)
    const current = this.hashSync.values
    const keys = [...TosiVirta.HASH_KEYS, 'task']
    if (keys.some((k) => (current[k] ?? undefined) !== patch[k])) {
      // our own write notifies the observer too; that echo must not read back
      this.writingHash = true
      try {
        this.hashSync.update(patch)
      } finally {
        this.writingHash = false
      }
    }
  }

  /**
   * The slice menu: project › (all projects | each project | no project),
   * status › each status, owner › each status. Every leaf carries its mode's
   * icon (a project, its own), so the button — which shows the chosen
   * leaf's icon and caption — reads `⚑ ready` or `👤 ready` with no prefix.
   */
  private renderSlice(
    select: TosiSelect,
    choices: { project: string[]; status: string[] }
  ): void {
    const { span } = elements
    const store = this._store
    type Leaf = { caption: string; value: string; icon: string | HTMLElement }
    const projectLeaf = (v: string): Leaf => {
      if (v === NONE_VALUE)
        return {
          caption: EMPTY_LANE_NAMES.project!,
          value: sliceValue('project', v),
          icon: AXIS_ICONS.project,
        }
      // the select's popped menu lives in the document, outside this
      // board's styles: the icon slot is sized there
      useDocumentStyles()
      const iconEl = span({ class: 'virta-option-icon' })
      // projects without an icon get their initials, like the tiles do
      const p = store ? store.project(v) : undefined
      renderIcon(iconEl, p ? p.icon || initialsOf(p.name) : '')
      return { caption: v, value: sliceValue('project', v), icon: iconEl }
    }
    const statusLeaves = (axis: 'status' | 'owner') => {
      const leaves: (Leaf | null)[] = []
      for (const v of choices.status) {
        leaves.push({
          caption: v === NONE_VALUE ? EMPTY_LANE_NAMES.status! : v,
          value: sliceValue(axis, v),
          icon: AXIS_ICONS[axis],
        })
        // the implicit status (the inbox) sits apart, first
        if (v === NONE_VALUE) leaves.push(null)
      }
      return leaves
    }
    const projects: (Leaf | null)[] = [
      {
        caption: 'all projects',
        value: sliceValue('project', ''),
        icon: 'layers',
      },
      null,
    ]
    for (const v of choices.project) {
      // the implicit project sits apart, last
      if (v === NONE_VALUE) projects.push(null)
      projects.push(projectLeaf(v))
    }
    select.options = [
      { caption: 'project', icon: AXIS_ICONS.project, options: projects },
      {
        caption: 'status',
        icon: AXIS_ICONS.status,
        options: statusLeaves('status'),
      },
      {
        caption: 'owner',
        icon: AXIS_ICONS.owner,
        options: statusLeaves('owner'),
      },
    ]
    const wanted = sliceValue(this.axis, this.scope)
    if (select.value !== wanted) select.value = wanted
  }

  /** The + on a lane: a task in the selected project, in that lane, opened with its title selected. */
  private async addTask(laneValue: string): Promise<void> {
    const store = this._store
    if (!store) return
    const laneTag = this.laneTagValue(laneValue)
    const tags: string[] = []
    const library = this.view === 'library'
    // the library's + needs only its column's type; a project scope, if any
    if (!this.scope && !library) return
    // `none` is implicit: a task in "no project" (or the inbox) simply lacks the tag
    if (this.scope && this.scope !== NONE_VALUE) {
      const key = scopeKeyFor(this.axis)
      // in the library a status scope means nothing: only a project carries
      if (!library || key === 'project') tags.push(`${key}:${this.scope}`)
    }
    if (laneTag !== null) tags.push(`${this.lane}:${laneTag}`)
    // the library holds no tasks: grouped by topic, rung or mode, a new
    // item is a practice until someone says otherwise
    if (library && !tags.some((t) => parseTag(t).key === TYPE_KEY))
      tags.push(`${TYPE_KEY}:practice`)
    // a task made in a filtered view matches the filter where it can, so it
    // doesn't vanish on Create (board #2438): the filter's positive tags,
    // for keys the scope and lane haven't already set
    const set = new Set(tags.map((t) => parseTag(t).key))
    for (const term of parseFilter(String(this.s.filter ?? '')).terms) {
      if (term.negate || set.has(term.key) || term.key === ARCHIVED_TAG)
        continue
      if (term.value === NONE_VALUE) continue
      if (term.key === 'status' && term.value === INBOX_STATUS) continue
      tags.push(term.value === null ? term.key : `${term.key}:${term.value}`)
      set.add(term.key)
    }
    this.openDraft(tags)
  }

  /**
   * The lane's +: an unsaved draft, not a placeholder task. It is only a
   * title and a description until **Create** (owner, 2026-09-26; board
   * #1075): leaving a field writes nothing, so a typo never becomes a
   * numbered task. **Discard** drops it; closing a draft that says something
   * asks first. A task must still say something (`EmptyTaskError`).
   */
  private openDraft(tags: string[]): void {
    if (String(this.s.selectedId)) this.select('')
    this.draftTags = tags
    this.s.title = ''
    this.s.bodyDraft = ''
    ;(this.parts.cardWindow as HTMLElement).classList.add('draft')
    this.showBodyField('', false)
    this.renderPickers()
    this.syncPlacement(tags)
    ;(this.parts.panel as HTMLElement).hidden = false
    ;(this.parts.titleInput as HTMLInputElement).focus()
  }

  /** A draft's Create is writing: a second click must not file it twice. */
  private creating = false

  /** Leave draft mode; what was typed is gone unless Create took it. */
  private endDraft(): void {
    this.draftTags = null
    ;(this.parts.cardWindow as HTMLElement).classList.remove('draft')
  }

  /** Close the draft without writing anything. */
  private discardDraft(): void {
    this.endDraft()
    ;(this.parts.panel as HTMLElement).hidden = true
    this.focus()
  }

  /** Whether the draft says anything: a title or a description. */
  private draftHasContent(): boolean {
    return (
      !!String(this.s.title ?? '').trim() ||
      !!String(this.s.bodyDraft ?? '').trim()
    )
  }

  /** Create: the draft becomes a task (its number, its record), shown as the full card. */
  private async createDraft(): Promise<void> {
    const store = this._store
    const tags = this.draftTags
    if (!store || !tags) return
    const title = String(this.s.title ?? '').trim()
    const body = String(this.s.bodyDraft ?? '')
    if (!title && !body.trim()) {
      this.complain('a task needs a title or a description')
      ;(this.parts.titleInput as HTMLInputElement).focus()
      return
    }
    // still a draft until the task exists (board #3463): ending it first
    // showed the last card's transcript and attachments for the whole
    // round-trip. The draft ends and the new card fills in one tick; a
    // failed write leaves the draft, with what was typed
    if (this.creating) return
    this.creating = true
    try {
      const task = await store.create({
        title,
        body: body.trim() ? body : '',
        tags,
      })
      this.endDraft()
      this.select(task.id)
    } catch (e) {
      this.complain(e)
    } finally {
      this.creating = false
    }
  }

  /**
   * Leaving a draft that says something (Escape, ×, the backdrop, another
   * card): ask before its text is gone. Resolves whether it was dropped.
   */
  private async leaveDraft(): Promise<boolean> {
    if (!this.draftHasContent()) {
      this.discardDraft()
      return true
    }
    const ok = await TosiVirta.confirm(
      'Discard this draft? It has not been created, so nothing will be kept.',
      'Discard draft'
    )
    if (ok) this.discardDraft()
    return ok
  }

  private async titleEdited(): Promise<void> {
    // a draft writes nothing until Create (#1075)
    if (this.draftTags) return
    const store = this._store
    const id = String(this.s.selectedId)
    const task = store?.get(id)
    const title = String(this.s.title).trim()
    if (!store || !task || !title || title === task.title) return
    await store.retitle(id, title)
  }

  /** The description as a field: `text` in it, focused if asked. */
  private showBodyField(text: string, focus: boolean): void {
    const input = this.parts.bodyInput as HTMLTextAreaElement
    this.s.bodyDraft = text
    input.value = text
    input.hidden = false
    ;(this.parts.body as HTMLElement).hidden = true
    fitTextarea(input)
    if (focus) input.focus()
  }

  /**
   * The description as the task has it: rendered to read, or — with none —
   * the empty field, ready to type in. Never while the field has focus:
   * a refresh from the host must not overwrite what is being typed.
   */
  private syncBody(task: Task): void {
    const input = this.parts.bodyInput as HTMLTextAreaElement
    if (document.activeElement === input) return
    if (!task.body.trim()) {
      this.showBodyField('', false)
      return
    }
    const body = this.parts.body as HTMLElement
    if (this.renderedBody !== task.body) {
      this.renderedBody = task.body
      renderMarkdown(task.body, body)
    }
    input.hidden = true
    body.hidden = false
  }

  /** A tap on the rendered description edits it in place (a link still goes where it points). */
  private bodyClicked(e: Event): void {
    if ((e.target as Element).closest('a')) return
    const task = this._store?.get(String(this.s.selectedId))
    if (task) this.showBodyField(task.body, true)
  }

  /** The field was left: save what changed, then show it rendered (a draft keeps its text until Create). */
  private async bodyCommitted(): Promise<void> {
    if (this.draftTags) return
    const store = this._store
    const id = String(this.s.selectedId)
    const task = store?.get(id)
    if (!store || !task) return
    const body = String(this.s.bodyDraft ?? '')
    if (body !== task.body) {
      try {
        await store.edit(id, body)
      } catch (e) {
        this.complain(e)
        return
      }
    }
    ;(this.parts.bodyInput as HTMLTextAreaElement).blur()
    this.syncBody(store.get(id) ?? task)
  }

  /** Escape in the field: back to what the task has, nothing written (in a draft, Escape is Discard). */
  private revertBody(): void {
    if (this.draftTags) {
      this.discardDraft()
      return
    }
    const task = this._store?.get(String(this.s.selectedId))
    const input = this.parts.bodyInput as HTMLTextAreaElement
    this.s.bodyDraft = task?.body ?? ''
    input.value = task?.body ?? ''
    input.blur()
    if (this.draftTags) return
    if (task) this.syncBody(task)
  }

  /** The lane for tasks without the lane tag: inbox for status, "no project" otherwise. */
  private emptyLaneName(): string {
    return EMPTY_LANE_NAMES[this.lane] ?? `no ${this.lane}`
  }

  private hiddenLanes(): string[] {
    return String(this.hide ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .map((v) => (v === '(inbox)' ? '' : v))
  }

  /**
   * The show toggles, as the owner specified them (#765): on a project board
   * **own** (the project's own tasks), **related** (tasks another project
   * owns that are upstream, downstream or blocking this one) and
   * **archived**. Own and related start on and can't both be off. In the
   * status and owner views it is just **archived**. Own and related live in
   * the \`show\` attribute (\`no-own\`, \`no-others\`), and archived is the
   * \`archived:any\` filter term, so the ▾ menu and the URL agree.
   */
  private syncShowToggles(onProject: boolean): void {
    const toggles = this.parts.showToggles as TosiSegmented
    const choices = [
      ...(onProject
        ? [
            { value: 'own', caption: 'own' },
            { value: 'related', caption: 'related' },
          ]
        : []),
      { value: 'archived', caption: 'archived' },
    ]
    const key = JSON.stringify(choices)
    if (this.showChoicesKey !== key) {
      this.showChoicesKey = key
      toggles.choices = choices
    }
    const flags = this.showFlags()
    const terms = (this.parts.filterTags as TosiTagList).tags
    const on = [
      ...(onProject && !flags.has('no-own') ? ['own'] : []),
      ...(onProject && !flags.has('no-others') ? ['related'] : []),
      ...(terms.includes('archived:any') ? ['archived'] : []),
    ].join(',')
    if (String(toggles.value ?? '') !== on) {
      this.syncingShow = true
      toggles.value = on
      this.syncingShow = false
    }
  }

  private showToggled(): void {
    if (this.syncingShow) return
    const toggles = this.parts.showToggles as TosiSegmented
    const on = new Set(
      String(toggles.value ?? '')
        .split(',')
        .filter(Boolean)
    )
    const onProject = toggles.choices.some((c) => c.value === 'own')
    // own and related can't both be off: the one just switched off comes back
    if (onProject && !on.has('own') && !on.has('related')) {
      this.syncShowToggles(true)
      return
    }
    // archived: the filter term, so the URL and the ▾ menu agree
    const tagList = this.parts.filterTags as TosiTagList
    const has = tagList.tags.includes('archived:any')
    if (on.has('archived') !== has) {
      tagList.tags = on.has('archived')
        ? [...tagList.tags.filter((t) => t !== 'archived'), 'archived:any']
        : tagList.tags.filter((t) => t !== 'archived:any')
      this.composeFilter()
    }
    if (!onProject) return
    const flags = this.showFlags()
    if (on.has('own') === flags.has('no-own')) this.setShowFlag('no-own')
    if (on.has('related') === flags.has('no-others'))
      this.setShowFlag('no-others')
  }

  /**
   * Archive every card an ending lane shows (#766), once the reader has
   * confirmed it: the owner's spec is a real confirmation, not a toast.
   */
  private async archiveLane(laneId: string): Promise<void> {
    const store = this._store
    const ids = (this.laneTasks.get(laneId) ?? [])
      .filter((t) => !t.tags.includes(ARCHIVED_TAG))
      .map((t) => t.id)
    if (!store || !ids.length) return
    const n = `${ids.length} task${ids.length === 1 ? '' : 's'}`
    const ok = await TosiVirta.confirm(
      `This will hide ${n}. Are you sure? ("show: archived" brings them back.)`,
      'Archive all'
    )
    if (!ok) return
    this.attempt(store.archive(ids))
  }

  /**
   * Whether lanes build only the cards in view (a virtual list). On in a
   * browser; a test DOM with no layout (happy-dom) sees nothing in view, so
   * its tests turn it off and one test turns it back on.
   */
  static virtualLanes = true

  /** How the board asks "are you sure?": TosiDialog.confirm, replaceable in tests. */
  static confirm: (message: string, title?: string) => Promise<boolean> = (
    message,
    title
  ) => TosiDialog.confirm(message, title)

  /** The toggle row: one segment per lane value, on = shown. Choices follow the lanes. */
  private syncLaneToggles(values: string[], hidden: Set<string>): void {
    const toggles = this.parts.laneToggles as TosiSegmented
    const ordered = orderLanes(values)
    const choices = ordered.map((v) => ({
      value: v === '' ? '(inbox)' : v,
      caption: v === '' ? this.emptyLaneName() : v,
    }))
    const key = JSON.stringify(choices)
    if (this.toggleChoicesKey !== key) {
      this.toggleChoicesKey = key
      toggles.choices = choices
    }
    const shown = ordered
      .filter((v) => !hidden.has(v))
      .map((v) => (v === '' ? '(inbox)' : v))
    const value = shown.join(',')
    if (toggles.value !== value) {
      this.syncingToggles = true
      toggles.value = value
      this.syncingToggles = false
    }
  }

  /** A toggle changed: hide = every lane not switched on. */
  private lanesToggled(): void {
    if (this.syncingToggles) return
    const toggles = this.parts.laneToggles as TosiSegmented
    const on = new Set(
      String(toggles.value ?? '')
        .split(',')
        .filter(Boolean)
        .map((v) => (v === '(inbox)' ? '' : v))
    )
    const all = toggles.choices.map((c) =>
      c.value === '(inbox)' ? '' : c.value
    )
    // hidden values that belong to the other axis (status lanes while by
    // project, and vice versa) are kept, so flipping back restores them
    const kept = this.hiddenLanes().filter((v) => !all.includes(v))
    const hidden = [...all.filter((v) => !on.has(v)), ...kept]
    const text = hidden.map((v) => (v === '' ? '(inbox)' : v)).join(' ')
    this.appliedHide = text
    if (text !== this.hide) this.hide = text
    this.rememberHide()
    this.refresh()
  }

  /**
   * Which lanes a slice shows is remembered per slice, in this browser
   * (board #160): a lane toggled off by project stays off the next time that
   * project is picked, or the board is opened on it. Only a board over
   * durable data (a host, or a `db`) remembers; a demo board does not.
   */
  private hideMemoryKey(): string {
    const where = this.host || (this.db ? `db:${this.db}` : '')
    return where
      ? `virta:hide:${where}:${sliceValue(this.axis, this.scope)}`
      : ''
  }

  private rememberHide(): void {
    const key = this.hideMemoryKey()
    if (!key) return
    try {
      localStorage.setItem(key, String(this.hide ?? ''))
    } catch {
      /* no storage: the choice lasts this page's life */
    }
  }

  /** A slice picked (a menu, a tile, a load): the lanes it last showed, if it was ever toggled. */
  private recallHide(): void {
    const key = this.hideMemoryKey()
    if (!key) return
    let saved: string | null = null
    try {
      saved = localStorage.getItem(key)
    } catch {
      return
    }
    if (saved === null || saved === String(this.hide ?? '')) return
    this.appliedHide = saved
    this.hide = saved
  }

  private buildLanes(values: string[], ids: string[]): void {
    const { div, h3, span, template, button } = elements
    const board = this.parts.board as HTMLElement
    const key = this.key
    board.replaceChildren(
      ...values.map((value, i) => {
        const id = ids[i]!
        return div(
          {
            class: 'lane',
            dataDrop: `${TASK_MIME};text/markdown;text/plain;text/html;Files`,
            onDrop: (e: DragEvent) => {
              this.handleDrop(e, value)
              if (e.defaultPrevented) this.endDrag()
            },
            apply: (el: Element) => {
              ;(el as HTMLElement).dataset.lane = id
            },
          },
          h3(
            span({
              class: 'swatch',
              style: {
                background: colorFor(value === '' ? 'inbox' : value).bg,
              },
            }),
            span(
              { class: 'name' },
              value === '' ? this.emptyLaneName() : value
            ),
            span({ class: 'lane-count' }),
            // done work piles up: sweep the lane out of sight in one go
            // (#766) — reversible, "show archived" brings it back
            ...(this.lane === 'status' &&
            (FINISHED_STATUSES as readonly string[]).includes(value)
              ? [
                  button(
                    {
                      class: 'archive-all',
                      title: 'archive every card in this lane',
                      onClick: (e: Event) => {
                        e.stopPropagation()
                        void this.archiveLane(id)
                      },
                    },
                    'archive all'
                  ),
                ]
              : []),
            button(
              {
                class: 'add',
                title: 'add a task here',
                onClick: (e: Event) => {
                  e.stopPropagation()
                  this.attempt(this.addTask(value))
                },
              },
              '+'
            )
          ),
          div(
            {
              class: 'cards',
              // only the cards in view are built: a lane of hundreds (every
              // project's backlog) draws a screenful, at natural heights
              // (owner, 2026-10-03: the all-projects board was slow)
              bindList: {
                value: `${key}.byLane.${id}`,
                idPath: 'id',
                ...(TosiVirta.virtualLanes
                  ? {
                      virtual: {
                        height: CARD_HEIGHT_ESTIMATE,
                        minHeight: CARD_MIN_HEIGHT,
                      },
                    }
                  : {}),
              },
            },
            template(
              div(
                {
                  class: 'card',
                  draggable: 'true',
                  dataDrag: `${TASK_MIME};text/markdown;text/plain`,
                  bind: [
                    {
                      value: '^.id',
                      binding: (el: Element, v: string) =>
                        this.stampCard(el, v),
                    },
                    {
                      // on a project's board, a task another project owns
                      // (it blocks or delays this one) is marked as theirs
                      value: '^.tags',
                      binding: (el: Element, v: string[]) =>
                        this.markElsewhere(el, v),
                    },
                  ],
                },
                span({
                  class: 'num',
                  bind: {
                    value: '^.num',
                    binding: (el: Element, v: number) =>
                      (el.textContent = `#${v}`),
                  },
                }),
                // a parent's progress, 3/7 (board #755): its tags change
                // whenever a part moves it, so that is when this redraws
                span({
                  class: 'parts',
                  hidden: true,
                  bind: {
                    value: '^.tags',
                    binding: (el: Element) => this.renderProgress(el),
                  },
                }),
                span({
                  class: 'title',
                  // a task may be only a description: its first line stands in
                  bind: [
                    {
                      value: '^.title',
                      binding: (el: Element, v: string) =>
                        renderCardTitle(el, 'title', v),
                    },
                    {
                      value: '^.body',
                      binding: (el: Element, v: string) =>
                        renderCardTitle(el, 'body', v),
                    },
                  ],
                }),
                div({
                  class: 'tags',
                  bind: {
                    value: '^.tags',
                    binding: (el: Element, v: string[]) =>
                      this.renderTags(el, v),
                  },
                }),
                div({
                  class: 'workers',
                  bind: {
                    value: '^.tags',
                    binding: (el: Element, v: string[]) =>
                      this.renderWorkers(el, v),
                  },
                })
              )
            )
          )
        )
      })
    )
  }

  private updateLaneCounts(
    values: string[],
    ids: string[],
    lanes: Map<string, Task[]>
  ): void {
    const board = this.parts.board as HTMLElement
    for (let i = 0; i < values.length; i++) {
      const lane = board.querySelector(`[data-lane="${ids[i]}"] .lane-count`)
      if (lane) {
        const tasks = lanes.get(values[i]!)!
        const { points, sized } = sizePoints(tasks)
        // teams that size show the lane's points beside its count
        lane.textContent = sized
          ? `${tasks.length} · ${points} pts`
          : String(tasks.length)
      }
    }
  }

  /** Mark a card whose task another project owns (see `refresh`'s related tasks). */
  private markElsewhere(el: Element, tags: string[] | string): void {
    const list = Array.isArray(tags) ? tags : String(tags ?? '').split(' ')
    const scope = this.scope
    const owners =
      scopeKeyFor(this.axis) === 'project' && scope && scope !== NONE_VALUE
        ? tagValues(list, 'project')
        : []
    const elsewhere = owners.length > 0 && !owners.includes(scope)
    el.classList.toggle('elsewhere', elsewhere)
    if (elsewhere) (el as HTMLElement).dataset.owner = owners.join(', ')
    else delete (el as HTMLElement).dataset.owner
  }

  /** A parent card's progress chip: done of counting parts; hidden on a task with no parts. */
  private renderProgress(el: Element): void {
    const chip = el as HTMLElement
    const id = (chip.closest('.card') as HTMLElement | null)?.dataset.id
    const task = id ? this._store?.get(id) : undefined
    const pr = task && this._store ? progressOf(this._store.tasks, task) : null
    chip.hidden = !pr || pr.parts === 0
    if (!pr || chip.hidden) return
    chip.textContent = `${pr.done}/${pr.total}`
    chip.title = `${pr.done} of ${pr.total} parts done; its status follows them`
  }

  /**
   * The card window's family (board #755): what this task is part of, and
   * its own parts with their status, each one openable.
   */
  private renderFamily(task: Task): void {
    const panel = this.parts.familyPanel as HTMLElement
    const store = this._store
    if (!store) return
    const above = parentNumOf(task.tags)
    const up = above === null ? undefined : store.byNum(above)
    const kids = childrenOf(store.tasks, task)
    const key = JSON.stringify([
      task.id,
      up?.num,
      up?.title,
      kids.map((k) => [k.num, k.title, statusValue(k.tags)]),
    ])
    if (panel.dataset.key === key) return
    panel.dataset.key = key
    panel.textContent = ''
    panel.hidden = !up && !kids.length
    if (panel.hidden) return
    const { div, a, span, strong } = elements
    const link = (t: Task, text: string) =>
      a(
        {
          href: '#',
          class: 'lineage-link',
          onClick: (e: Event) => {
            e.preventDefault()
            this.select(t.id)
          },
        },
        text
      )
    if (up)
      panel.append(div(strong('part of '), link(up, `#${up.num} ${up.title}`)))
    if (kids.length) {
      const pr = progressOf(store.tasks, task)
      panel.append(
        div(
          strong(`parts: ${pr.done}/${pr.total} done`),
          span({ class: 'family-note' }, ' — its status follows them')
        ),
        ...kids.map((k) =>
          div(
            { class: 'family-part' },
            span({ class: 'family-status' }, statusValue(k.tags) || 'inbox'),
            link(k, `#${k.num} ${k.title}`)
          )
        )
      )
    }
  }

  private stampCard(el: Element, id: string): void {
    ;(el as HTMLElement).dataset.id = id
    el.classList.toggle('selected', id === String(this.s.selectedId))
  }

  /** Chips: the value alone, colored by key (full tag in the tooltip); loud ones first. */
  private renderTags(el: Element, tags: string[] | string): void {
    const { span } = elements
    const list = Array.isArray(tags)
      ? tags
      : tags
      ? String(tags).split(' ')
      : []
    const shown = list.filter(
      (t) => !t.startsWith('working:') && !t.startsWith(`${this.lane}:`)
    )
    // a value under two keys on one card ('source:github', 'authority:github')
    // is ambiguous alone, so those chips carry the full tag
    const valueCount = new Map<string, number>()
    for (const t of shown) {
      const v = parseTag(t).value ?? t
      valueCount.set(v, (valueCount.get(v) ?? 0) + 1)
    }
    const chips = shown
      .map((t) => {
        const c = chipStyle(t)
        const { key, value: v } = parseTag(t)
        // a relationship chip already says its key ("blocks alpha")
        if (
          v !== null &&
          (valueCount.get(v) ?? 0) > 1 &&
          !RELATION_KEYS.includes(key)
        )
          c.text = t
        return c
      })
      .sort((a, b) => Number(b.loud) - Number(a.loud))
    el.replaceChildren(
      ...chips.map((c) =>
        span(
          {
            class: c.loud ? 'tag loud' : 'tag',
            title: c.title,
            style: { background: c.bg, color: c.ink },
          },
          c.text
        )
      )
    )
  }

  /** One jiggling avatar per identity working on the task. */
  private renderWorkers(el: Element, tags: string[] | string): void {
    const { span } = elements
    const list = Array.isArray(tags)
      ? tags
      : tags
      ? String(tags).split(' ')
      : []
    const workers = workersOf(list)
    ;(el as HTMLElement).hidden = workers.length === 0
    el.replaceChildren(
      ...workers.map((who) =>
        span(
          {
            class: 'avatar',
            title: `${who} is working on this`,
            style: { background: colorFor(who).bg, color: colorFor(who).ink },
          },
          initialsOf(who)
        )
      )
    )
  }

  /** The editable tag list changed: diff against the task and write the difference. */
  private async tagsEdited(): Promise<void> {
    if (this.syncingTags) return
    const store = this._store
    const id = String(this.s.selectedId)
    const task = store?.get(id)
    if (!store || !task) return
    const wanted = (this.parts.tagList as TosiTagList).tags
      .map((t) => t.trim())
      .filter(Boolean)
    const current = task.tags.filter((t) => !t.startsWith('working:'))
    const added = wanted.filter((t) => !current.includes(t))
    const removed = current.filter((t) => !wanted.includes(t))
    if (removed.length) await store.untag(id, ...removed)
    if (added.length) await store.tag(id, ...added)
  }

  private renderChoices(select: TosiSelect, choices: string[]): void {
    const current = String(this.s.laneValue)
    // tosi-select shows nothing for '', so the empty lane gets a sentinel value
    const inbox = this.lane === 'status' ? INBOX_STATUS : INBOX_VALUE
    const options = [
      { caption: this.emptyLaneName(), value: inbox },
      null,
      ...choices
        .filter((v) => v !== INBOX_STATUS)
        .map((v) => ({ caption: v, value: v })),
    ]
    // only a real change replaces the options: a menu open under the
    // pointer survives every write that doesn't add a value (board #52)
    const key = JSON.stringify(options)
    if (this.renderedChoices.get(select) !== key) {
      this.renderedChoices.set(select, key)
      select.options = options
    }
    const wanted = current === '' ? inbox : current
    if (select.value !== wanted) select.value = wanted
  }

  // ── card view ────────────────────────────────────────────────────────────

  private selectFromEvent(event: Event): void {
    const card = (event.target as Element).closest(
      '.card'
    ) as HTMLElement | null
    if (card?.dataset.id) this.select(card.dataset.id)
  }

  select(id: string): void {
    // leaving a draft: an empty one just goes; one that says something asks
    // first, and stays open if the answer is no (#1075)
    if (this.draftTags) {
      void this.leaveDraft().then((dropped) => {
        if (dropped && id) this.select(id)
      })
      return
    }
    const previous = String(this.s.selectedId)
    this.s.selectedId = id
    this.expanded.clear()
    this.revealed.clear()
    const board = this.parts.board as HTMLElement
    for (const el of Array.from(board.querySelectorAll('.card'))) {
      el.classList.toggle('selected', (el as HTMLElement).dataset.id === id)
    }
    if (id) {
      this.showCard(id)
      ;(this.parts.panel as HTMLElement).hidden = false
    } else if (previous) {
      ;(this.parts.panel as HTMLElement).hidden = true
      this.focus()
    }
    this.writeHash()
  }

  private showCard(id: string): void {
    const store = this._store
    const task = store?.get(id)
    if (!store || !task) {
      this.select('')
      return
    }
    this.s.num = task.num
    this.s.title = task.title
    this.s.body = task.body
    this.s.tags = task.tags.join(' ')
    this.syncingTags = true
    try {
      const tagList = this.parts.tagList as TosiTagList
      const shown = task.tags.filter((t) => !t.startsWith('working:'))
      if (tagList.tags.join('\n') !== shown.join('\n')) tagList.tags = shown
      colorTagChips(tagList)
      const all = new Set<string>()
      for (const t of store.tasks)
        for (const tag of t.tags) if (!tag.startsWith('working:')) all.add(tag)
      tagList.availableTags = Array.from(all)
        .sort()
        // the menu shows `caption`, not `value` (tosijs-ui#189): without
        // it every item was a blank row (#150)
        .map((tag) => ({ value: tag, caption: tag, ...tagChipColors(tag) }))
    } finally {
      this.syncingTags = false
    }
    this.syncBody(task)
    this.s.closed = task.closedAt !== null
    this.syncPlacement(task.tags)
    this.renderPickers()
    this.renderShowcasePanel(task)
    this.renderFamily(task)
    this.renderSkillPanel(task)
    this.renderMeasurePanel(task)
    this.renderPracticePanel(task)
    this.renderConflicts(task)
    this.plainRows = this.rowsFor(store.transcript(id))
    this.s.rows = this.plainRows
  }

  private rowsFor(entries: TranscriptEntry[]): Row[] {
    const rows: Row[] = []
    const store = this._store
    const id = String(this.s.selectedId)
    // who took back which comment, honouring only authors (#759)
    const retracted = store ? retractions(store.events(id)) : new Map()
    for (const entry of entries) {
      if (entry.type === 'group' && !this.expanded.has(entry.id)) {
        rows.push({
          id: entry.id,
          type: 'group',
          kind: 'group',
          by: '',
          at: '',
          text: `${entry.label} ▸`,
          count: entry.events.length,
          state: '',
          action: '',
          media: '',
        })
      } else {
        const events: VirtaEvent[] =
          entry.type === 'group' ? entry.events : [entry.event]
        for (const event of events) {
          const r = retracted.get(event.id)
          const revealed = r && this.revealed.has(event.id)
          const mine =
            event.kind === 'commented' && store && event.by === store.identity
          rows.push({
            id: event.id,
            type: 'event',
            kind: event.kind,
            by: `${store ? store.whoWrote(event) : whoWrote(event)} · ${timeAgo(
              event.at
            )}`,
            at: event.at,
            // a retracted comment is collapsed until asked for; its text
            // stays readable, struck through, for a reply that needs it
            text:
              r && !revealed
                ? `comment retracted${r.reason ? ` (${r.reason})` : ''} — show`
                : describeEvent(event),
            count: 1,
            state: r ? (revealed ? 'revealed' : 'retracted') : '',
            action: mine ? (r ? 'undo' : 'retract') : '',
            media:
              event.kind === 'attached'
                ? JSON.stringify({
                    name: event.payload.name,
                    url: event.payload.url,
                    file: event.payload.file,
                  })
                : '',
          })
        }
      }
    }
    return rows
  }

  private toggleFromEvent(event: Event): void {
    const row = (event.target as Element).closest('.entry')
    const store = this._store
    const id = String(this.s.selectedId)
    if (!row || !store || !id) return
    const rowId = (row as HTMLElement).dataset.rowId ?? ''
    // your own comment: retract it, or undo that (#759)
    if ((event.target as Element).closest('.action')) {
      const undo =
        row.classList.contains('retracted') ||
        row.classList.contains('revealed')
      this.attempt(store.retract(id, { comment: rowId, undo }))
      return
    }
    // a retracted comment: show its text, or collapse it again
    if (
      row.classList.contains('retracted') ||
      row.classList.contains('revealed')
    ) {
      if (this.revealed.has(rowId)) this.revealed.delete(rowId)
      else this.revealed.add(rowId)
      this.plainRows = this.rowsFor(store.transcript(id))
      this.s.rows = this.plainRows
      return
    }
    if (!row.classList.contains('group')) return
    const target = this.plainRows.find((r) => r.id === rowId)
    if (target?.type === 'group') {
      this.expanded.add(target.id)
      this.plainRows = this.rowsFor(store.transcript(id))
      this.s.rows = this.plainRows
    }
  }

  private async sendComment(): Promise<void> {
    const store = this._store
    const id = String(this.s.selectedId)
    const text = String(this.s.newComment).trim()
    if (!store || !id || !text) return
    this.s.newComment = ''
    await store.comment(id, text)
  }

  // ── drag and drop ────────────────────────────────────────────────────────

  /**
   * A card drags as the task's markdown. `data-drag-content` is what
   * tosijs-ui's drag-and-drop puts on the dataTransfer for every declared
   * type; `DownloadURL` (Chrome) makes a drop on the desktop save a file.
   */
  private handleDragStart(event: DragEvent): void {
    const card = (event.target as Element).closest(
      '.card'
    ) as HTMLElement | null
    const task = card?.dataset.id
      ? this._store?.get(card.dataset.id)
      : undefined
    if (!card || !task) return
    this.dragging = task.id
    const md = taskToMarkdown(task)
    card.dataset.dragContent = md
    const dt = event.dataTransfer
    if (dt) {
      dt.effectAllowed = 'copyMove'
      try {
        dt.setData(
          'DownloadURL',
          `text/markdown:${taskFileName(
            task
          )}:data:text/markdown;charset=utf-8,${encodeURIComponent(md)}`
        )
      } catch {
        /* not every browser accepts the type */
      }
    }
  }

  /**
   * A drop on a lane. A card from a virta board (this one or another window)
   * moves there. Anything else that is text — a `.md`/`.txt`/`.html` file, a
   * dragged selection, a markdown document — becomes a new task in that lane;
   * markdown that carries a `virta:` id we already hold moves instead of
   * duplicating.
   */
  /**
   * A drop the board handled stops propagation, so tosijs-ui's body-level
   * `drop` never runs, and the moved card is re-rendered, so its `dragend`
   * never reaches the body either: the drop highlights (`.drag-target`,
   * `.drag-over`, `.drag-source`) would stay lit. Tell drag-and-drop the
   * drag is over, on the body where it listens (board #159).
   */
  private endDrag(): void {
    this.dragging = null
    document.body.dispatchEvent(new Event('dragend'))
  }

  private handleDrop(event: DragEvent, laneValue: string): void {
    const dt = event.dataTransfer
    if (!dt || !this._store) return
    const types = Array.from(dt.types)
    if (types.includes(TASK_MIME)) {
      // the library's columns are types: a drop doesn't change what a thing
      // is (owner, 2026-09-30) — relating by dropping on a card comes later
      if (this.view === 'library') return
      event.preventDefault()
      event.stopPropagation()
      const id = this.dragging ?? this.taskIdFromMarkdown(dt.getData(TASK_MIME))
      this.dragging = null
      if (id && this._store.get(id)) {
        if (this.lane === 'project') {
          const from = tagValues(this._store.get(id)!.tags, 'project')[0] ?? ''
          this.handOver(id, from, laneValue)
        } else this.moveTo(id, laneValue)
      }
      return
    }
    const files = Array.from(dt.files ?? []).filter(
      (f) =>
        /\.(md|markdown|txt|text|html?)$/i.test(f.name) ||
        /^text\//.test(f.type)
    )
    const text = types.includes('text/markdown')
      ? dt.getData('text/markdown')
      : types.includes('text/plain')
      ? dt.getData('text/plain')
      : types.includes('text/html')
      ? htmlToText(dt.getData('text/html'))
      : ''
    if (!files.length && !text.trim()) return
    event.preventDefault()
    event.stopPropagation()
    this.attempt(
      this.importDocuments(
        files.length
          ? files.map(async (f) => ({
              name: f.name,
              text: await f.text(),
            }))
          : [Promise.resolve({ name: '', text })],
        laneValue
      )
    )
  }

  /** Turn dropped documents into tasks (or moves) in a lane, in order. */
  private async importDocuments(
    docs: Promise<{ name: string; text: string }>[],
    laneValue: string
  ): Promise<void> {
    const store = this._store
    if (!store) return
    for (const doc of await Promise.all(docs)) {
      const source = /\.html?$/i.test(doc.name)
        ? htmlToText(doc.text)
        : doc.text
      const parsed = markdownToTask(source)
      if (!parsed.title) continue
      if (parsed.id && store.get(parsed.id)) {
        await store.setTag(parsed.id, this.lane, this.laneTagValue(laneValue))
        continue
      }
      const tags = parsed.tags.filter((t) => !t.startsWith(`${this.lane}:`))
      const laneTag = this.laneTagValue(laneValue)
      if (laneTag !== null) tags.push(`${this.lane}:${laneTag}`)
      if (doc.name) tags.push(`source:file`)
      const task = await store.create({
        title: parsed.title,
        body: parsed.body,
        tags,
      })
      this.select(task.id)
    }
  }

  private taskIdFromMarkdown(md: string): string | null {
    return md.match(/^---\n(?:.*\n)*?virta: (\S+)/)?.[1] ?? null
  }

  private async moveLane(value: string): Promise<void> {
    if (this.draftTags) {
      this.setDraftTag(this.lane, this.laneTagValue(value))
      return
    }
    const store = this._store
    const id = String(this.s.selectedId)
    if (!store || !id) return
    if (this.lane === 'project')
      this.moveProject(this.laneTagValue(value) ?? '')
    else this.moveTo(id, value)
  }

  /**
   * Move a task to another lane, shown at once (board #143): the card moves
   * between the lanes' lists now — a view-only change; the store and the log
   * are untouched until the host takes the write, which is replayed as ever.
   * The refresh after it draws the same board; after a failed write, it
   * puts the card back where the log says it is.
   */
  private moveTo(id: string, laneValue: string): void {
    const store = this._store
    if (!store) return
    // a parent doesn't drag its parts, and its status follows them: moving
    // one by hand offers to move its unfinished parts there (owner,
    // 2026-10-06), after which it follows
    const task = store.get(id)
    const parts =
      this.lane === 'status' && task ? store.unfinishedParts(id) : []
    if (task && parts.length) {
      const where = laneValue || this.emptyLaneName()
      void TosiVirta.confirm(
        `#${
          task.num
        } follows its parts, so it can't be moved on its own.\n\nMove its ${
          parts.length
        } unfinished part${parts.length === 1 ? '' : 's'} to ${where} too? #${
          task.num
        } will follow them there.`,
        `Move to ${where}`
      ).then((yes) => {
        if (!yes) return
        this.attempt(
          store.moveParts(id, laneValue).finally(() => this.refresh())
        )
      })
      return
    }
    this.showMoved(id, laneValue)
    this.attempt(
      store
        .setTag(id, this.lane, this.laneTagValue(laneValue))
        .finally(() => this.refresh())
    )
  }

  /** The card in its new lane, before the write lands (see `moveTo`). */
  private showMoved(id: string, laneValue: string): void {
    const to = safeId(laneValue)
    const target = this.laneTasks.get(to)
    if (!target) return
    let from = ''
    let task: Task | undefined
    for (const [laneId, list] of this.laneTasks) {
      task = list.find((t) => t.id === id)
      if (task) {
        from = laneId
        break
      }
    }
    if (!task || from === to) return
    const byLane = this.s.byLane as unknown as Record<string, unknown>
    const without = this.laneTasks.get(from)!.filter((t) => t.id !== id)
    const withIt = [task, ...target]
    this.laneTasks.set(from, without)
    this.laneTasks.set(to, withIt)
    byLane[from] = without
    byLane[to] = withIt
  }

  /**
   * The window's project select. Leaving a project asks what that project is
   * waiting on — blocks, delays, or nothing — then `store.move()` does the
   * tagging. The select snaps back until the move lands.
   */
  private moveProject(next: string): void {
    if (this.draftTags) {
      this.setDraftTag('project', next || null)
      return
    }
    const id = String(this.s.selectedId)
    if (!this._store || !id) return
    this.handOver(id, String(this.s.projectValue), next)
  }

  /**
   * Hand `id` from one project to another: the one it leaves stays
   * interested (`store.move`'s default), and no question is asked — delete
   * the `interested:` tag to end that, or change it to `blocks:` /
   * `delays:` when the old project is waiting on it (owner, 2026-09-25,
   * board #173). Leaving for no project just clears the tag.
   */
  private handOver(id: string, from: string, next: string): void {
    const store = this._store
    if (!store || from === next) return
    if (next === '') this.attempt(store.setTag(id, 'project', null))
    else this.attempt(store.move(id, next))
  }

  private renderProjectChoices(select: TosiSelect, choices: string[]): void {
    const current = String(this.s.projectValue)
    select.options = [
      { caption: 'no project', value: NO_PROJECT },
      null,
      ...choices.map((v) => ({ caption: v, value: v })),
    ]
    const wanted = current === '' ? NO_PROJECT : current
    if (select.value !== wanted) select.value = wanted
  }

  // ── practices (boards #2472, #2491) ──────────────────────────────────────

  /** A practice's (or policy's) measure on its card: shown with its version, set, or asked for. */
  /**
   * A skill's card (board #2477): which version the board serves, which
   * projects pinned or last ran which, and any edit that isn't served —
   * with why, since that is the owner's to act on.
   */
  private renderSkillPanel(task: Task): void {
    const panel = this.parts.skillPanel as HTMLElement
    const store = this._store
    const name = tagValues(task.tags, SKILL_KEY)[0]
    const isSkill =
      !!store && !!name && tagValues(task.tags, TYPE_KEY)[0] === SKILL_TYPE
    const { versions, ignored } = isSkill
      ? skillVersions(task, store.events(task.id), (e) => store.whoWrote(e))
      : { versions: [] as SkillVersion[], ignored: [] as IgnoredEdit[] }
    const key = JSON.stringify([
      task.id,
      isSkill,
      versions.length,
      ignored.length,
      tagValues(task.tags, PIN_KEY),
      tagValues(task.tags, RAN_KEY),
    ])
    if (panel.dataset.key === key) return
    panel.dataset.key = key
    panel.textContent = ''
    panel.hidden = !isSkill
    if (!isSkill) return
    const { div, span, strong, code } = elements
    const latest = versions.at(-1)
    const list = (label: string, values: string[]) =>
      values.length
        ? [div(strong(`${label}: `), values.join(', '))]
        : ([] as HTMLElement[])
    panel.append(
      latest
        ? div(
            strong(`serves v${latest.version}`),
            span(
              { class: 'family-note' },
              ` — ${latest.at.slice(0, 10)} by ${latest.by}${
                latest.note && latest.note !== 'first' ? `: ${latest.note}` : ''
              }`
            )
          )
        : '',
      ...versions
        .filter((v) => v.caution)
        .map((v) =>
          div(
            { class: 'family-part' },
            span({ class: 'family-status' }, 'check'),
            span(`v${v.version} ${v.at.slice(0, 10)} ${v.by}: ${v.caution}`)
          )
        ),
      latest
        ? ''
        : div(
            strong('not served'),
            ' — no version by someone who may write it'
          ),
      ...list('pinned', tagValues(task.tags, PIN_KEY)),
      ...list('last ran', tagValues(task.tags, RAN_KEY)),
      ...ignored.map((i) =>
        div(
          { class: 'family-part' },
          span({ class: 'family-status' }, 'ignored'),
          span(`${i.at.slice(0, 10)} ${i.by}: ${i.why}`)
        )
      ),
      div(
        { class: 'family-note' },
        'Change it with ',
        code(`virta skill publish ${name}`),
        '; machines run it through a stub (',
        code(`virta skill install ${name}`),
        ').'
      )
    )
  }

  private renderMeasurePanel(task: Task): void {
    const panel = this.parts.measurePanel as HTMLElement
    const type = tagValues(task.tags, TYPE_KEY)[0] ?? ''
    // where it came from and what replaced it (board #2496)
    const family =
      this._store && isEntry(task) ? lineage(this._store, task) : null
    const key = JSON.stringify([
      task.id,
      type,
      task.measure,
      task.record,
      family?.earlier.map((t) => [t.num, t.record]),
      family?.later.map((t) => [t.num, t.title]),
    ])
    if (panel.dataset.key === key) return
    panel.dataset.key = key
    panel.textContent = ''
    const measured = type === 'practice' || type === 'policy'
    panel.hidden = !measured && !family?.earlier.length && !family?.later.length
    if (panel.hidden) return
    const { div, span, strong, button, a } = elements
    const link = (t: Task) =>
      a(
        {
          href: '#',
          class: 'lineage-link',
          onClick: (e: Event) => {
            e.preventDefault()
            this.select(t.id)
          },
        },
        `#${t.num} ${t.title}`
      )
    if (family?.later.length)
      panel.append(
        div(
          { class: 'lineage replaced' },
          strong('replaced by '),
          ...family.later.flatMap((t, i) => (i ? [', ', link(t)] : [link(t)]))
        )
      )
    // a predecessor's evidence: read-only, it was collected against the whole
    for (const t of family?.earlier ?? [])
      panel.append(
        div(
          { class: 'lineage earlier' },
          strong('earlier evidence from '),
          link(t),
          span({ class: 'practice-record' }, ` — ${recordLine(t)}`)
        )
      )
    if (!measured) return
    const m = task.measure
    panel.append(
      m
        ? div(
            { class: 'measure' },
            div(strong('measured by '), span(m.measure)),
            div(strong('retire if '), span(m.death)),
            span({ class: 'measure-version' }, `v${m.version}`)
          )
        : // a practice with no measure is a draft (owner, 2026-09-30)
          span(
            { class: 'measure-missing' },
            `no measure yet: a ${type} with no measure is a draft`
          ),
      button(
        { onClick: () => void this.editMeasure(task.id) },
        m ? 'replace measure' : 'set measure'
      ),
      // its evidence: outcomes under this measure, and the grade (#2475)
      div({ class: 'practice-record' }, recordLine(task))
    )
  }

  /**
   * What practices proposed or did on this task (board #2474): each
   * suggestion waiting, with accept and overrule; each automatic change
   * nobody has undone, with undo. A person's answer is the evidence.
   */
  private renderPracticePanel(task: Task): void {
    const panel = this.parts.practicePanel as HTMLElement
    const store = this._store
    if (!store) return
    const events = store.events(task.id)
    const resolved = new Set(
      events
        .filter((e) => e.kind === 'resolved')
        .map((e) => (e.payload as { of: string }).of)
    )
    const changes = events.filter(
      (e) => e.kind === 'practiced' && !resolved.has(e.id)
    )
    const key = JSON.stringify([
      task.id,
      task.pending,
      changes.map((e) => e.id),
    ])
    if (panel.dataset.key === key) return
    panel.dataset.key = key
    panel.textContent = ''
    panel.hidden = !task.pending.length && !changes.length
    if (panel.hidden) return
    const { div, span, button } = elements
    const act = (of: string, outcome: 'accept' | 'overrule' | 'undo') => {
      const word =
        outcome === 'accept'
          ? 'accepted'
          : outcome === 'overrule'
          ? 'overruled'
          : 'undone'
      this.attempt(store.resolve(task.id, of, word))
    }
    for (const p of task.pending)
      panel.append(
        div(
          { class: 'practice-suggestion' },
          span('💡 ', p.text),
          button(
            { class: 'primary', onClick: () => act(p.id, 'accept') },
            'accept'
          ),
          button({ onClick: () => act(p.id, 'overrule') }, 'overrule')
        )
      )
    for (const e of changes) {
      const payload = e.payload as { added: string[]; removed: string[] }
      panel.append(
        div(
          { class: 'practice-suggestion' },
          span(
            '⚙︎ ',
            `${e.by.replace(/ via .*$/, '')} set ${payload.added.join(' ')}${
              payload.removed.length
                ? ` (was ${payload.removed.join(' ')})`
                : ''
            }`
          ),
          button({ onClick: () => act(e.id, 'undo') }, 'undo')
        )
      )
    }
  }

  /**
   * Edits made from an older version of the body (board #2473): each kept
   * beside it, flagged, with who made it. Choosing one saves it as the
   * body; keeping the current one saves that; merging is editing the body
   * and saving — any edit from the current body settles them all.
   */
  private renderConflicts(task: Task): void {
    const panel = this.parts.conflictPanel as HTMLElement
    const key = JSON.stringify([task.id, task.conflicts, task.body])
    if (panel.dataset.key === key) return
    panel.dataset.key = key
    panel.textContent = ''
    panel.hidden = !task.conflicts.length
    if (panel.hidden) return
    const store = this._store
    const { div, strong, button } = elements
    panel.append(
      strong(
        `⚠ ${
          task.conflicts.length === 1
            ? 'An edit was'
            : `${task.conflicts.length} edits were`
        } made from an older version: kept here, not lost. Choose, or merge by editing the description.`
      )
    )
    for (const c of task.conflicts) {
      // tosijs-ui's resolvable diff: accept or reject each change, then
      // save the merge (current body on the left, theirs on the right)
      const diff = tosiDiff({
        class: 'conflict-diff',
        original: task.body,
        modified: c.body,
        resolvable: true,
        originalLabel: 'current',
        modifiedLabel: `${c.by}'s`,
      }) as TosiDiff
      panel.append(
        div(
          { class: 'conflict' },
          div({ class: 'conflict-by' }, `${c.by} · ${timeAgo(c.at)}`),
          diff,
          div(
            { class: 'conflict-actions' },
            button(
              {
                class: 'primary',
                onClick: () =>
                  store && this.attempt(store.edit(task.id, diff.value)),
              },
              'save this merge'
            ),
            button(
              {
                onClick: () =>
                  store && this.attempt(store.edit(task.id, c.body)),
              },
              'use their version'
            )
          )
        )
      )
    }
    panel.append(
      button(
        {
          onClick: () => store && this.attempt(store.edit(task.id, task.body)),
        },
        'keep the current one'
      )
    )
  }

  /** Set or replace how a practice is judged, in a dialog. */
  private async editMeasure(id: string): Promise<void> {
    const store = this._store
    const task = store?.get(id)
    if (!store || !task) return
    const answer = await this.askDialog(`How #${task.num} is judged`, [
      {
        name: 'measure',
        label: 'measured by: what success looks like',
        type: 'text',
        value: task.measure?.measure ?? '',
      },
      {
        name: 'death',
        label: 'retire if: the result that means retire or revise',
        type: 'text',
        value: task.measure?.death ?? '',
      },
    ])
    if (!answer) return
    this.attempt(
      store.measure(id, {
        measure: answer.measure ?? '',
        death: answer.death ?? '',
      })
    )
  }

  // ── showcase (board #2439) ───────────────────────────────────────────────

  private appliedGroup = 'type'
  private carouselKey = ''

  /**
   * The carousel: on the tiles, the recent showcases and each other
   * project's latest; on a project's board, that project's own. Hidden when
   * there is nothing to show. Rebuilt only when what it shows changes.
   */
  private renderCarousel(overview: boolean, signedOut: boolean): void {
    const el = this.parts.carousel as HTMLElement
    // only on the tiles, scrolling with them: on a board it is space the
    // lanes need (owner, 2026-10-01)
    const scroller = this.parts.overviewScroll as HTMLElement
    if (el.parentElement !== scroller) scroller.prepend(el)
    const store = this._store
    const n = Number(this.showcases ?? SHOWCASE_COUNT)
    const still =
      typeof matchMedia === 'function' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches
    const slidesData: CarouselSlide[] =
      n <= 0
        ? []
        : store && !signedOut && overview
        ? pickShowcases(store.tasks, { n }).map((t) => {
            const show = t.showcase!
            const project = tagValues(t.tags, 'project')[0] ?? ''
            return {
              id: t.id,
              num: t.num,
              project,
              look: store.project(project),
              title: show.title,
              text: show.text,
              link: show.link,
              key: [t.id, show],
              picture: () => this.showcasePicture(show, still),
            }
          })
        : signedOut && this.publicFace
        ? this.publicSlides(n, still)
        : []
    el.hidden = slidesData.length === 0
    const key = JSON.stringify(slidesData.map((d) => d.key))
    if (key === this.carouselKey) return
    this.carouselKey = key
    el.textContent = ''
    if (!slidesData.length) return
    const { div, strong, span, button } = elements
    const slides = slidesData.map((d) => {
      const text = span({ class: 'showcase-text' })
      renderShowcaseText(text, d.text)
      const { project, look } = d
      // the project's icon, bottom right: its board, one click away — signed
      // out there is no board to open, so it only says whose it is
      const badge = (
        d.id
          ? button({
              class: 'showcase-project',
              title: `open ${project}'s board`,
            })
          : span({ class: 'showcase-project', title: project })
      ) as HTMLElement
      badge.dataset.project = d.id ? project : ''
      renderIcon(badge, look?.icon || project.slice(0, 2).toUpperCase())
      if (look) {
        const colors = projectLook(look)
        badge.style.background = colors.bg
        badge.style.color = colors.ink
      }
      const slide = div(
        {
          class: 'slide',
          tabindex: 0,
          role: 'link',
          title: d.link ? `open ${d.link}` : d.id ? `open #${d.num}` : '',
        },
        div(
          { class: 'slide-card' },
          d.picture() ?? span(),
          div(
            { class: 'showcase-words' },
            // cut with an ellipsis; the whole title on hover
            strong({ title: d.title }, d.title),
            text,
            span({ class: 'showcase-num' }, `${project} · #${d.num}`)
          ),
          badge
        )
      ) as HTMLElement
      slide.dataset.id = d.id
      slide.dataset.link = d.link
      return slide
    })
    // tosijs-ui's carousel: native scrolling, so it swipes (2026-10-01)
    const carousel = tosiCarousel(...slides) as TosiCarousel
    carousel.dots = slides.length > 1
    carousel.loop = true
    carousel.snapDuration = 0.4
    // it rotates, and holds still for a mouse over it, keyboard focus in
    // it or reduced motion — never for a tap (tosijs-ui#204, 1.16.2)
    carousel.auto = slides.length > 1 ? 6 : 0
    el.append(carousel)
    this.fitCarousel()
    // the tiles reflow with the width, and the carousel with them
    if (!this.carouselObserver && typeof ResizeObserver === 'function') {
      this.carouselObserver = new ResizeObserver(() => this.fitCarousel())
      this.carouselObserver.observe(this.parts.overviewScroll as HTMLElement)
    }
  }

  private carouselObserver: ResizeObserver | null = null

  /**
   * Signed out: the public projects' showcases, picked the way the board
   * picks its own (owner, 2026-10-14). A stored picture loads from the
   * public copy the summary names; a slide opens its link, nothing else.
   */
  private publicSlides(n: number, still: boolean): CarouselSlide[] {
    const host = String(this.host ?? '').replace(/\/$/, '')
    const shows = (this.publicFace ?? []).flatMap((p) =>
      p.showcases.map((show) => ({
        tags: [`project:${p.name}`],
        showcase: { media: show.media || show.picture, at: show.at },
        p,
        show,
      }))
    )
    return pickShowcases(shows, { n }).map(({ p, show }) => ({
      id: '',
      num: show.num,
      project: p.name,
      look: {
        name: p.name,
        icon: p.icon,
        color: p.color,
        background: p.background,
      },
      title: show.title,
      text: show.text,
      link: show.link,
      key: [p.name, show],
      picture: () =>
        showcaseMedia(
          show.picture
            ? `${host}/blob/${PUBLIC_FILES_AREA}/${show.picture}`
            : show.media,
          show.alt,
          still
        ),
    }))
  }

  /** How many columns the tiles are laid out in right now (1 when it can't be measured). */
  private tileColumns(): number {
    const grid = this.parts.overview as HTMLElement
    const tracks = getComputedStyle(grid)
      .gridTemplateColumns.split(' ')
      .filter((t) => t.endsWith('px')).length
    return tracks || 1
  }

  /** The carousel shows half as many showcases at once as the tiles have columns. */
  private fitCarousel(): void {
    const carousel = (this.parts.carousel as HTMLElement).querySelector(
      'tosi-carousel'
    ) as TosiCarousel | null
    if (!carousel) return
    const across = Math.min(
      showcasesAcross(this.tileColumns()),
      Math.max(1, carousel.children.length)
    )
    if (carousel.maxVisibleItems !== across) {
      carousel.maxVisibleItems = across
      // nothing to page through when they all fit
      const pages = carousel.children.length > across
      carousel.dots = pages
      carousel.auto = pages ? 6 : 0
    }
  }

  /**
   * A showcase's picture: its media URL, or a stored picture fetched with
   * the viewer's own credential (board #2436) — a private file has no URL
   * a tag could load, so the element gets a local one when it arrives.
   */
  private showcasePicture(
    show: NonNullable<Task['showcase']>,
    still: boolean
  ): HTMLElement | null {
    if (!show.file) return showcaseMedia(show.media, show.alt, still)
    const { img, video } = elements
    const isVideo = show.file.type.startsWith('video/')
    const el = (
      isVideo
        ? video({
            muted: true,
            loop: true,
            playsInline: true,
            autoplay: !still,
            class: 'showcase-media',
            'aria-label': show.alt,
          })
        : img({ alt: show.alt, class: 'showcase-media' })
    ) as HTMLImageElement | HTMLVideoElement
    void this.fileUrl(show.file).then((url) => {
      if (url) el.src = url
    })
    return el
  }

  /** A slide opens its link (a new tab), else its task; the carousel's own dots page it. */
  private carouselClicked(e: Event): void {
    const target = e.target as HTMLElement
    // the project's icon: that project's board
    const badge = target.closest('.showcase-project') as HTMLElement | null
    if (badge?.dataset.project) {
      e.stopPropagation()
      this.setAxis('project')
      this.setScope(badge.dataset.project)
      this.setView('board')
      return
    }
    const slide = target.closest('.slide') as HTMLElement | null
    if (!slide) return
    const link = slide.dataset.link ?? ''
    if (link && isSafeNavigationUrl(link) && httpOnly(link))
      window.open(link, '_blank', 'noopener,noreferrer')
    else if (slide.dataset.id) this.select(slide.dataset.id)
  }

  /** The card's showcase section: a closed task's showcase, or the offer of one. */
  private renderShowcasePanel(task: Task): void {
    const panel = this.parts.showcasePanel as HTMLElement
    const key = JSON.stringify([task.id, task.closedAt, task.showcase])
    if (panel.dataset.key === key) return
    panel.dataset.key = key
    panel.textContent = ''
    panel.hidden = task.closedAt === null
    if (panel.hidden) return
    const { div, strong, button, a, span } = elements
    const show = task.showcase
    if (!show) {
      panel.append(
        button(
          {
            class: 'showcase-offer',
            title: 'what this shipped: a picture or video, a line, a link',
            onClick: () => void this.editShowcase(task.id),
          },
          '✨ add a showcase'
        )
      )
      return
    }
    const text = span({ class: 'showcase-text' })
    renderShowcaseText(text, show.text)
    const media = this.showcasePicture(show, true)
    panel.append(
      div(
        { class: 'showcase-card' },
        ...(media ? [media] : []),
        div(
          { class: 'showcase-words' },
          strong(show.title),
          text,
          ...(show.link && isSafeNavigationUrl(show.link) && httpOnly(show.link)
            ? [
                a(
                  {
                    href: show.link,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  },
                  show.link
                ),
              ]
            : [])
        )
      ),
      div(
        { class: 'showcase-actions' },
        button(
          { onClick: () => void this.editShowcase(task.id) },
          'edit showcase'
        ),
        button(
          {
            onClick: () =>
              this.attempt(
                this._store!.showcase(task.id, { text: '', withdraw: true })
              ),
          },
          'withdraw'
        )
      )
    )
  }

  /** Add or edit a closed task's showcase, in a dialog. */
  private async editShowcase(id: string): Promise<void> {
    const store = this._store
    const task = store?.get(id)
    if (!store || !task) return
    const now = task.showcase
    const answer = await this.askDialog(`Showcase #${task.num}`, [
      {
        name: 'media',
        label: 'picture: a screen capture or video (URL, required)',
        type: 'url',
        value: now?.media ?? '',
      },
      {
        name: 'title',
        label: 'title',
        type: 'text',
        value: now?.title ?? task.title,
      },
      {
        name: 'text',
        label: `a line on what it does (up to ${SHOWCASE_TEXT_MAX}; **bold** works)`,
        type: 'text',
        value: now?.text ?? '',
        maxLength: SHOWCASE_TEXT_MAX,
      },
      {
        name: 'alt',
        label: 'what the picture shows (for screen readers)',
        type: 'text',
        value:
          now && now.alt !== `screen capture of ${now.title}` ? now.alt : '',
      },
      {
        name: 'link',
        label: 'link: docs, demo, video (URL, optional)',
        type: 'url',
        value: now?.link ?? '',
      },
    ])
    if (!answer) return
    this.attempt(
      store.showcase(id, {
        title: answer.title,
        text: answer.text ?? '',
        media: answer.media,
        alt: answer.alt,
        link: answer.link,
      })
    )
  }

  // ── attachments by drag and drop (board #2436) ───────────────────────────

  /** A link (or a file) dragged over an open task: it can land here. */
  private attachDragOver(e: DragEvent): void {
    const types = Array.from(e.dataTransfer?.types ?? [])
    if (!types.includes('text/uri-list') && !types.includes('Files')) return
    if (this.draftTags || !String(this.s.selectedId)) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    ;(this.parts.cardWindow as HTMLElement).classList.add('drop-target')
  }

  private attachDragLeave(e: DragEvent): void {
    const win = this.parts.cardWindow as HTMLElement
    if (!win.contains(e.relatedTarget as Node | null))
      win.classList.remove('drop-target')
  }

  /**
   * A drop on an open task: each http(s) link becomes an attachment, named
   * by the last part of its path (else its host). A file can't land yet —
   * uploads come with blob storage (tosijs-platform#8) — and says so.
   */
  private attachDrop(e: DragEvent): void {
    ;(this.parts.cardWindow as HTMLElement).classList.remove('drop-target')
    const dt = e.dataTransfer
    const store = this._store
    const id = String(this.s.selectedId)
    if (!dt || !store || !id || this.draftTags) return
    const urls = (dt.getData('text/uri-list') || '')
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith('#') && httpOnly(l))
    const files = Array.from(dt.files ?? [])
    if (urls.length === 0 && files.length === 0) return
    e.preventDefault()
    // a file: stored, then attached; a link: attached as a link (owner,
    // 2026-09-27: a link is a link, a file is stored)
    if (files.length) void this.uploadFiles(id, files)
    else
      for (const url of urls)
        this.attempt(store.attach(id, attachmentNameFor(url), url))
  }

  /**
   * Files dropped on a task (board #2436): each is checked before any
   * bytes move (a kind a task carries, 25 MB at most), stored in the
   * host's private files area under its content hash, then attached.
   */
  private async uploadFiles(id: string, files: File[]): Promise<void> {
    const store = this._store
    const service = this.fileService
    if (!store) return
    if (!service) {
      this.complain(
        'files need a host: this board keeps its tasks in this browser only'
      )
      return
    }
    for (const file of files) {
      try {
        this.s.syncStatus = `uploading ${file.name}…`
        const stored = await uploadFile(
          service,
          file.name,
          await file.arrayBuffer(),
          file.type
        )
        await store.attach(id, file.name, '', stored)
        this.s.syncStatus = 'live'
      } catch (e) {
        this.complain(e)
      }
    }
  }

  /** The host's file routes, for this store's credential; null for a board with no host. */
  fileService: Pick<ServiceClient, 'putBlob' | 'getBlob'> | null = null
  /** A stored file's local URL, fetched once per path with the reader's credential. */
  private fileUrls = new Map<string, Promise<string | null>>()

  /** A stored file, readable in this page: an object URL, or null when it isn't this reader's. */
  private fileUrl(file: AttachedFile): Promise<string | null> {
    const key = `${file.area}/${file.path}`
    let url = this.fileUrls.get(key)
    if (!url) {
      const service = this.fileService
      // no service yet (the store attaches, and draws, before its connect
      // sets one): say nothing, and remember nothing — the next draw asks
      // again (the carousel's pictures never loaded, 2026-10-01)
      if (!service) return Promise.resolve(null)
      url = service
        .getBlob(file.area, file.path)
        .then((blob) =>
          blob
            ? URL.createObjectURL(
                // the type the event recorded, not whatever came back
                new Blob([blob], { type: file.type })
              )
            : null
        )
        .catch(() => null)
      this.fileUrls.set(key, url)
    }
    return url
  }

  /** Drop the file service and every object URL made with it. */
  private forgetFiles(): void {
    this.fileService = null
    this.hostClient = null
    for (const url of this.fileUrls.values())
      void url.then((u) => u && URL.revokeObjectURL(u))
    this.fileUrls.clear()
  }

  /**
   * The status (lane) and project selects, for a card or a draft (board
   * #2438: a draft showed neither, so a task filed from another project's
   * board landed there unseen).
   */
  private syncPlacement(tags: readonly string[]): void {
    const store = this._store
    if (!store) return
    ;(this.parts.laneLabel as HTMLElement).textContent = this.lane
    this.s.laneValue = tagValues(tags, this.lane)[0] ?? ''
    // every value of the lane key on the board: recomputed only when the
    // log or the lane key moved, and handed on only when it changed — a
    // write that adds no value must not rebuild the select under the
    // pointer (board #52)
    const at = `${this.lane}@${store.cursor}`
    if (this.laneChoicesAt !== at) {
      this.laneChoicesAt = at
      const choices = new Set<string>()
      for (const t of store.tasks)
        for (const v of tagValues(t.tags, this.lane)) choices.add(v)
      const sorted = Array.from(choices).sort()
      if (sorted.join('\n') !== this.laneChoiceList.join('\n')) {
        this.laneChoiceList = sorted
        this.s.laneChoices = sorted
      }
    }
    this.renderChoices(this.parts.laneSelect as TosiSelect, this.laneChoiceList)
    this.s.projectValue = tagValues(tags, 'project')[0] ?? ''
    // in project lanes the lane select would duplicate the project select
    ;(this.parts.laneSelect as HTMLElement).closest('label')!.hidden =
      this.lane === 'project'
    this.renderProjectChoices(
      this.parts.projectSelect as TosiSelect,
      this.s.projectNames as unknown as string[]
    )
  }

  /** The tags the pickers read: the draft's, else the open card's. */
  private pickerTags(): string[] {
    if (this.draftTags) return this.draftTags
    const task = this._store?.get(String(this.s.selectedId))
    return task ? task.tags : []
  }

  /** Each picker's options (conventional values, then any in use) and its value. */
  private renderPickers(): void {
    const tags = this.pickerTags()
    for (const key of PICKER_KEYS) {
      const select = this.parts[`${key}Select`] as TosiSelect
      const inUse = new Set<string>()
      for (const t of this._store?.tasks ?? [])
        for (const v of tagValues(t.tags, key)) inUse.add(v)
      const option = (v: string) => ({ caption: v, value: v })
      let choices: SelectOptions
      if (key === 'kind') {
        // the common four, then submenus by theme (board #2435)
        const { top, themes, other } = kindGroups(inUse)
        // the task's own kind shows even when it isn't one we offer
        const own = tagValues(tags, 'kind')[0]
        if (
          own &&
          !top.includes(own) &&
          !themes.some(([, k]) => k.includes(own)) &&
          !other.includes(own)
        )
          other.push(own)
        choices = [
          ...top.map(option),
          null,
          ...themes.map(([caption, kinds]) => ({
            caption,
            options: kinds.map(option),
          })),
          ...(other.length
            ? [{ caption: 'other', options: other.map(option) }]
            : []),
        ]
      } else
        choices = [
          ...PICKER_VALUES[key],
          ...Array.from(inUse)
            .filter((v) => !PICKER_VALUES[key].includes(v))
            .sort(),
        ].map(option)
      const options: SelectOptions = [
        { caption: `no ${key}`, value: NO_VALUE },
        null,
        ...choices,
      ]
      // only a real change replaces the options (an open menu survives; #52)
      const optionsKey = JSON.stringify(options)
      if (this.renderedChoices.get(select) !== optionsKey) {
        this.renderedChoices.set(select, optionsKey)
        select.options = options
      }
      const wanted = tagValues(tags, key)[0] ?? NO_VALUE
      if (select.value !== wanted) select.value = wanted
    }
  }

  /** In a draft: one value for `key` (or none), shown in its selects. */
  private setDraftTag(key: string, value: string | null): void {
    if (!this.draftTags) return
    this.draftTags = [
      ...this.draftTags.filter((t) => parseTag(t).key !== key),
      ...(value ? [`${key}:${value}`] : []),
    ]
    this.syncPlacement(this.draftTags)
  }

  /** A picker changed: the draft's tags, or the card's (one value per key). */
  private pickTag(key: PickerKey, value: string): void {
    if (this.draftTags) {
      this.setDraftTag(key, value || null)
      return
    }
    const id = String(this.s.selectedId)
    const store = this._store
    if (!store || !id) return
    if ((tagValues(store.get(id)?.tags ?? [], key)[0] ?? '') === value) return
    this.attempt(store.setTag(id, key, value || null))
  }

  /** The tag value a lane means: none for the empty lane, and none for the implicit inbox. */
  private laneTagValue(laneValue: string): string | null {
    if (laneValue === '') return null
    if (this.lane === 'status' && laneValue === INBOX_STATUS) return null
    return laneValue
  }

  // ── keyboard ─────────────────────────────────────────────────────────────

  private handleKey(event: KeyboardEvent): void {
    const target = event.target as HTMLElement
    const typing = target.matches('input, textarea, select')
    // a draft is a small dialog: Enter is Create (the default button;
    // ⌘/Ctrl+Enter in the description, where Enter is a new line), Escape is
    // Discard (the cancel button) — the owner's convention (#1075)
    if (this.draftTags) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.discardDraft()
        return
      }
      const inBody = target === this.parts.bodyInput
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        (!inBody || event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        void this.createDraft()
        return
      }
    }
    if (event.key === 'Escape') {
      if (typing) target.blur()
      else this.select('')
      this.focus()
      return
    }
    if (typing) return
    if (event.key === '/') {
      event.preventDefault()
      ;(this.parts.filter as HTMLElement).focus()
      return
    }
    const flat = this.flatOrder()
    const index = flat.findIndex((t) => t.id === String(this.s.selectedId))
    if (event.key === 'j' || event.key === 'ArrowDown') {
      event.preventDefault()
      const next = flat[Math.min(flat.length - 1, index + 1)]
      if (next) this.highlight(next.id)
    } else if (event.key === 'k' || event.key === 'ArrowUp') {
      event.preventDefault()
      const prev = flat[Math.max(0, index - 1)]
      if (prev) this.highlight(prev.id)
    } else if (event.key === 'Enter') {
      const id = String(this.s.selectedId)
      if (id) {
        this.select(id)
        ;(this.parts.panel as HTMLElement).hidden = false
      }
    }
  }

  /**
   * Cards in on-screen order: lane by lane, top to bottom — from the lanes
   * as drawn, not the DOM, which holds only the cards in view.
   */
  private flatOrder(): Task[] {
    return [...this.laneTasks.values()].flat()
  }

  /**
   * Bring a card into view: it may not be built yet (a virtual lane builds
   * what's in view), so its lane scrolls to about where it is, and the card
   * is put in view once it's there.
   */
  private revealCard(id: string): void {
    const board = this.parts.board as HTMLElement
    const built = () =>
      Array.from(board.querySelectorAll('.card')).find(
        (el) => (el as HTMLElement).dataset.id === id
      ) as HTMLElement | undefined
    const shown = built()
    if (shown) {
      shown.scrollIntoView?.({ block: 'nearest' })
      return
    }
    for (const [lane, tasks] of this.laneTasks) {
      const index = tasks.findIndex((t) => t.id === id)
      if (index < 0) continue
      const laneEl = Array.from(board.querySelectorAll('.lane')).find(
        (el) => (el as HTMLElement).dataset.lane === lane
      )
      const cards = laneEl?.querySelector('.cards') as HTMLElement | null
      if (!cards) return
      cards.scrollTop = Math.max(
        0,
        index * CARD_MIN_HEIGHT - cards.clientHeight / 2
      )
      requestAnimationFrame(() =>
        built()?.scrollIntoView?.({ block: 'nearest' })
      )
      return
    }
  }

  /** Select without opening the panel (unless it is already open). */
  private highlight(id: string): void {
    const open = !(this.parts.panel as HTMLElement).hidden
    this.s.selectedId = id
    const board = this.parts.board as HTMLElement
    for (const el of Array.from(board.querySelectorAll('.card')))
      el.classList.toggle('selected', (el as HTMLElement).dataset.id === id)
    if (open) this.showCard(id)
    this.revealCard(id)
  }
}

export const tosiVirta = TosiVirta.elementCreator()
